import {
  applyButtonReadOnly, applyFieldReadOnly, applySelectReadOnly,
  currencyChipHtml, dateFieldHtml, escapeHtml, formatNum, makeDraggable, markCurrentRow,
  clearableFieldHtml, fieldClearHtml,
  mountCollabField, mountDateField, notchedFieldHtml, placePopover, popoverHost, presenceDotHtml,
  stepForFraction, closeModalAndWait, formSnapshot, fromScaled, toScaled, wireDayDrag,
  askConfirm, todayIso, wireSwipeNav,
  type MountedField, type MoneyCurrency,
} from '@colplat/ui-web';
import { createHistoryPanel, type HistoryPanel } from './webledgerHistoryPanel.js';
import {
  splitNumbering, tagLinkTargets, type HistoryContext, type TransactionHistory,
} from './webledgerHistory.js';
import { apiFetch, failureDetail, failureMessage } from '../api.js';
import { appHref } from '../globals.js';
import { knownOffline, readCached } from '../offlineReads.js';
import { datasetLedger } from './webledgerLedger.js';
import { elementScroller, restoreScroll, trackScroll } from './webledgerScroll.js';
import { accountAssistantHtml, debtHelperHtml } from './webledgerDebtHelper.js';
import {
  accountsView, attachmentsOf, bankInfoOf, budgetsView, buildRegister, dayOrderAfterMove, debtsView,
  transactionWithSplits,
  bookPositions, byCurrencyRank, settledBalances, settledHistory, settledShifts, shiftsAsOf, currencyRank, NO_WALLET, walletAccountHistories, walletColours,
  walletHistory, walletsView, normalTransferKind, standingBefore, transferAmounts, walletLegs, accountBalanceBefore, walletBalanceBefore, enteredBefore,
  type AccountHistory, type WalletTransferRow,
  type BookPosition, type DebtLine, type RegisterRow, type SettledShift, type WalletHistory,
} from '@colplat/app-webledger/domain';
import { constellationHtml, hideTableTip, hoverState, showTableTip, walletKeys } from './webledgerAccountsChart.js';
import { sortCurrencies } from './webledgerCurrencyOrder.js';
import { balancesOn, type WalletKeys } from './webledgerAccountsChart.js';
import { wireInspectGestures } from './webledgerInspect.js';
import {
  bootstrapWebledgerClient, clearNoticeAt, clearPageNotice, pageFailure, showNoticeAt,
  webledgerShellHtml, wireBackButton, type WebledgerClient,
} from '../webledgerClient.js';
import {
  bsModal, HELP_BUBBLE_ICON, openTransactionEditor, timeAgo, transactionEditorModalHtml,
  wireHelpBubble, wireSplitHelp,
} from './webledgerTransactionEditor.js';
import {
  attachmentBarHtml, formatBytes, mountAttachmentBar, openAttachmentTab,
} from './webledgerAttachments.js';
import { bankInfoBadgeHtml, mountBankInfo } from './webledgerBankInfo.js';
import {
  accountPointAt, dayAt, scrollToDay, sparkSpan, sparklineSvg, wireSparkScroll, type SparkScroll, type SparkSpan,
} from './webledgerSparkline.js';
import {
  heldDifferences, heldFor, queuedPayloadToDto,
  type AccountDto, type CurrencyDto, type HeldDifference, type QueuedTransactionPayload,
} from './webledgerOfflineState.js';
import { forgetForm, guardUnsaved, rememberForm, takeSavedForm } from './webledgerFormRestore.js';
import { applyRecordState, recordState, RecordStateTracker } from './webledgerRecordState.js';
import { mountReviewBanner } from './webledgerReviewBanner.js';
import { mountRefusedBanner } from './webledgerRefusedBanner.js';
import { logSearchMatcher } from './webledgerLogSearch.js';
import { wireHeadReveal, type HeadReveal } from './webledgerHeadReveal.js';
import { RecordHold, holdNoticeHtml, type Holder } from './webledgerHold.js';
import { openAccountPicker } from './webledgerAccountPicker.js';
import { accountLabelHtml, accountShortLabel } from './webledgerAccountTree.js';
import { inlineAccountIcon } from './webledgerAccountDisplay.js';
import { parseIdList, PREF } from './webledgerPrefs.js';
import {
  emptyTagSelection, loadTagSelection, renderTagSelection,
  tagSelectionMembers, wireTagSelection, type TagDto,
} from './webledgerTags.js';
import { fillTotalModes, totalsHtml } from './webledgerTotals.js';
import {
  budgetDialogHtml, budgetsPaneHtml, mountBudgets, overBudgetCount, type MountedBudgets,
} from './webledgerBudgets.js';
import { convertTotals, type FxRates } from './webledgerReportsData.js';
import { parseFieldKey, type FieldValue, type PresenceUser } from '@colplat/protocol';
import type { OutboxDocumentWrite } from '@colplat/core-client';

/**
 * The wallets page (ADR-0023) — the envelope view over the double entry.
 *
 * Nothing here is a second bookkeeping system. Every number on this page is a
 * `SUM()` over ordinary split rows, which is why the strip at the top can say
 * something a budgeting app cannot: the wallets add up to the money actually on
 * hand, currency by currency. It holds by construction, because a money leg
 * carries at most one wallet and anything not earmarked is counted as
 * Unassigned.
 *
 * Per currency, throughout. A wallet is short in forints or in euros, never
 * just "short"; it borrows and repays one currency at a time; and no rate is
 * applied anywhere on this page. Borrowing euros to spend forints is a euro
 * loan plus an ordinary exchange — two separate, individually true facts.
 *
 * Colour never carries a state on its own (invariant #11): every shortfall
 * shows a word as well as a band, and the running column spells out "over".
 */

interface WalletDto {
  id: string;
  name: string;
  /** `wallet.shortName` — what the notches call it. Empty when nobody has set one. */
  shortName: string;
  /** The colour in force — its own, or the palette's. See `walletColours`. */
  color: string;
  parentId: string | null;
  sortOrder: number;
  own: Record<string, string>;
  rollup: Record<string, string>;
  shortIn: string[];
}

interface WalletsResponse {
  wallets: WalletDto[];
  unassigned: Record<string, string>;
  cash: Record<string, string>;
}

/** Who holds each transaction on this page, as the register reports it. */
interface RegisterLock {
  id: string;
  lockedBy?: PresenceUser | null;
  ownedBy?: PresenceUser | null;
  ownedSince?: string | null;
}

interface Episode {
  id: string; currencyCode: string; fromRow: number; toRow: number;
  fromDate: string; toDate: string; worst: string; closed: boolean;
  covers: Array<{ rowIndex: number; fromWalletId: string; amount: string; kind: string }>;
}

interface Debt {
  debtorId: string; creditorId: string; currencyCode: string; amount: string; lines: DebtLine[];
}

const UNASSIGNED = 'unassigned';

/**
 * Currency metadata, so HUF prints without decimals and EUR with two.
 *
 * Filled from the cached `/currencies` read every webledger page already
 * primes. Until it lands, `formatNum`'s own fallback of two decimals applies —
 * briefly wrong for forints, corrected by the repaint that follows.
 */
const moneyByCode = new Map<string, MoneyCurrency>();

function money(code: string): MoneyCurrency {
  return moneyByCode.get(code) ?? { fraction: 2, code };
}

/** A plain number for prose. Amounts in lists and tables get a chip instead. */
export function formatAmount(raw: string | number, ccy: string): string {
  return formatNum(raw, money(ccy));
}

/**
 * The shared currency chip (`packages/ui-web`), not a bespoke span.
 *
 * Every signed amount in this app is one of these, and the reason is the chip
 * tally: `bootstrapWebledgerClient` mounts it on every page, and it finds its
 * numbers by looking for `.cf-cur-chip[data-cur]`. Hand-rolled markup renders
 * the same and silently opts the page out of Cmd/Ctrl-click totalling — which
 * is exactly the question a wallet register invites ("what do these four rows
 * come to?").
 */
function chip(
  raw: string | number, ccy: string, className?: string,
  notch?: { text: string; says?: string },
): string {
  return currencyChipHtml(raw, ccy, {
    currency: money(ccy),
    ...(className ? { className } : {}),
    ...(notch ? { notch: notch.text } : {}),
    ...(notch?.says ? { notchSays: notch.says } : {}),
  });
}

function nonZero(bag: Record<string, string>): string[] {
  // In the Settings page's order, like every other row of chips.
  return sortCurrencies(Object.keys(bag).filter((c) => Math.abs(Number(bag[c])) > 0.00005));
}

function balancesHtml(
  bag: Record<string, string>,
  history: readonly WalletHistory[] = [],
  span?: SparkSpan,
  markDay?: string,
): string {
  const codes = nonZero(bag);
  // Spans, not divs: this markup sits inside the button that opens the
  // wallet's log, and a button may only contain phrasing content.
  if (codes.length === 0) return '<span class="wl-wal__ccy"><span class="text-muted small">empty</span></span>';
  // The chip carries its own currency mark, so the separate code label is gone.
  /*
   * Chip then chart, the chips flush right against it.
   *
   * The chart is a fixed width, so right-aligning the pair lines every
   * figure up on its last digit AND starts every chart at the same x — the
   * second of which is what makes scrolling them together legible.
   */
  return codes.map((c) => {
    const series = history.find((h) => h.currencyCode === c);
    const chart = series && span
      ? `<span class="wl-spark" data-spark="${escapeHtml(c)}">${sparklineSvg(series, span, markDay)}</span>`
      : '';
    return `<span class="wl-wal__ccy">${chip(bag[c]!, c)}${chart}</span>`;
  }).join('');
}

/** Depth-first, parents before children, honouring sortOrder. */
export function orderWallets(wallets: readonly WalletDto[]): Array<{ wallet: WalletDto; depth: number }> {
  const byParent = new Map<string | null, WalletDto[]>();
  for (const w of wallets) {
    const list = byParent.get(w.parentId);
    if (list) list.push(w); else byParent.set(w.parentId, [w]);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name));
  }
  const out: Array<{ wallet: WalletDto; depth: number }> = [];
  const walk = (parent: string | null, depth: number): void => {
    for (const w of byParent.get(parent) ?? []) { out.push({ wallet: w, depth }); walk(w.id, depth + 1); }
  };
  walk(null, 0);
  return out;
}

/** Full path, never an indent: two wallets may both be called "66". */
export function walletPath(wallets: readonly WalletDto[], id: string): string {
  const byId = new Map(wallets.map((w) => [w.id, w]));
  const parts: string[] = [];
  let cursor = byId.get(id);
  let guard = 0;
  while (cursor && guard < 32) { parts.unshift(cursor.name); cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined; guard += 1; }
  return parts.join(' / ') || 'Unassigned';
}

function ancestorIds(wallets: readonly WalletDto[], id: string): string[] {
  const byId = new Map(wallets.map((w) => [w.id, w]));
  const out: string[] = [];
  let cursor = byId.get(id)?.parentId ?? null;
  let guard = 0;
  while (cursor && guard < 32) { out.push(cursor); cursor = byId.get(cursor)?.parentId ?? null; guard += 1; }
  return out;
}

/**
 * A wallet's balance with the "If debts were settled" switch applied: every
 * debt between it (with everything inside it) and a wallet outside it, as of
 * `day`, paid back. Unassigned is the absence of a wallet and owes nothing,
 * so it is the same either way.
 */
export function settledBag(
  bag: Record<string, string>, id: string, settled: boolean,
  wallets: readonly WalletDto[], debts: Parameters<typeof settledBalances>[2], day?: string,
): Record<string, string> {
  if (!settled || id === UNASSIGNED) return bag;
  return settledBalances(bag, new Set([id, ...descendantIds(wallets, id)]), debts, day);
}

/** The debt lines behind the switch, signed for one wallet and its family. None while it is off. */
export function walletShifts(
  id: string, settled: boolean, wallets: readonly WalletDto[], debts: Parameters<typeof settledShifts>[1],
): SettledShift[] {
  if (!settled || id === UNASSIGNED) return [];
  return settledShifts(new Set([id, ...descendantIds(wallets, id)]), debts);
}

/**
 * The log's running column with the debts settled: each row's running total
 * plus the shifts of every entry up to and including its own.
 *
 * A shift lands on the FIRST row of its transaction, so every leg of that
 * entry already counts it — the column shows one figure per currency per
 * entry, and which leg it is read off must not decide whether the debt is
 * in it. A debt line whose entry is not in this register (it should always
 * be: a debt is a transfer this wallet took part in) goes after the last
 * row of its day rather than nowhere.
 *
 * `shiftAt[i]` is the correction in force at row `i`, per currency, for a
 * caller that converts the running total and needs every currency's.
 * The rows come back as they were, and `shiftAt` undefined, when there is
 * nothing to settle.
 */
export function settledRunning<R extends { transactionId: string; txDate: string; currencyCode: string; running: string }>(
  rows: readonly R[], shifts: readonly SettledShift[],
): { rows: R[]; shiftAt: Array<Record<string, string>> | undefined } {
  if (shifts.length === 0) return { rows: [...rows], shiftAt: undefined };
  const firstOf = new Map<string, number>();
  rows.forEach((r, i) => { if (!firstOf.has(r.transactionId)) firstOf.set(r.transactionId, i); });
  const placed = shifts.map((s) => {
    let at = firstOf.get(s.transactionId);
    if (at === undefined) {
      at = -1;
      for (let i = 0; i < rows.length; i += 1) if (rows[i]!.txDate <= s.txDate) at = i;
    }
    return { at, s };
  }).sort((a, b) => a.at - b.at);
  const sums = new Map<string, bigint>();
  let k = 0;
  const shiftAt: Array<Record<string, string>> = [];
  const out = rows.map((r, i) => {
    for (; k < placed.length && placed[k]!.at <= i; k += 1) {
      const { s } = placed[k]!;
      sums.set(s.currencyCode, (sums.get(s.currencyCode) ?? 0n) + toScaled(s.delta));
    }
    const now: Record<string, string> = {};
    for (const [c, v] of sums) now[c] = fromScaled(v);
    shiftAt.push(now);
    return { ...r, running: fromScaled(toScaled(r.running) + (sums.get(r.currencyCode) ?? 0n)) };
  });
  return { rows: out, shiftAt };
}

/**
 * Whether the log's totals carry the switch: only where it changes them, one
 * real wallet chosen. The book as a whole is the same either way — every
 * debt is one wallet's minus and another's plus.
 */
export function settledSwitchShown(section: string, chosen: string): boolean {
  return section === 'log' && chosen !== 'all' && chosen !== UNASSIGNED;
}

function descendantIds(wallets: readonly WalletDto[], id: string): string[] {
  const out: string[] = [];
  const walk = (parent: string): void => {
    for (const w of wallets) if (w.parentId === parent) { out.push(w.id); walk(w.id); }
  };
  walk(id);
  return out;
}

/**
 * A shortfall belongs to the DEEPEST wallet that has it.
 *
 * A parent whose subtree is short only because a child is short must not raise
 * a second item for the same money — but a parent short in its own right still
 * raises one, for the part its children do not explain.
 */
export interface Shortfall { walletId: string; currencyCode: string; amount: number }

export function shortfalls(wallets: readonly WalletDto[]): Shortfall[] {
  const rollup = new Map(wallets.map((w) => [w.id, w.rollup]));
  const out: Shortfall[] = [];
  for (const { wallet } of orderWallets(wallets)) {
    const bag = rollup.get(wallet.id) ?? {};
    for (const ccy of Object.keys(bag).sort()) {
      let short = -Number(bag[ccy]);
      if (short <= 0.00005) continue;
      for (const child of wallets.filter((w) => w.parentId === wallet.id)) {
        const childShort = -Number((rollup.get(child.id) ?? {})[ccy] ?? 0);
        if (childShort > 0) short -= childShort;
      }
      if (short > 0.00005) out.push({ walletId: wallet.id, currencyCode: ccy, amount: short });
    }
  }
  return out;
}

/**
 * What a wallet-transfer row says about itself.
 *
 * Cash moving between wallets never moves at all — it sits in the same
 * account and only the earmark changes — so a transfer is invisible in the
 * numbers. What a reader needs to know is the part the numbers cannot carry:
 * whether it is coming back. A loan is owed and will show in the debts; a
 * hand-over is a deliberate repurposing and will not; a repayment closes one.
 * ADR-0023 keeps that distinction in a `kind` on the sidecar precisely
 * because it cannot be derived from the legs.
 *
 * Both directions are named, because a transfer has two legs and each one is
 * read on its own: the wallet that lent wants "Lent to Maintenance", the one
 * that borrowed wants "Loan from Car".
 *
 * Words, not a colour (invariant #11).
 */
export function transferLabel(
  kind: string | null, incoming: boolean, counterpart: string,
  role?: { paidBack: string; lent: string } | null, fmt: (v: string) => string = (v) => v,
  part?: string,
): string | undefined {
  if (!kind) return undefined;
  const to = (prep: string): string => (counterpart ? ` ${prep} ${counterpart}` : '');
  // Only part of the row was owed: say how much, since the row's own figure
  // is no longer the debt's.
  const of = part ? ` ${part}` : '';
  /*
   * Paid back, lent, or both — worked out from where the two wallets stood
   * (`transferRoles`), never asked: the direction already says which, and a
   * repayment that overshoots into a new loan had no word to pick.
   */
  if ((kind === 'loan' || kind === 'repay') && role && Number(role.paidBack) > 0) {
    if (Number(role.lent) <= 0) return `Paid back${of}${to(incoming ? 'by' : 'to')}`;
    return incoming
      ? `Paid back ${fmt(role.paidBack)}${to('by')}, loan of ${fmt(role.lent)}`
      : `Paid back ${fmt(role.paidBack)}${to('to')}, lent ${fmt(role.lent)}`;
  }
  // The preposition belongs to the verb, not to the direction: money is lent
  // TO and borrowed FROM, but a hand-over is given BY the wallet that gave it.
  const word = ({
    loan: incoming ? [part ? 'Loan of' : 'Loan', 'from'] : ['Lent', 'to'],
    repay: incoming ? [part ? 'Loan of' : 'Loan', 'from'] : ['Lent', 'to'],
    grant: incoming ? ['Given', 'by'] : ['Given', 'to'],
  } as Record<string, [string, string] | undefined>)[kind];
  if (!word) return undefined;
  return `${word[0]}${of}${to(word[1])}`;
}

/**
 * What a collapsed transfer row says about itself.
 *
 * `transferLabel` names ONE direction because an ordinary row is one leg and
 * is read from that leg's side. A collapsed row has no side — it is the whole
 * movement — so it names the kind once and lets the arrow carry the rest.
 *
 * It matters most where the wallet column is not there to say it: that column
 * appears only in the all-wallets view, so inside a parent wallet this chip
 * is the only thing that says which way the money went.
 */
export function movedLabel(
  kind: string | null, fromName: string, toName: string,
  role?: { paidBack: string; lent: string } | null, fmt: (v: string) => string = (v) => v,
): string | undefined {
  const owed = kind === 'loan' || kind === 'repay';
  const word = owed && role && Number(role.paidBack) > 0
    ? (Number(role.lent) > 0 ? `Paid back ${fmt(role.paidBack)} + loan ${fmt(role.lent)}` : 'Paid back')
    : ({ loan: 'Loan', repay: 'Loan', grant: 'Handed over' } as Record<string, string | undefined>)[kind ?? ''];
  if (!word) return undefined;
  return fromName && toName && fromName !== toName
    ? `${word}: ${fromName} → ${toName}`
    : word;
}

/**
 * What one transfer behind a debt is called.
 *
 * `movedLabel` names the kind and the direction, which is the pair of facts a
 * signed line needs: a `-300` under "Car owes Renovation" is a loan that went
 * the other way, and a reader who cannot see that has a minus sign to take on
 * trust. A grant is named too and says plainly that it changes nothing —
 * leaving it out would answer "what makes up this debt?" and leave "why did
 * handing them 500 not change it?" to somebody more worried.
 */
export function debtLineSays(
  line: Pick<DebtLine, 'kind' | 'fromWalletId' | 'toWalletId'> & Partial<Pick<DebtLine, 'paidBack' | 'lent'>>,
  nameOf: (id: string) => string,
  fmt: (v: string) => string = (v) => v,
): string {
  const role = line.paidBack !== undefined && line.lent !== undefined
    ? { paidBack: line.paidBack, lent: line.lent } : null;
  const label = movedLabel(line.kind, nameOf(line.fromWalletId), nameOf(line.toWalletId), role, fmt)
    ?? nameOf(line.fromWalletId);
  return line.kind === 'grant' ? `${label} — given, so it changes nothing here` : label;
}

/**
 * Which rows a tag filter leaves showing.
 *
 * Applied to rows the server has already finished with, never to the query
 * that produced them. A wallet register carries a running balance and
 * shortfall episodes, and both are facts about the WHOLE sequence: "the wallet
 * stood at this much after that row", "it was under from here to there".
 * Filter the query and those get recomputed over a subset — a balance the
 * wallet never actually had, and gaps that never actually happened. So the
 * arithmetic keeps counting everything and this decides only what is on
 * screen.
 *
 * `any` is the default because it is what picking two tags usually means;
 * `all` is offered once there are two to combine, exactly as on the reports.
 */
/**
 * The chosen tags, written out to sit beside the filter button.
 *
 * A filter you cannot see is a filter you forget you set, and "why is this
 * list missing things" is the question that produces an hour later — so the
 * answer stays on the page even though the control that sets it does not.
 *
 * Named up to three and counted after that. The point is to notice the
 * filter is on, not to read a list: four names is already longer than the
 * line it sits on and would push the register down.
 *
 * The any/all qualifier only appears with two or more, because with one tag
 * "any of them" is a statement about nothing.
 */
/**
 * Which stored tag ids still apply.
 *
 * `vocabularyKnown` is the whole point. "No tags" and "no tags YET" are the
 * same empty list, and only one of them means a stored tag has been deleted —
 * so on a device whose first sync has not landed, nothing is dropped. The
 * alternative, which shipped briefly, was to reconcile against whatever the
 * replica happened to hold at mount: on a cold start that is nothing, so every
 * stored tag looked deleted and the reader's filter was silently forgotten.
 * Losing somebody's filter is not a safe default — it hands them a different
 * ledger and says nothing.
 */
export function keepKnownTags(
  stored: readonly string[], tags: readonly TagDto[], vocabularyKnown: boolean,
): string[] {
  if (!vocabularyKnown) return [...stored];
  const known = new Set(tags.map((t) => t.id));
  return stored.filter((id) => known.has(id));
}

/**
 * Everything the tag controls draw from, as one comparable string.
 *
 * They are redrawn only when this moves, because `loadAll` runs on every sync
 * and most syncs change no tag — redrawing regardless would rebuild the filter
 * popover and the quick form's tag select several times a minute, closing
 * whichever one the reader had open (invariant #5).
 *
 * It must therefore name every input, which is the bug it was written for: the
 * key was the vocabulary alone, so a selection restored *after* the first paint
 * never got drawn. The register filtered by a tag whose chip, count and Clear
 * button were all showing "none chosen" — a filter you cannot see and cannot
 * clear.
 */
export function tagControlsKey(
  tags: readonly TagDto[], selected: ReadonlySet<string>, mode: 'any' | 'all',
): string {
  return [
    tags.map((t) => `${t.id}\u0000${t.name}`).join('\u0001'),
    [...selected].sort().join(','),
    mode,
  ].join('\u0002');
}

/**
 * Can the short form post these two legs at all?
 *
 * The same test `buildQuickEntry` applies, asked before the form opens rather
 * than after Save is pressed. The two disagreed, and the way that showed up
 * was the worst available: the form opened, accepted an edit, and then
 * declined to save it with the reason only in the preview and the
 * screen-reader region. From the chair it was a Save button that did nothing.
 *
 * Each leg against its OWN account, in its own currency. Every tab has
 * "Arrives as" now, so a spend may leave in dollars and be booked in forints
 * — the entry this was widened for — and what the form cannot say is only a
 * leg claiming a currency its account does not hold. An expense leg written
 * in euros onto a forint-only account is a perfectly storable transaction and
 * a misstatement this form would repeat on save; it belongs in the full
 * editor, which shows the mismatch rather than papering over it.
 */
export function quickFormCanHold(
  legs: ReadonlyArray<{ account: { currencyCode: string; isMulticurrency: boolean }; currencyCode: string }>,
): boolean {
  return legs.every(({ account, currencyCode }) =>
    account.isMulticurrency || account.currencyCode === currencyCode);
}

/**
 * The two legs the short form posts, signed, each in its own currency.
 *
 * The money LEAVES one end as `amount` in `ccy` and ARRIVES at the other as
 * `arrives` in `arrivesCcy` — the same sentence on every tab. Which end is
 * the money account is what differs: it is where a Spend or a Move leaves
 * from, and where Money in arrives. Legs come back money-first, because the
 * save path and the register both read `splits[0]` as the money leg.
 */
export function quickLegs(
  kind: 'spend' | 'income' | 'move',
  sent: { amount: number; ccy: string },
  arrives: { amount: number; ccy: string },
): { money: { amount: number; ccy: string }; other: { amount: number; ccy: string } } {
  const leaving = { amount: -sent.amount, ccy: sent.ccy };
  const arriving = { amount: arrives.amount, ccy: arrives.ccy };
  return kind === 'income'
    ? { money: arriving, other: leaving }
    : { money: leaving, other: arriving };
}

/**
 * The debt between wallets the short form is asking to record, or "none".
 *
 * Each tab says it its own way, and this is where they become one record
 * (ADR-0023, amendment of 2026-10-18):
 *
 *   Move       its own two wallets, measured on the leg that ARRIVED — the
 *              one the read path always fell back to;
 *   Spend      "borrowed from" another wallet: the spending wallet paid with
 *              money it did not have, and owes the one it borrowed from —
 *              the common case, and the one `debtShiftLegs` moves the money
 *              for; or "paid for" another wallet: the spending wallet lent,
 *              the one it was for owes. Both measured on the payment leg;
 *   Money in   "received for" another wallet: that wallet's money landed in
 *              this one, which now owes it, measured on the arriving leg.
 *
 * `amount` empty is all of the leg. Anything that leaves the record without
 * two different wallets comes back as `{ kind: '' }` — "there is none of
 * these any more" — never as nothing, because the form offers the question
 * on every tab and an answer of "no" has to be able to take back a "yes".
 */
export function quickDebtMove(
  tab: 'spend' | 'income' | 'move',
  debt: { kind: string; forWallet: string; amount: string; measure?: 'sent' | 'arrived' | '' },
  wallets: { money: string | null; other: string | null },
  legIds: { money: string; other: string },
): { kind: '' } | {
  kind: string; fromWalletId: string; toWalletId: string; amountSplitId: string; amount: string | null;
} {
  const { kind, inward } = debtChoice(debt.kind);
  if (!kind || !wallets.money) return { kind: '' };
  const amount = debt.amount.trim() === '' ? null : debt.amount.trim();
  /*
   * Which leg the debt is counted on, and so in which currency: the amount
   * that LEFT, or the one that ARRIVED. They differ only on a conversion —
   * dollars paid, forints booked to the car — and then either is a fair
   * measure; which one decides the currency the debt is owed in.
   */
  const measure = debt.measure || defaultDebtMeasure(tab);
  const sentLeg = tab === 'income' ? legIds.other : legIds.money;
  const arrivedLeg = tab === 'income' ? legIds.money : legIds.other;
  const amountSplitId = measure === 'sent' ? sentLeg : arrivedLeg;
  if (tab === 'move') {
    if (!wallets.other || wallets.other === wallets.money) return { kind: '' };
    return { kind, fromWalletId: wallets.money, toWalletId: wallets.other, amountSplitId, amount };
  }
  if (!debt.forWallet || debt.forWallet === wallets.money) return { kind: '' };
  // Money in is always "received for": that wallet's money, this one owes it.
  return tab === 'spend' && !inward
    ? { kind, fromWalletId: wallets.money, toWalletId: debt.forWallet, amountSplitId, amount }
    : { kind, fromWalletId: debt.forWallet, toWalletId: wallets.money, amountSplitId, amount };
}

/**
 * What the short form's "Between wallets" choice means: the stored kind, and
 * whether the other wallet's money came IN to this one.
 *
 * Spend offers the direction in the choice itself — "Borrowed from" and
 * "Paid for" — because that is how it is thought: we pay from Renovation and
 * borrow the rest from Car. `borrow` and `grant-in` are those inward choices;
 * `loan` and `grant` are the outward ones, and what the other tabs offer.
 */
export function debtChoice(value: string): { kind: '' | 'loan' | 'grant'; inward: boolean } {
  switch (value) {
    case 'borrow': return { kind: 'loan', inward: true };
    case 'grant-in': return { kind: 'grant', inward: true };
    case 'loan': return { kind: 'loan', inward: false };
    case 'grant': return { kind: 'grant', inward: false };
    default: return { kind: '', inward: false };
  }
}

/** The options each tab offers, as [value, label, title]. */
export function debtChoices(tab: 'spend' | 'income' | 'move'): ReadonlyArray<readonly [string, string, string]> {
  const none = ['', 'No', 'Nobody owes anybody because of this'] as const;
  return tab === 'spend'
    ? [
      none,
      ['borrow', 'Borrowed from', 'Paid from this wallet, partly or wholly with another wallet\u2019s money \u2014 owed back'],
      ['loan', 'Paid for', 'Paid on another wallet\u2019s behalf \u2014 it owes this one'],
      ['grant-in', 'Grant from', 'Another wallet\u2019s money, given for good'],
      ['grant', 'Grant to', 'Paid for another wallet, given for good'],
    ]
    : [
      none,
      ['loan', 'Loan / payback', 'To be paid back \u2014 or paying back what was owed'],
      ['grant', 'Free grant', 'Handed over for good, never coming back'],
    ];
}

/**
 * Where a debt is counted when nobody has said: on the money account's own
 * leg for a Spend and Money in — the wallet's own money — and on the leg
 * that arrived for a Move, which is what a Move's debt always was.
 */
export function defaultDebtMeasure(tab: 'spend' | 'income' | 'move'): 'sent' | 'arrived' {
  return tab === 'spend' ? 'sent' : 'arrived';
}

export function tagFilterSummary(names: readonly string[], mode: 'any' | 'all'): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  const qualifier = ` \u2014 ${mode} of them`;
  return names.length <= 3 ? names.join(', ') + qualifier : `${names.length} tags${qualifier}`;
}

export function filterRowsByTags<T extends { tagIds: string[] }>(
  rows: readonly T[], selected: ReadonlySet<string>, mode: 'any' | 'all',
): T[] {
  if (selected.size === 0) return [...rows];
  return rows.filter((row) => (mode === 'all'
    ? [...selected].every((id) => row.tagIds.includes(id))
    : row.tagIds.some((id) => selected.has(id))));
}

/**
 * A second running total, over only the rows a tag filter is showing.
 *
 * The register's own `running` column is the wallet's real balance after each
 * row, counted over EVERY entry including the ones the filter hides — that is
 * deliberate and must not change, because a balance computed over a subset is
 * a balance the wallet never had. But it answers "what did this do to the
 * wallet", and a reader who has filtered to one project is asking a different
 * question: "what has this project come to so far".
 *
 * So: the same arithmetic on the same field, over the visible rows alone. That
 * equivalence is the point — with no filter on, every row is visible and this
 * would reproduce `running` exactly, which is why it is not shown then.
 *
 * Per currency, never netted across one, like every other number on this page.
 * Scaled integers rather than floats for the same reason the reports page uses
 * them: a cumulative sum is where a half-cent becomes visible.
 *
 * Computed here rather than on the server because the tag filter is a client
 * concern by construction — the register deliberately ships every row with its
 * `tagIds` so that the balance and the shortfall episodes can be computed over
 * the whole sequence. Asking the server to filter would produce a running
 * total over a subset, which is the thing this column exists to keep separate.
 */
/**
 * The wallet's whole position after each row, in one currency.
 *
 * The question a multicurrency log could not answer: a wallet short in
 * forints and long in euros shows two running columns, both true, neither of
 * them "how am I doing". This converts the lot at today's rate so there is one
 * number to read, and a wallet near zero looks near zero.
 *
 * **It is a reading, not a balance,** and the two differ in a way worth being
 * plain about: the rate is today's, applied to every row, so a line from
 * January is shown at a value it never actually had. For the newest row — the
 * one the question is usually about — that is exactly right. Going up the
 * list it drifts into "what this would be worth now". The register still
 * carries each currency's own running total underneath, and `ADR-0023`'s rule
 * that a wallet is short in forints or in euros, never just short, is kept by
 * `shortIn`: the mark stays per currency even when the figure is converted.
 *
 * `missing` is a currency with no rate — dropped from the sum, which makes
 * the total wrong by an amount nobody can see unless it is said. Same stance
 * as `totalsHtml`'s own warning, for the same reason.
 */
/**
 * The mark on a running total, and what it is a total of.
 *
 * At phone width the register stacks its numbers instead of putting them in
 * columns, and each one carried its own word — "RUNNING", on all two hundred
 * and fifty rows, which is a column heading printed once per row. The mark
 * replaces it: one glyph, leading the chip, so the line reads
 * `∑ 🇺🇸 USD 13,580.00` left to right.
 *
 * Two glyphs, because the number changes meaning with the wallet selector
 * and nothing on the row said so: a slice of the book (one wallet, or the
 * money in none of them) is `∑`, and the whole book is `∑∑`. Reported as
 * exactly that confusion — a +13,580 row in Car whose running total read
 * -1,278.41, which was the book's position and not Car's.
 *
 * Tried and rejected at this size, both by looking rather than by reasoning:
 * `⅀` (U+2140, the double-struck summation) is a different glyph at 72px and
 * the same one at 12px, and two `∑` offset diagonally — the first idea — is
 * a smudge. Side by side they are two marks and read as two marks.
 *
 * The glyph is decoration; `runningScopeSays` is the sentence, and it is on
 * the cell as its accessible name. Colour and shape are never the only
 * signal (invariant #11).
 */
export const TOTAL_MARK = '\u2211';

/** Everything in the book, rather than one slice of it. */
export const WHOLE_BOOK_MARK = '\u2211\u2211';

/**
 * The two marks that say a figure is in the wrong place.
 *
 * `W` beside a running total: the WALLET (or the book) is short in that
 * currency. `A` beside an amount: the ASSET ACCOUNT that leg came out of is
 * below zero — money spent out of an account that did not hold it.
 *
 * Two different facts, and the reason they are two marks rather than one
 * repeated: a wallet is about what money is earmarked FOR, an account about
 * whether the money EXISTS. A wallet can be perfectly funded out of a bank
 * account that is empty, and the reverse.
 *
 * They are beside different figures on purpose. A running total is per
 * wallet and per currency; an account is per leg. A row with two legs on two
 * accounts has one running chip and two accounts, so a single stacked marker
 * in the running column could not say which account it meant. Beside the
 * amount it needs no saying.
 *
 * A letter rather than a glyph, because `❗` could only ever mean "something
 * is wrong here" — which, now that two things can be, is the one thing not
 * worth saying. Never the letter alone: the colour, the letter and the
 * sentence in `title`/`aria-label` are three tellings of it (invariant #11).
 */
export function shortfallMarkHtml(letter: 'W' | 'A', says: string, moves: readonly string[] = []): string {
  // Not `title`: the browser waits a second or so before showing one, and
  // the reader hovering a mark has already asked. The page's own tooltip
  // shows it at once (see the register's hover); the same words are the
  // accessible name.
  const full = [says, ...moves].join('. ');
  return `<span class="wl-reg__over" role="img" aria-label="${escapeHtml(full)}"`
    + ` data-tip-says="${escapeHtml(says)}" data-tip-moves="${escapeHtml(moves.join('\n'))}">${letter}</span>`;
}

/**
 * The `A` beside an amount: the account this went below zero.
 *
 * A row that stands for a move inside what is being looked at carries
 * several legs on several accounts, and is marked if any of them went under.
 * The mark named the row's own account all the same — the first leg's — so
 * paying Sakhi Sevaka back from Revolut said "Revolut is below zero after
 * it" while Revolut stayed positive and it was Sakhi Sevaka's account that
 * was still under. Reported from prod. On such a row it names the legs that
 * went under, each with its own before and after.
 */
export function accountMarkHtml(row: Pick<RegisterRow,
  'accountOverdrawn' | 'accountName' | 'accountBefore' | 'accountAfter' | 'currencyCode' | 'legs'>): string {
  if (!row.accountOverdrawn) return SHORTFALL_SLOT;
  const under = row.legs.length > 0
    ? row.legs.filter((l) => l.accountOverdrawn)
    : [{ accountName: row.accountName, accountBefore: row.accountBefore, accountAfter: row.accountAfter }];
  const names = [...new Set(under.map((l) => l.accountName))];
  const said = names.length === 1 ? `${names[0]!} is`
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!} are`;
  return shortfallMarkHtml('A', `${said} below zero in ${row.currencyCode} after this`,
    under.flatMap((l) => (l.accountBefore !== null && l.accountAfter !== null
      ? [moveLine(l.accountName, l.accountBefore, l.accountAfter, row.currencyCode)] : [])));
}

/** “Safe: 1,200.00 → −300.00 EUR”, for a mark's tooltip. */
export function moveLine(who: string, before: string, after: string, ccy: string): string {
  return `${who}: ${formatAmount(before, ccy)} → ${formatAmount(after, ccy)} ${ccy}`;
}

/** The empty slot that keeps a marked figure in line with an unmarked one. */
export const SHORTFALL_SLOT = '<span class="wl-reg__over wl-reg__over--none" aria-hidden="true"></span>';

/**
 * Where the money went, as a line: `Donations → Safe`, `Safe → Food`.
 *
 * The log named the category and not the account. "· Donations" says what
 * kind of money arrived and leaves out which account now holds it, which is
 * half the question somebody opens this list to answer — and the half that
 * the `A` mark beside the figure is already talking about.
 *
 * The order is the direction of travel, read off the sign, because a fixed
 * order would be wrong half the time: money coming IN arrives from the
 * category into the account, money going OUT leaves the account for the
 * category. Printing "Safe → Donations" on an income is a smaller mistake
 * than it looks only until somebody uses it to decide something.
 *
 * One name alone when there is no other: a transfer between two money
 * accounts has no category leg, and a collapsed row (`amount === '0'`) is
 * several legs whose direction is in the arrow beside the wallets and whose
 * accounts are in the panel underneath.
 */
/**
 * An account and everything filed under it.
 *
 * "Account" on this bar means the subtree, not the single row: narrowing to
 * `Assets` and being shown nothing because the money is in `Assets ➤ Safe`
 * would be a filter that answers a question nobody asked. The picker only
 * offers leaves today, so this is usually a set of one — which is exactly
 * why it is worth writing down now rather than discovering later.
 *
 * Depth-guarded, because a parent chain is data and a cycle in it would
 * otherwise hang the page rather than draw a wrong list.
 */
export function accountSubtree(
  accounts: readonly { id: string; parentId?: string | null }[], rootId: string,
): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const a of accounts) {
    const parent = a.parentId ?? '';
    if (parent === '') continue;
    (childrenOf.get(parent) ?? childrenOf.set(parent, []).get(parent)!).push(a.id);
  }
  const out = new Set<string>([rootId]);
  const queue = [rootId];
  for (let guard = 0; queue.length > 0 && guard < 10000; guard += 1) {
    for (const child of childrenOf.get(queue.shift()!) ?? []) {
      if (out.has(child)) continue;
      out.add(child);
      queue.push(child);
    }
  }
  return out;
}

/**
 * The log rows whose transaction has a leg on one of these accounts — ANY
 * leg, not only the money legs the log is made of.
 *
 * The log holds asset and liability legs and nothing else, so a test against
 * those alone could never match an equity or expense account: picking
 * `Equity / Open.Bal.` asked "which entries opened a balance" and the answer
 * was always none, though the opening entries were right there with their
 * money side in the list. Reported from use. Matching the transaction
 * answers the question that was asked, and keeps both ends of a transfer
 * whichever end was picked.
 */
export function rowsTouchingAccounts<T extends { transactionId: string }>(
  rows: readonly T[],
  legs: Iterable<{ transactionId: string; accountId: string }>,
  wanted: ReadonlySet<string>,
): T[] {
  const touching = new Set<string>();
  for (const leg of legs) if (wanted.has(leg.accountId)) touching.add(leg.transactionId);
  return rows.filter((r) => touching.has(r.transactionId));
}

export function moneyPath(accountName: string, counterpart: string, amount: string): string {
  if (!counterpart) return accountName;
  if (!accountName) return counterpart;
  const value = Number(amount);
  if (!Number.isFinite(value) || value === 0) return counterpart;
  return value > 0 ? `${counterpart} \u2192 ${accountName}` : `${accountName} \u2192 ${counterpart}`;
}

export function runningMark(scope: string): string {
  return scope === 'all' ? WHOLE_BOOK_MARK : TOTAL_MARK;
}

/** What the running column is counting, in words. Used above the list and on each cell. */
export function runningScopeSays(scope: string, walletName?: string): string {
  if (scope === 'all') return 'Running total for the whole book — every wallet together';
  if (scope === UNASSIGNED) return 'Running total for the money in no wallet';
  return `Running total for ${walletName ?? 'this wallet'}, including anything nested inside it`;
}

/**
 * What the shortfall mark is talking about.
 *
 * It said "this wallet is in minus" whatever the log was showing, and the
 * log is not always showing a wallet. On "All wallets together" the running
 * total is the whole book's position, so the mark lands beside rows that
 * belong to a wallet which is perfectly healthy — reported against a
 * +13,580 USD row in Car whose running total read -1,278.41 and carried the
 * alarm, while Car itself had never been short of anything. The figure was
 * right; the sentence beside it named the wrong thing.
 *
 * Three scopes, three nouns, because "Unassigned" is not a wallet either —
 * it is the money that is in none of them.
 */
export function shortfallSubject(scope: string): string {
  if (scope === 'all') return 'the book as a whole is';
  if (scope === UNASSIGNED) return 'money in no wallet is';
  return 'this wallet is';
}

export function convertedRunning(
  rows: readonly { splitId: string; amount: string; currencyCode: string }[],
  target: string,
  rates: Record<string, number>,
  /**
   * Accumulate only these rows — what the tag column is counted over.
   *
   * Same shape and same meaning as `taggedRunning`'s, and here for the same
   * reason: the tag total answers "how much of this has the project cost",
   * which is a sum over the rows the filter is showing and no others. The
   * wallet's own total passes nothing and counts everything, because hiding a
   * row changes what you are looking at and not what the wallet holds.
   */
  visible?: ReadonlySet<string>,
  /** `settledRunning`'s `shiftAt`, by row index: the debts settled on top of what the rows add up to. */
  shiftAt?: ReadonlyArray<Readonly<Record<string, string>>>,
): { byRow: Map<string, { amount: string; shortIn: string[] }>; missing: string[] } {
  const running = new Map<string, bigint>();
  const byRow = new Map<string, { amount: string; shortIn: string[] }>();
  const missing = new Set<string>();

  for (const [i, row] of rows.entries()) {
    if (visible && !visible.has(row.splitId)) continue;
    running.set(row.currencyCode, (running.get(row.currencyCode) ?? 0n) + toScaled(row.amount));
    const totals = new Map(running);
    for (const [code, v] of Object.entries(shiftAt?.[i] ?? {})) totals.set(code, (totals.get(code) ?? 0n) + toScaled(v));
    const held: Record<string, string> = {};
    const shortIn: string[] = [];
    for (const [code, value] of totals) {
      if (value === 0n) continue;
      held[code] = fromScaled(value);
      if (value < 0n) shortIn.push(code);
    }
    const { amount, missing: noRate } = convertTotals(held, target, rates);
    for (const code of noRate) missing.add(code);
    byRow.set(row.splitId, { amount: amount.toFixed(4), shortIn: shortIn.sort() });
  }
  return { byRow, missing: [...missing].sort() };
}

export function taggedRunning(
  rows: readonly { splitId: string; amount: string; currencyCode: string }[],
  visible: ReadonlySet<string>,
): Map<string, string> {
  const running = new Map<string, bigint>();
  const out = new Map<string, string>();
  for (const row of rows) {
    if (!visible.has(row.splitId)) continue;
    const next = (running.get(row.currencyCode) ?? 0n) + toScaled(row.amount);
    running.set(row.currencyCode, next);
    out.set(row.splitId, fromScaled(next));
  }
  return out;
}

/** An account's position after one entry, as the log's running column shows it. */
export interface AccountRunningAt {
  /** Per currency the entry has a leg in on the account, in the order met: the balance after it. */
  moved: Array<{ currencyCode: string; running: string }>;
  /** Every currency the account holds after the entry — what a converted figure adds up. */
  held: Record<string, string>;
}

/**
 * The log's running column when it is filtered to an account: that
 * account's balance after each entry, per currency.
 *
 * Asked from use: an account filter is the question "what happened on this
 * account", and the book's or the wallet's running total beside its rows
 * answers a different one. The account's own balance is the figure a bank
 * statement can be held against.
 *
 * With a wallet chosen as well, it is the part of the account that wallet
 * accounts for — both filters at once, which is what the rows under it are.
 * A money leg counts when its wallet is in `inWallet`. A category leg has no
 * wallet of its own (the earmark is on the money side), so it counts when
 * the entry is one of the wallet's: what Car spent on fuel. With every
 * wallet chosen there is no `scope` and every leg counts — the account as
 * the accounts page has it.
 *
 * Walked over every entry in the book, in the register's order, so a
 * balance does not depend on which rows a tag or currency filter is hiding.
 * Returned only for entries with a leg on the account.
 */
export function accountRunning(
  transactions: readonly { id: string; txDate: string; seq?: number | null }[],
  legs: readonly { transactionId: string; accountId: string; currencyCode: string; amount: string; walletId: string | null }[],
  accountIds: ReadonlySet<string>,
  scope?: {
    inWallet: (walletId: string | null) => boolean;
    isMoney: (accountId: string) => boolean;
    /** The entries the wallet takes part in: the log's own rows. */
    entries: ReadonlySet<string>;
  },
): Map<string, AccountRunningAt> {
  const byTx = new Map<string, Array<(typeof legs)[number]>>();
  for (const leg of legs) {
    if (!accountIds.has(leg.accountId)) continue;
    (byTx.get(leg.transactionId) ?? byTx.set(leg.transactionId, []).get(leg.transactionId)!).push(leg);
  }
  const ordered = transactions.filter((t) => byTx.has(t.id)).sort((a, b) =>
    a.txDate.localeCompare(b.txDate) || (a.seq ?? 0) - (b.seq ?? 0) || a.id.localeCompare(b.id));
  const counts = (leg: (typeof legs)[number]): boolean => !scope
    || (scope.isMoney(leg.accountId) ? scope.inWallet(leg.walletId) : scope.entries.has(leg.transactionId));
  const running = new Map<string, bigint>();
  const out = new Map<string, AccountRunningAt>();
  for (const tx of ordered) {
    const mine = byTx.get(tx.id)!;
    for (const leg of mine) {
      if (counts(leg)) running.set(leg.currencyCode, (running.get(leg.currencyCode) ?? 0n) + toScaled(leg.amount));
    }
    const codes = [...new Set(mine.map((l) => l.currencyCode))];
    const held: Record<string, string> = {};
    for (const [code, v] of running) if (v !== 0n) held[code] = fromScaled(v);
    out.set(tx.id, {
      moved: codes.map((code) => ({ currencyCode: code, running: fromScaled(running.get(code) ?? 0n) })),
      held,
    });
  }
  return out;
}

/**
 * The two end caps on a shortfall episode, for the row at `index`.
 *
 * The arrows point INTO the gap, so they depend on which way the register is
 * ordered. Newest first (what this page shows) puts the row where the wallet
 * went under at the BOTTOM of the run with the rest of it above, so its arrow
 * points up — the reverse of a date-ascending register. Words as well as
 * arrows, because the rail down the side is colour (invariant #11).
 */
export function episodeCaps(
  index: number, episode: { fromRow: number; toRow: number; closed: boolean } | undefined,
  newestFirst = true,
): string {
  if (!episode) return '';
  const cap = (arrow: string, text: string): string => `<div class="wl-reg__cap">${arrow} ${text}</div>`;
  const started = cap(newestFirst ? '▲' : '▼', `short from here${episode.closed ? '' : ' — still open'}`);
  const ended = episode.closed ? cap(newestFirst ? '▼' : '▲', 'back in the black') : '';
  const parts: string[] = [];
  if (index === episode.toRow) parts.push(ended);
  if (index === episode.fromRow) parts.push(started);
  return (newestFirst ? parts : parts.reverse()).join('');
}

/** The four things this page is about, one at a time. */
type Section = 'wallets' | 'log' | 'budgets' | 'debts';

const SECTIONS: ReadonlyArray<{ id: Section; label: string }> = [
  { id: 'wallets', label: 'Wallets' },
  { id: 'log', label: 'Log' },
  // Between the log and the debts because that is the order of interest: what
  // happened, whether it was within the plan, and who owes whom.
  { id: 'budgets', label: 'Budgets' },
  { id: 'debts', label: 'Between wallets' },
];

/** Where each section lives, for the nav and for `pushState`. */
export const SECTION_HREF: Record<Section, string> = {
  wallets: '/webledger/wallets',
  log: '/webledger/wallets/log',
  budgets: '/webledger/wallets/budgets',
  debts: '/webledger/wallets/between',
};

export function readSection(raw: string | undefined): Section {
  return SECTIONS.some((s) => s.id === raw) ? raw as Section : 'wallets';
}

/**
 * One section at a time, rather than the wallet list and its log side by side.
 *
 * The two-column version worked only on a wide screen and only while the
 * ledger was young: the log grows by a row per entry forever, so anything
 * placed under it — the debts table was — retreats further down the page every
 * month, and on a phone the second column landed below a log nobody could
 * scroll past. Sections cost one click and behave the same at every width.
 *
 * The switcher is the top nav now, and each section is a route — so a
 * bookmark, the back button and a shared link all land where they say. The
 * panes still live in one document and are swapped in place rather than
 * reloaded, because the page they share is the expensive part: a load builds
 * the wallet list, the budgets and the debts whichever section is showing.
 */
function shellBody(): string {
  return `
    <!-- Offline edits the server held for review (webledgerReviewBanner.ts):
         above everything, because its author has to see it whichever tab
         they are on. -->
    <div id="wlRefusedBanner" hidden></div>
    <div id="wlReviewBanner" hidden></div>
    <div class="wl-head-fold"><div class="wl-head-fold__in"><div class="wl-totals wl-totals--flow" role="status">
      <!-- Rewritten by paintCashTotals: on the log, with one wallet chosen,
           these are that wallet's figures and the sentence has to say so. A
           bar that silently changes what it is counting is worse than one
           that never narrowed at all. -->
      <span class="text-secondary small" id="wlTotalsLabel">Wallets add up to the money on hand</span>
      <span class="cf-cur-chips" id="wlCashTotals"></span>
      <!-- Shown by settledSwitchShown. -->
      <div class="form-check form-switch mb-0 wl-settled-switch" id="wlSettledTotalsWrap" hidden>
        <input class="form-check-input" type="checkbox" role="switch" id="wlSettledTotals">
        <label class="form-check-label small" for="wlSettledTotals">If debts were settled</label>
      </div>
      <select class="form-select form-select-sm ms-auto wl-totalmode" id="wlTotalMode"
        aria-label="Show the total by currency or converted into one"></select>
    </div></div></div>
    <div class="wl-sec mb-3" id="wlSecBar">
      <!-- The four sections moved to the top nav, where they are routes and
           carry their own counts. What stays is the one control that belongs
           to this page rather than to navigation. -->
      <button class="btn btn-sm btn-primary" type="button" id="wlQuickBtn">Add entry</button>
    </div>

    <section id="wlPane-wallets" data-pane="wallets" role="tabpanel"
      aria-labelledby="wlTab-wallets" tabindex="-1">
      <div id="wlAttention" class="mb-3"></div>
      <div class="cf-card p-3">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <div class="cf-h">Wallets</div>
            <div class="form-check form-switch mb-0 wl-settled-switch">
              <input class="form-check-input" type="checkbox" role="switch" id="wlSettledList">
              <label class="form-check-label small" for="wlSettledList">If debts were settled</label>
            </div>
          </div>
          <button class="btn btn-sm btn-outline-primary" type="button" id="wlNewBtn">New wallet</button>
        </div>
        <p class="small text-muted mb-2">Open one to see everything that went through it.</p>
        <div id="wlInspect" class="wl-acct-inspect mb-2" role="status" aria-live="polite" hidden></div>
        <div id="wlList"></div>
      </div>
    </section>

    <section id="wlPane-log" data-pane="log" role="tabpanel"
      aria-labelledby="wlTab-log" tabindex="-1" hidden>
      <div class="cf-card p-3">
        <!-- One line: which log, what it means, and what it is filtered to.
             The standing explanation is behind the "?" because it is the
             same sentence every time you look at this page — worth reading
             once and worth having, but not worth a paragraph above the list
             for ever. -->
        <!-- No position-relative utility here: Bootstrap's carries !important
             and would beat the sticky. Sticky establishes a containing block
             for the tag popover just as relative did. -->
        <!-- Four filters, one shape.
             
             They grew one at a time and ended up as four idioms: a bare
             select cleared by choosing its first option, a tree cleared by a
             text link, a second bare select cleared by its first option
             again, and a popover cleared by another link. Nothing said they
             were the same kind of thing, and nothing taught you how to turn
             the next one off.
             
             Now every one of them is a notched field with its name in its
             own border and, when it is doing something, a cross at its
             right — see fieldClearHtml. The select keeps its first option as
             well, because "All wallets together" and "Any" are real choices
             and not only the absence of a filter. -->
        <div class="wl-log__bar d-flex flex-wrap align-items-end gap-2">
          <div style="min-width:240px">
            ${clearableFieldHtml('Wallet log',
    '<select id="wlLogSelect" class="wl-nf__ctl" aria-label="Wallet log"></select>',
    fieldClearHtml('wlLogSelectClear', 'Show every wallet'), 'id="wlLogSelectLbl"')}
          </div>
          <!-- The chart of accounts, through the same tree every other
               account field on this page opens. A select would have to list
               every account flat, which is the thing the tree exists to
               avoid; and unlike currencies there are too many to put on the
               bar. The select below is the value, hidden; the button only
               opens the tree.
               
               Its cross is the optional one: this control is also every
               account field on the forms, where there is no "no account" to
               clear to, so the clear is the caller's to ask for. -->
          <div id="wlLogAccWrap" style="min-width:190px">
            <select hidden id="wlLogAcc"></select>
            ${clearableFieldHtml('Account',
    '<button type="button" class="wl-nf__ctl wl-acct-btn" id="wlLogAccBtn"'
    + ' data-acct-btn="wlLogAcc" aria-label="Account">'
    + '<span data-acct-icon></span>'
    + '<span class="wl-acct-btn__name" data-acct-name></span>'
    + '<span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span>'
    + '</button>',
    fieldClearHtml('wlLogAccClear', 'Show every account'), 'id="wlLogAccLbl"')}
          </div>
          <!-- Beside the wallet rather than behind a button: unlike tags there
               are only ever a handful of currencies, and "which money" is a
               question asked of a mixed log constantly. -->
          <div id="wlLogCcyWrap" hidden style="min-width:130px">
            ${clearableFieldHtml('Currency',
    '<select id="wlLogCcy" class="wl-nf__ctl" aria-label="Currency"></select>',
    fieldClearHtml('wlLogCcyClear', 'Show every currency'), 'id="wlLogCcyLbl"')}
          </div>
          <!-- A button, not a row of chips. Every tag in the book used to be
               laid out here permanently, so a book with forty of them spent
               several lines of the page on a filter that is mostly not in
               use. What is worth the space is the ANSWER — which tags are
               on — and that is what the button says once there are any. -->
          <div id="wlLogTags" hidden style="min-width:150px">
            ${clearableFieldHtml('Tags',
    '<button type="button" class="wl-nf__ctl wl-acct-btn" id="wlLogTagBtn"'
    + ' aria-haspopup="dialog" aria-expanded="false" aria-label="Tags">'
    + '<span class="wl-acct-btn__name" id="wlLogTagSummary"></span>'
    + '<span class="badge text-bg-primary" id="wlLogTagCount" hidden></span>'
    + '<span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span>'
    + '</button>',
    fieldClearHtml('wlLogTagClear', 'Show every tag'), 'id="wlLogTagsLbl"')}
          </div>
          <!-- The fifth filter, typed rather than picked: notes and amounts,
               narrowing as you type — see webledgerLogSearch. Same notched
               shape and the same cross as the four before it; a browser's
               own clear button is hidden so there is only one. -->
          <div style="min-width:180px">
            ${clearableFieldHtml('Search',
    '<input type="search" id="wlLogSearch" class="wl-nf__ctl wl-nf__search" autocomplete="off"'
    + ' spellcheck="false" placeholder="Note or amount" aria-label="Search notes and amounts">',
    fieldClearHtml('wlLogSearchClear', 'Clear the search'))}
          </div>
          <!-- How much of the log you are being shown, next to the controls
               that decided it and next to the "?" that explains it.
               Deliberately only the arithmetic: "109/284" is the part worth
               having in the corner of your eye every time you glance at this
               bar, and the sentence about what the running total still counts
               is the part worth reading once. That sentence is in the help
               panel, which is a click away and immediately to the right.
               The full label is on the element for anyone who cannot infer a
               ratio from two numbers and a slash, which includes every screen
               reader and a fair number of people. -->
          <span class="small text-secondary mb-1" id="wlLogFilterCount"
            aria-live="polite" hidden></span>
          <!-- Last on the row, after the controls it explains: the "?" is the thing
               you reach for once and then stop seeing, so it should not sit
               between the two things you use every time. The small bottom
               margin optically centres a 24px glyph against a 31px control,
               which align-items-end alone would leave sitting low. -->
          <span class="cf-help cf-help--left mb-1">
            <button type="button" class="cf-help__btn" id="wlLogHelpBtn"
              aria-label="What this log shows" aria-expanded="false"
              aria-controls="wlLogHelpPanel">${HELP_BUBBLE_ICON}</button>
            <span class="cf-help__panel" id="wlLogHelpPanel" role="note"></span>
          </span>
          <!-- The log's own copy, on the right of the row it belongs to. It
               is a second element rather than the one above moved, because
               that one still serves the other three sections; both call the
               same opener. -->
          <button type="button" class="btn btn-sm btn-primary ms-auto mb-1"
            id="wlQuickBtnLog">Add entry</button>
        </div>
        <!-- The list scrolls inside this, not with the page. That is what
             lets the header come and go without the reader losing their
             place: the region changes height, its scrollTop does not, so
             whatever was the top row stays the top row. -->
        <div class="wl-reg-scroll" id="wlRegScroll"><div id="wlRegister"></div></div>
      </div>
    </section>

    ${budgetsPaneHtml()}

    <section id="wlPane-debts" data-pane="debts" role="tabpanel"
      aria-labelledby="wlTab-debts" tabindex="-1" hidden>
      <div class="cf-card p-3">
        <div class="cf-h mb-2">Between wallets</div>
        <div id="wlDebts"></div>
      </div>
    </section>`;
}

function dialogHtml(): string {
  return `
  <dialog id="wlTransferDlg" class="wl-dlg">
    <form method="dialog">
      <h2 class="h6 mb-2" id="wlTransferTitle"></h2>
      <p class="small" id="wlTransferIntro"></p>
      <div class="mb-2" id="wlTransferFromWrap">
        <label class="cf-h d-block mb-1" for="wlTransferFrom">Take from</label>
        <select id="wlTransferFrom" class="form-select form-select-sm"></select>
      </div>
      <div class="row g-2">
        <div class="col-6">
          <label class="cf-h d-block mb-1" for="wlTransferAmount">Amount</label>
          <input id="wlTransferAmount" class="form-control form-control-sm" inputmode="decimal">
        </div>
        <div class="col-6" id="wlTransferKindWrap">
          <label class="cf-h d-block mb-1" for="wlTransferKind">Kind</label>
          <select id="wlTransferKind" class="form-select form-select-sm">
            <option value="loan" title="To be paid back — or paying back what was owed">Loan / payback</option>
            <option value="grant" title="Handed over for good, never coming back">Free grant</option>
          </select>
        </div>
      </div>
      <div id="wlTransferWarn" aria-live="polite"></div>
      <div class="alert alert-danger py-2 small mt-2 mb-0" id="wlTransferError" hidden></div>
      <div class="d-flex justify-content-end gap-2 mt-3">
        <button class="btn btn-sm btn-outline-secondary" type="button" id="wlTransferCancel">Not now</button>
        <button class="btn btn-sm btn-primary" type="button" id="wlTransferConfirm">Record it</button>
      </div>
    </form>
  </dialog>
  <!-- The same mechanics as the transaction editor, and for the reason the
       differences kept producing bugs rather than for tidiness. This was a
       native dialog, whose top layer is why the account picker has to host
       itself inside, whose UA display:none fought the layout the panel
       needed, and whose UA centring is what the shell/panel split existed to
       work around. A Bootstrap modal is an ordinary positioned element with
       overflow-y:auto, so the picker, the drag and the scrolling all behave
       the way they already do in the full editor -- because they ARE the way
       they behave in the full editor. -->
  <div class="modal fade" id="wlQuickDlg" tabindex="-1" aria-hidden="true" data-bs-backdrop="static"
    aria-labelledby="wlqTitle">
    <!-- Not modal-lg. That is 800px and the form grew by 240 on the way to
         becoming a modal, for no reason anybody asked for: it was 560 as a
         native dialog and 560 is what the fields want. -->
    <div class="modal-dialog wl-modal--quick" data-dlg-panel>
      <div class="modal-content">
    <form>
      <div class="modal-header py-2 d-flex justify-content-between align-items-center gap-2" data-dlg-handle>
        <span class="cf-drag-grip" aria-hidden="true">&#10303;</span>
        <!-- Stepping through the log behind this form. Hidden while adding,
             because a new entry is not in the list yet and has nothing to be
             next to.

             Down for the previous entry and up for the next, because the log
             is newest first: the one BEFORE this in time is the one below it
             on the page. The arrows say where the marker will go, which is
             the only part of this the reader can check against the list. -->
        <span class="btn-group btn-group-sm me-1" id="wlqNav" hidden role="group"
          aria-label="Move through the list">
          <button type="button" class="btn btn-outline-secondary" id="wlqPrev"
            aria-label="Previous transaction — older, further down the list"
            title="Previous — older, down the list">&darr;</button>
          <button type="button" class="btn btn-outline-secondary" id="wlqNext"
            aria-label="Next transaction — younger, further up the list"
            title="Next — younger, up the list">&uarr;</button>
        </span>
        <h2 class="h6 mb-0" id="wlqTitle">Add an entry</h2>
        <!-- Beside the title, where it is read before the fields rather than
             hunted for among them. Empty for an entry that did not come from
             a bank, which is most of them. -->
        <span class="flex-grow-1" id="wlqBank"></span>
        <span class="cf-help">
          <button type="button" class="cf-help__btn" id="wlqHelpBtn"
            aria-label="What this form is for" aria-expanded="false" aria-controls="wlqHelpPanel">${HELP_BUBBLE_ICON}</button>
          <span class="cf-help__panel" id="wlqHelpPanel" role="note">The short way in. Everything here
            becomes an ordinary balanced transaction — the full editor is a click away on any row
            afterwards.</span>
        </span>
        <button type="button" class="btn btn-sm btn-outline-secondary" id="wlqHistory" aria-expanded="false" hidden>History</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" id="wlqFull" hidden>Open the full editor</button>
      </div>
      <div class="modal-body">
      <div class="alert alert-info py-2 px-3 small" id="wlqNotice" role="status" aria-live="polite" hidden></div>
      <!-- Who has this entry when it is not this tab's, since when, and the
           way in (webledgerHold.ts) — the same words the full editor uses. -->
      <div class="alert alert-secondary py-2 px-3 small" id="wlqHoldNotice" hidden></div>
      <!-- Taken off you: what was typed here, kept beside their version,
           each value with Keep mine — the full editor's banner and strips. -->
      <div id="wlqHeldBanner" hidden></div>
      <div id="wlqHeldFields"></div>

      <div class="btn-group btn-group-sm mb-3" role="group">
        <input type="radio" class="btn-check" name="wlqKind" id="wlqSpend" value="spend" checked>
        <label class="btn btn-outline-primary" for="wlqSpend">Spend</label>
        <input type="radio" class="btn-check" name="wlqKind" id="wlqIncome" value="income">
        <label class="btn btn-outline-primary" for="wlqIncome">Money in</label>
        <!-- One Move, not two. "Move cash" moved between accounts and
             "Between wallets" moved between earmarks, and they were the same
             sentence — money leaves one place and arrives in another, and
             either end of it might be an account, a wallet, or both. Splitting
             them meant neither could say the other half: a transfer could not
             record whose money it became, and a re-earmarking could not cross
             two accounts. -->
        <input type="radio" class="btn-check" name="wlqKind" id="wlqMove" value="move">
        <label class="btn btn-outline-primary" for="wlqMove">Move</label>
      </div>

      <div class="row g-2">
        <!-- Date, then the note: the two things that say WHICH entry this
             is, read together on one line. The amount went down beside
             "Arrives as" (below), its other half when money changes currency.

             The date takes only the width a date needs. In a fixed share of
             the row a third of the field was empty space between the date
             and its calendar button.

             Every field here is notched — its name sits in its own top
             border. That is the house style, not a flourish: a label above
             only lines up when EVERY control has one, and the moment some do
             not the row becomes a staircase. See notchedFieldHtml. -->
        <div class="col-auto wl-qdate">
          ${notchedFieldHtml('Date', dateFieldHtml('wlqDate'))}
        </div>
        <div class="col">
          ${notchedFieldHtml('Note',
            '<input id="wlqNote" class="wl-nf__ctl" placeholder="optional" aria-label="Note">')}
        </div>

        <!-- An account and the wallet its money is earmarked for belong on
             one line: the wallet is a property OF that leg rather than a
             separate question, and reading them apart invites earmarking the
             wrong one. The wallet takes only what its longest name needs and
             the account takes the rest, because account paths are long and
             wallet names are short. Below the small breakpoint they stack,
             where sharing a line would leave both too narrow to read. -->
        <div class="col-12 col-sm-8">
          <!-- The select stays the value, hidden, exactly as in the full
               editor: everything that reads or writes this field still goes
               through it and the button only opens the tree. -->
          <select hidden id="wlqAccount"></select>
          ${notchedFieldHtml('Paid from',
            '<button type="button" class="wl-nf__ctl wl-acct-btn" id="wlqAccountBtn"'
            + ' data-acct-btn="wlqAccount" aria-label="Account">'
            + '<span data-acct-icon></span>'
            + '<span class="wl-acct-btn__name" data-acct-name></span>'
            + '<span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span>'
            + '</button>', 'id="wlqAccountLbl"')}
        </div>
        <div class="col-12 col-sm-4" id="wlqWalletWrap">
          ${notchedFieldHtml('Wallet',
            '<select id="wlqWallet" class="wl-nf__ctl" aria-label="Wallet"></select>', 'id="wlqWalletLbl"')}
        </div>

        <div class="col-12 col-sm-8" id="wlqAccount2Wrap" hidden>
          <select hidden id="wlqAccount2"></select>
          ${notchedFieldHtml('To account',
            '<button type="button" class="wl-nf__ctl wl-acct-btn" id="wlqAccount2Btn"'
            + ' data-acct-btn="wlqAccount2" aria-label="To account">'
            + '<span data-acct-icon></span>'
            + '<span class="wl-acct-btn__name" data-acct-name></span>'
            + '<span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span>'
            + '</button>')}
        </div>
        <div class="col-12 col-sm-4" id="wlqWallet2Wrap" hidden>
          ${notchedFieldHtml('To wallet',
            '<select id="wlqWallet2" class="wl-nf__ctl" aria-label="To wallet"></select>')}
        </div>

        <div class="col-12" id="wlqCategoryWrap">
          <select hidden id="wlqCategory"></select>
          ${notchedFieldHtml('Category',
            '<button type="button" class="wl-nf__ctl wl-acct-btn" id="wlqCategoryBtn"'
            + ' data-acct-btn="wlqCategory" aria-label="Category">'
            + '<span data-acct-icon></span>'
            + '<span class="wl-acct-btn__name" data-acct-name></span>'
            + '<span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span>'
            + '</button>', 'id="wlqCategoryLbl"')}
        </div>

        <!-- The amount, with "Arrives as" to its right when the money
             changes currency on the way: the two figures are one fact, the
             rate, and read as a pair. Half the row each, and half for the
             amount when it is alone too — a figure and a currency do not
             need the whole width, and stretched across it the number sat
             a long way from the label it belongs to. Stacked on a phone,
             where two number-and-currency pairs side by side are too
             narrow. -->
        <div class="col-12 col-sm-6">
          <!-- The currency is asked, not inferred. It used to be taken
               silently off the chosen account, which meant a donation in one
               currency into an account declared in another could not be said
               at all. -->
          ${notchedFieldHtml('Amount',
            '<span class="wl-nf__pair">'
            + '<input id="wlqAmount" class="wl-nf__ctl text-end cf-no-spin" type="number"'
            + ' min="0" placeholder="0" aria-label="Amount">'
            + '<select id="wlqCcy" class="wl-nf__ctl wl-nf__ctl--auto" aria-label="Currency"></select>'
            + '</span>')}
        </div>
        <!-- Always here on a Move, and always says a currency. On Spend and
             Money in it appears where the destination allows a choice
             (arrivesShown): dollars paid and forints booked to the car.
             On a Move it used to appear only when the money landed somewhere that held
             a currency the source did not — which asked the destination what
             it COULD hold, and so missed every conversion inside one
             multi-currency account. Saying it outright is both simpler and
             the only way to express a conversion the accounts alone cannot
             imply.
             The pair of amounts IS the rate, and nothing has to store one:
             100.00 EUR out and 38,500.00 HUF in says it exactly. The same
             thing the bank-feed merge relies on. -->
        <div class="col-12 col-sm-6" id="wlqTargetWrap">
          ${notchedFieldHtml('Arrives as',
            '<span class="wl-nf__pair">'
            + '<input id="wlqTargetAmount" class="wl-nf__ctl text-end cf-no-spin" type="number"'
            + ' min="0" placeholder="0" aria-label="Amount that arrives">'
            + '<select id="wlqTargetCcy" class="wl-nf__ctl wl-nf__ctl--auto"'
            + ' aria-label="Currency it arrives in"></select>'
            + '</span>')}
        </div>

        <!-- Who owes whom because of this entry, on every tab (ADR-0023,
             amendment of 2026-10-18). A Move asks it of its own two wallets;
             Spend and Money in name the other wallet, because the entry has
             only one: "Maintenance paid this for the car". The amount is
             optional — empty is all of it — and never more than the leg.
             Below the amounts, because "how much of it" reads after "it". -->
        <div class="col-12" id="wlqXferKindWrap" hidden>
          <div class="row g-2">
            <!-- Short words, because three fields share this line and "How
                 much" with its currency needs the room: the full question is
                 the select's accessible name, and each option's title says
                 what it means. -->
            <div class="col">
              ${notchedFieldHtml('Between wallets',
                // Options per tab, from debtChoices (paintMoveExtras).
                '<select id="wlqXferKind" class="wl-nf__ctl" aria-label="Does another wallet owe or get this">'
                + '<option value="">No</option>'
                + '</select>', 'id="wlqXferKindLbl"')}
            </div>
            <div class="col-12 col-sm-4" id="wlqXferForWrap" hidden>
              ${notchedFieldHtml('For wallet',
                '<select id="wlqXferFor" class="wl-nf__ctl" aria-label="For which wallet"></select>',
                'id="wlqXferForLbl"')}
            </div>
            <div class="col-12 col-sm-5" id="wlqXferAmountWrap" hidden>
              ${notchedFieldHtml('How much',
                '<span class="wl-nf__pair">'
                + '<input id="wlqXferAmount" class="wl-nf__ctl text-end cf-no-spin" type="number" min="0"'
                + ' step="any" placeholder="all of it" aria-label="How much of it is owed — empty for all of it">'
                // Which of the entry's two amounts the debt is counted in —
                // the one that left, or the one that arrived. Only when they
                // are in different currencies; otherwise there is no choice.
                + '<select id="wlqXferCcy" class="wl-nf__ctl wl-nf__ctl--auto"'
                + ' aria-label="Currency the debt is recorded in"></select>'
                + '</span>')}
            </div>
          </div>
        </div>

        <!-- Tags above files: a tag is part of saying what this entry IS, and
             belongs with the fields that do. A receipt is evidence for it
             afterwards. -->
        <div class="col-12" id="wlqTagsWrap" hidden>
          <!-- The same notch as every other field, but a REAL fieldset: this
               one genuinely is a group of related controls (the chips and the
               picker), so unlike notchedFieldHtml it keeps its grouping
               semantics rather than taking role="none". -->
          <fieldset class="wl-nf">
            <legend id="wlqTagsLabel">Tags</legend>
            <div class="wl-tag-bar" role="group" aria-labelledby="wlqTagsLabel">
              <span id="wlqTagChips"></span>
              <select class="wl-nf__ctl wl-nf__ctl--auto" id="wlqTagPick" aria-label="Add a tag">
                <option value="">Add a tag…</option>
              </select>
            </div>
          </fieldset>
        </div>
        <div class="col-12">${attachmentBarHtml('wlq')}</div>
      </div>

      <!-- What this will post: one status line always — why it cannot be
           saved yet, or that it balances and what it owes — and under a
           toggle the two assistants, one after the other: what the entry
           does to each account, then where the two wallets of a debt stand
           (webledgerDebtHelper). Collapsed until somebody opens them, and
           remembered per user (PREF.quickAssist). Not live regions: they
           change on every keystroke. -->
      <!-- The heading is the toggle, its chevron in front, as a collapsible
           section in Notes: the whole line is the target, and the expander
           chevron is the app's one (styles.css), big enough to be seen — a
           ▸ character here read as a dot and was missed. -->
      <button type="button" class="cf-h wl-q-assist-btn mt-3 mb-1" id="wlqAssistBtn"
        aria-expanded="false" aria-controls="wlqAssist"
        title="What the entry does to each account, and where the wallets of a debt stand"><span
        class="cf-exp" aria-hidden="true"></span> What this will post</button>
      <div id="wlqStatus" class="small"></div>
      <div id="wlqAssist" hidden>
        <div id="wlqPreview" class="wl-q-preview mt-1"></div>
        <div id="wlqXferHelp" class="mt-2"></div>
      </div>
      <!-- Two boxes, because they have different lifetimes: the warning below
           is rebuilt on every keystroke by the preview, and would wipe a lease
           notice the moment you typed. Not a live region: notifications are
           announced through the page's cf-sr region, and a second one here
           would say the same sentence twice. -->
      <div id="wlqLease"></div>
      <div id="wlqWarn" aria-live="polite"></div>
      </div>

      <div class="modal-footer py-2">
        <button class="btn btn-sm btn-outline-secondary" type="button" id="wlqCancel">Cancel</button>
        <button class="btn btn-sm btn-primary" type="button" id="wlqSave">Save</button>
      </div>
    </form>
      </div>
    </div>
    <!-- Outside the draggable modal-dialog: that is what a drag transforms,
         and a transformed element becomes the containing block for its
         fixed-position descendants — which would leave the timeline placed
         against a moved panel rather than against the window. -->
    <div id="wlqTimeline"></div>
  </div>

  <dialog id="wlEditDlg" class="wl-dlg">
    <form method="dialog">
      <h2 class="h6 mb-2">Edit wallet</h2>
      <p class="small text-muted mb-2">The name is a shared field: if somebody else is editing it you
        will see who, and it will be read-only until they are done.</p>
      <div id="wlEditName" class="mb-2"></div>
      <div id="wlEditShort" class="mb-1"></div>
      <div class="small text-muted mb-2">The short name is what the log notches into a figure's
        border, where a long one would be cut off. Leave it empty and the full name is used.</div>
      <label class="cf-h d-block mb-1" for="wlEditColour">Colour</label>
      <input type="color" id="wlEditColour" class="form-control form-control-color form-control-sm mb-1"
        aria-describedby="wlEditColourHelp">
      <div class="small text-muted mb-2" id="wlEditColourHelp">What this wallet is drawn in where the
        accounts page splits a balance by wallet.</div>
      <label class="cf-h d-block mb-1" for="wlEditParent">Inside</label>
      <select id="wlEditParent" class="form-select form-select-sm"></select>
      <div class="small text-muted mt-1">Moving a wallet takes everything inside it along, and changes
        nothing about the money — a wallet's balance is the legs that point at it.</div>
      <div id="wlEditWarn" aria-live="polite"></div>
      <!-- Delete on the left, away from Done: it cannot be undone from here
           (only from Deleted records), and the server refuses it while the
           wallet still has money, wallets inside it, or a debt. -->
      <div class="d-flex justify-content-between gap-2 mt-3">
        <button class="btn btn-sm btn-outline-danger" type="button" id="wlEditDelete">Delete wallet</button>
        <button class="btn btn-sm btn-primary" type="button" id="wlEditDone">Done</button>
      </div>
    </form>
  </dialog>

  <dialog id="wlNewDlg" class="wl-dlg">
    <form method="dialog">
      <h2 class="h6 mb-2">New wallet</h2>
      <label class="cf-h d-block mb-1" for="wlNewName">Name</label>
      <input id="wlNewName" class="form-control form-control-sm mb-2" maxlength="60">
      <label class="cf-h d-block mb-1" for="wlNewShort">Short</label>
      <input id="wlNewShort" class="form-control form-control-sm" maxlength="12"
        placeholder="optional" aria-describedby="wlNewShortHelp">
      <div class="small text-muted mb-2" id="wlNewShortHelp">For the log, where a long name is cut
        off. Empty means use the full name.</div>
      <label class="cf-h d-block mb-1" for="wlNewColour">Colour</label>
      <input type="color" id="wlNewColour" class="form-control form-control-color form-control-sm mb-2">
      <label class="cf-h d-block mb-1" for="wlNewParent">Inside</label>
      <select id="wlNewParent" class="form-select form-select-sm"></select>
      <div class="alert alert-danger py-2 small mt-2 mb-0" id="wlNewError" hidden></div>
      <div class="d-flex justify-content-end gap-2 mt-3">
        <button class="btn btn-sm btn-outline-secondary" type="button" id="wlNewCancel">Cancel</button>
        <button class="btn btn-sm btn-primary" type="button" id="wlNewConfirm">Create</button>
      </div>
    </form>
  </dialog>`;
}

interface MoneyAccount { id: string; name: string; currencyCode: string; type: string; isPlaceholder: boolean }

/**
 * Whether a Move would move nothing at all.
 *
 * Same account and same wallet usually means exactly that, and the form says
 * so rather than writing a pair of legs that cancel.
 *
 * A conversion is the exception, and the one this did not know about: CHF
 * 100 becoming Ft.41,070 in one account and one wallet is an event worth
 * recording, and the ledger already holds several the importer wrote.
 * Refusing it meant such an entry could be opened on this form and never
 * saved again — including a save that only changed the note or the date.
 */
/**
 * The magnitude both legs balance on, in the transaction's own currency.
 *
 * `value` is denominated in the transaction's currency on BOTH legs, so it
 * can only be taken from a leg actually in that currency — that leg's amount
 * IS the value, and the other leg's amount is what landed. One side always
 * is: either the money leaving or the money arriving.
 *
 * `undefined` when neither is, because then the two amounts are a rate
 * between two currencies and say nothing about the third. There would be no
 * honest number to balance on, so the form refuses rather than inventing
 * one.
 *
 * Which side carries it is the whole of what made this form reject half the
 * conversions in the ledger: it assumed the leaving leg, and the importer
 * wrote plenty the other way round.
 */
/**
 * Which currency the "Arrives as" picker should be showing.
 *
 * `allowed` IS "what this destination can hold" — every currency for a
 * multi-currency account, its own one otherwise — so the whole rule is about
 * what survives a change of destination, in order of who asked for it:
 *
 *   `prefer`  a record being opened. It says what the entry IS.
 *   `chosen`  what the person last picked themselves.
 *   `was`     whatever was showing, which may have been forced by an account.
 *   `fallback` the destination's own currency.
 *
 * The distinction between `chosen` and `was` is the one worth having. A
 * single-currency destination forces its currency and locks the picker;
 * moving on from there to an account that can hold anything should not leave
 * that forced currency behind as though it had been asked for. What the
 * person last actually picked is what they still mean, and it returns as
 * soon as a destination can take it.
 *
 * This reset to the destination's own currency on every change for a while,
 * which lost the forints the moment somebody moved a conversion from a
 * specialised account to a general one — the ordinary edit now that wallets
 * carry the distinction accounts used to.
 */
export function chooseTargetCurrency(opts: {
  allowed: readonly string[];
  prefer?: string | undefined;
  chosen?: string | undefined;
  was: string;
  fallback: string;
}): string {
  const { allowed } = opts;
  for (const candidate of [opts.prefer, opts.chosen, opts.was, opts.fallback]) {
    if (candidate !== undefined && candidate !== '' && allowed.includes(candidate)) return candidate;
  }
  return allowed[0] ?? '';
}

export function balancingAmount(
  leaving: { ccy: string; amount: number },
  arriving: { ccy: string; amount: number },
  txCcy: string,
): number | undefined {
  if (leaving.ccy === txCcy) return leaving.amount;
  if (arriving.ccy === txCcy) return arriving.amount;
  return undefined;
}

export function movesNothing(
  sameAccount: boolean, sameWallet: boolean, converting: boolean,
): boolean {
  return sameAccount && sameWallet && !converting;
}

export async function mountWebledgerWallets(
  root: HTMLElement, initial: Section = 'wallets',
): Promise<void> {
  root.innerHTML = webledgerShellHtml(initial, shellBody() + dialogHtml() + budgetDialogHtml())
    + transactionEditorModalHtml()
    // The editor's history slide-over lives here. Not optional: its lease
    // subscriber reads this element, so a page that hosts the editor without
    // it dies on the SECOND open — when claiming one lease releases the
    // previous one and wakes the subscriber.
    + '<div id="wlTimeline"></div>'
    + '<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>';
  wireBackButton(root);
  wireSplitHelp(root);
  wireHelpBubble(root, 'wlqHelpBtn', 'wlqHelpPanel');
  wireHelpBubble(root, 'wlLogHelpBtn', 'wlLogHelpPanel');
  wireHelpBubble(root, 'wlBudHelpBtn', 'wlBudHelpPanel');

  const bootstrapped = await bootstrapWebledgerClient(root);
  if (!bootstrapped) return;
  // Bound once, so the closures below see a client that is definitely there
  // rather than re-proving it on every use.
  const client: WebledgerClient = bootstrapped;

  let data: WalletsResponse = { wallets: [], unassigned: {}, cash: {} };
  let debts: Debt[] = [];
  /*
   * Which debts are folded open, so a re-render does not close them.
   *
   * Opening a transaction from one of these lines and closing it again runs
   * `loadAll`, which rebuilds this table from scratch — so the panel the
   * reader opened to find that entry was shut by the act of following it.
   *
   * Keyed by the pair and currency rather than by position: the table sorts
   * by amount, and the amount is exactly what changes when somebody edits
   * one of these entries. An index would reopen whichever debt had taken
   * that row.
   */
  const openDebts = new Set<string>();
  const debtKey = (d: Debt): string => `${d.debtorId}|${d.creditorId}|${d.currencyCode}`;
  /** Money accounts only — where a wallet transfer can be parked. */
  let moneyAccounts: MoneyAccount[] = [];
  /** Every account, which is what the transaction editor needs. */
  let allAccounts: AccountDto[] = [];
  let currencies: CurrencyDto[] = [];
  /** Rates for the totals bar's conversion, and nothing else on this page. */
  let fx: FxRates | undefined;
  /** Every tag in the book, and the ones the log is filtered to. */
  let tags: TagDto[] = [];
  /** What the tag controls last drew — see `loadAll`, which only redraws when this moves. */
  let lastTagControlsKey = '';
  const selectedTags = new Set<string>();
  /**
   * The account the log is narrowed to, or '' for any.
   *
   * Held here rather than read off a control, because the control is a
   * button showing a name and the filter is an id — and the two part company
   * the moment an account is renamed or the chosen one is not in this log.
   */
  let logAccountId = '';
  /**
   * The tags on the entry the short form is holding, and the link rows they
   * came from.
   *
   * Two collections because a tag on a transaction is its own record: putting
   * one on is creating a row, taking it off is deleting the row that is
   * already there, and only the second needs the id. An entry being created
   * has no links yet, so the map is simply empty and every chip turned on is
   * a create.
   */
  const quickTagSelection = emptyTagSelection();

  const el = <T extends HTMLElement>(id: string): T => root.querySelector<T>(`#${id}`)!;

  /**
   * Announced *and* on screen, for the things that happen on the page itself
   * — a drag that would not save, a row that would not open.
   *
   * `client.notify` alone writes to the `.cf-sr` region, which is one pixel
   * square and clipped to nothing. A refusal that goes only there is a button
   * that appears to do nothing, which is how three separate bugs on this page
   * were reported before anyone worked out they were the same one.
   */
  const fail = pageFailure(root, client.notify);

  /**
   * The same, for the two modal dialogs.
   *
   * `showModal()` puts the dialog in the top layer with a backdrop over
   * everything else, so the page notice is both covered and greyed out. A
   * dialog that refuses has to say so inside itself.
   */
  const refuseInDialog = (dlg: 'wlTransfer' | 'wlNew', message: string): void => {
    client.notify(message);
    showNoticeAt(el(`${dlg}Error`), message);
  };
  const clearDialogNotice = (dlg: 'wlTransfer' | 'wlNew'): void => { clearNoticeAt(el(`${dlg}Error`)); };

  const records = new RecordStateTracker(client, () => { paintRowStates(); });

  /*
   * Declared here rather than beside its first use, deliberately.
   *
   * `loadAll` and `renderTabBadges` both read this, and both are called during
   * setup — a `const` further down the file would be in its temporal dead zone
   * when they run, which this file has already been bitten by twice (see the
   * note on `attachments`). Keep it above everything that touches it.
   */
  const budgetPanel: MountedBudgets = mountBudgets({
    root,
    token: client.token,
    notify: (message) => client.notify(message),
    tags: () => tags,
    currencies: () => currencies,
    money,
    // Computed from the replica, like everything else on this page.
    budgets: () => budgetsView(datasetLedger(client.dataset)),
    walletOptions: () => orderWallets(data.wallets)
      .map(({ wallet }) => ({ id: wallet.id, label: walletPath(data.wallets, wallet.id) })),
    walletName: (id) => walletPath(data.wallets, id),
    openLog: (walletId, tagId) => { openLog(walletId ?? 'all', tagId); },
    onChange: () => { renderTabBadges(); },
    newId: () => crypto.randomUUID(),
    openRecord: (recordId) => client.session.openRecord('budget', recordId),
    closeRecord: (recordId) => { client.session.closeRecord('budget', recordId); },
    commitField: async (recordId, field, value) => {
      const outcome = await client.session.claim('budget', recordId, field);
      if (!outcome.ok) return false;
      await client.session.commitAndRelease('budget', recordId, field, value);
      return true;
    },
    setToggle: async (recordId, field, wanted) => {
      // `setToggle`, not `toggle`: a form says what the answer should be,
      // and may be saved before the field has ever been written.
      await client.session.openRecord('budget', recordId);
      try {
        await client.session.setToggle('budget', recordId, field, wanted);
        /*
         * And set it locally, for the reason the reorder does: this panel is
         * drawn from the replica, and the replica's feed holds its cursor a
         * minute behind the present, so a write made a second ago is offered
         * to nobody. Without this the budget stays on the page until the
         * delta catches up, which reads as the switch not working.
         */
        const row = datasetLedger(client.dataset).budgets().find((b) => b.id === recordId);
        if (row !== undefined) {
          await client.dataset.applyChanges({ budget: [{ ...row, [field]: wanted }] });
        }
        return true;
      } catch {
        return false;
      } finally {
        client.session.closeRecord('budget', recordId);
      }
    },
  });

  /** Which section is showing, and which wallet's log it was last pointed at. */
  let section: Section = initial;
  const rememberedLog = client.prefs.get(PREF.walletsLog) ?? '';
  /**
   * Where the reader last was — `markCurrentRow`'s question.
   *
   * Two ids, because a row here is a LEG and not a transaction. A transfer
   * between two money accounts has both its legs in the same register, one
   * per row, sharing a transaction id; keyed on that alone the marker always
   * landed on whichever leg came first, so clicking the second one lit up its
   * neighbour instead. 38 of these 254 transactions are that shape.
   *
   * The leg is therefore what is marked, and the transaction is the fallback
   * for when that exact leg is not on screen — a different wallet's log, or an
   * entry so new that the row it will occupy did not exist when it was saved.
   *
   * Both are kept across repaints on purpose: a save, a live update from
   * somebody else and a change of wallet all rebuild the log, and losing your
   * place to any of them is worse than never having had a marker.
   */
  let currentTxId: string | null = null;
  let currentSplitId: string | null = null;

  /**
   * Put the marker back after a repaint, on the leg if it is there.
   *
   * The fallback call is made only when the leg is missing: `markCurrentRow`
   * clears the previous marker before setting its own, so calling it twice
   * unconditionally would undo the precise answer and replace it with the
   * approximate one.
   */
  /** Mark the current row; whether it is in the list at all. */
  function applyMarker(opts: { flash: boolean; scroll: boolean }): boolean {
    const reg = el('wlRegister');
    const mark = { flash: opts.flash, ...(opts.scroll ? { scroll: 'if-needed' as const } : {}) };
    if (currentSplitId && markCurrentRow(reg, currentSplitId, { attr: 'data-split', ...mark })) return true;
    return markCurrentRow(reg, currentTxId, { attr: 'data-tx', ...mark }) !== null;
  }
  /** Set by a save: follow the saved entry (`renderRegister`). */
  let flashNext = false;
  /**
   * Until when the log keeps the saved entry marked and in view, and whether
   * its sweep has played yet.
   *
   * Asked from use: a new entry should be selected and scrolled into view.
   * A save is drawn more than once — when the delta lands and when the save
   * itself finishes — and only the first draw used to point at it; the next
   * restored the remembered scroll position over it, which an instant jump
   * had never updated. So for a few seconds every draw points at it, the
   * sweep plays once (when the row is first there), and nothing restores an
   * older position on top. Any scrolling of your own ends it at once.
   */
  let followUntil = 0;
  let sweepPending = false;
  /** How long to keep it in view once it is there, and how long to wait for it to arrive at all. */
  const FOLLOW_MS = 4000;
  const ARRIVE_MS = 15_000;

  /**
   * Where the reader was in this wallet's log.
   *
   * Guarded on the section, because this page scrolls one window through
   * four of them: a position recorded while the wallet list or the budgets
   * are showing is not a position in the log.
   */
  // The log scrolls inside its own pane, not the window — twenty-five
  // thousand pixels of rows under a header that stays put.
  const logScroller = elementScroller(el('wlRegScroll'));
  // Scrolling of your own ends the following at once.
  for (const kind of ['wheel', 'touchstart', 'keydown'] as const) {
    el('wlRegScroll').addEventListener(kind, () => { followUntil = 0; }, { passive: true });
  }
  const logScroll = trackScroll(
    client.prefs, PREF.scrollWalletLog('all'), () => section === 'log', logScroller,
  );

  /**
   * Show one section, hide the others.
   *
   * `render` is false only for the very first call, which is applying a
   * remembered choice before the first load: the load fetches the log itself
   * and doing it here as well would be two requests for one page.
   */
  /*
   * The sections that scroll the window — the wallets, the budgets, the
   * debts — keep their Add entry bar at the top as the small header, and a
   * tap on it brings the whole header back (webledgerHeadReveal). The log has
   * its own scroller and folds instead.
   */
  const headReveal: HeadReveal = wireHeadReveal(el('wlSecBar'), { onPage: true });
  function showSection(next: Section, opts: { render?: boolean; push?: boolean } = {}): void {
    // Only when it actually moves. Clicking a second wallet while already in
    // the log would otherwise stack another /wallets/log entry, and Back
    // would appear to do nothing until you had pressed it once per click.
    const moved = section !== next;
    section = next;
    for (const pane of root.querySelectorAll<HTMLElement>('[data-pane]')) {
      pane.hidden = pane.dataset['pane'] !== next;
    }
    // The log carries its own Add entry, in the row with the filters it
    // belongs beside; the loose one above would be the same button twice.
    el('wlSecBar').hidden = next === 'log';
    // The log folds its header its own way; anything brought back over the
    // section being left goes with it.
    headReveal.reset();
    // And at the top of the window. The nav can now be brought back from
    // halfway down a section, and the next one would otherwise open at that
    // depth — the log with its own header scrolled out of sight.
    if (moved) window.scrollTo({ top: 0, behavior: 'instant' });
    // Open, and the list back at the top: arriving at a section should not
    // land you halfway down what you were last reading.
    if (next !== 'log') setFold(0, false);
    else requestAnimationFrame(() => { setFold(foldAt, false); });
    // The nav is the switcher, so it is what has to show where you are.
    for (const link of root.querySelectorAll<HTMLAnchorElement>('[data-nav] .nav-link')) {
      const item = (link.closest('[data-nav]') as HTMLElement).dataset['nav'];
      link.classList.toggle('active', item === next);
    }
    if (opts.push && moved) history.pushState({ section: next }, '', appHref(SECTION_HREF[next]));
    // The bar counts one wallet on the log and the whole book everywhere
    // else, so leaving a section changes what it is about — see `cashInScope`.
    // Without this, walking from a wallet's log to the wallets page left
    // "In Renovation" sitting above every wallet in the ledger.
    paintCashTotals();
    // Fetched on arrival, not merely unhidden: somebody else may have posted
    // into this wallet since the page loaded.
    if (next === 'log' && opts.render !== false) void renderRegister();
  }

  /**
   * Point the log at one wallet and go there — what clicking a wallet does.
   *
   * `onlyTag` is how a budget shows its working: it REPLACES whatever tag
   * filter was set rather than adding to it, because the reader asked for one
   * project's transactions and silently intersecting that with a filter they
   * set half an hour ago would show them a shorter list than the number they
   * clicked on.
   */
  function openLog(walletId: string, onlyTag?: string): void {
    const select = el<HTMLSelectElement>('wlLogSelect');
    if ([...select.options].some((o) => o.value === walletId)) select.value = walletId;
    client.prefs.set(PREF.walletsLog, walletId);
    if (onlyTag) {
      selectedTags.clear();
      selectedTags.add(onlyTag);
      client.prefs.set(PREF.walletsTags, onlyTag);
      renderTagFilter();
    }
    // `push`, like any other way of arriving at the log. Without it the
    // section changed under a URL that still said /wallets, so Back left the
    // page entirely — to Accounts, or wherever the reader came from — rather
    // than returning to the wallet list they had just clicked out of.
    showSection('log', { push: true });
    // Focus follows the eye. Without this a keyboard user activates a wallet
    // and stays on a list that is no longer on the screen.
    el('wlPane-log').focus();
  }

  /**
   * The two things `loadAll` cannot work out for itself, plus the choices
   * this reader made last time.
   *
   * Rates are genuinely not a row of this ledger — nothing on this device can
   * derive one — so they stay on the cached read the reports use, with
   * whatever staleness the server last reported, which `totalsHtml` shows
   * rather than hides.
   *
   * The currency table and the tag vocabulary used to be fetched here too.
   * They are replicated rows, so `loadAll` now reads them along with
   * everything else and they follow a sync like the rest of the page; what
   * is left here is only what must happen *before* the first paint — the
   * stored tag selection has to be reconciled against a tag list, and the
   * total-mode select is filled once because its value is the reader's own.
   */
  async function primeCurrencies(): Promise<void> {
    const fxRead = await readCached<FxRates>(client.docCache, 'fx', '/apps/webledger/fx', client.token);
    if (fxRead.ok) fx = fxRead.data;

    /*
     * Whatever was chosen last time, restored as stored.
     *
     * Deliberately NOT reconciled against the tag list here. That check used
     * to live on this line and read the vocabulary off the replica — which on
     * a device whose first sync has not landed yet is empty, so every stored
     * tag looked deleted and the filter was silently forgotten. Dropping
     * somebody's filter is not a safe default: it is a different ledger than
     * the one they left open, presented as if nothing happened.
     *
     * The reconciliation belongs where the tag list is actually known, which
     * is `loadAll`.
     */
    for (const id of parseIdList(client.prefs.get(PREF.walletsTags))) selectedTags.add(id);
    if (client.prefs.get(PREF.walletsTagMode) === 'all') tagModeValue = 'all';
    fillTotalModes(el<HTMLSelectElement>('wlTotalMode'), fx,
      client.prefs.get(PREF.walletsTotalMode) ?? 'by');
  }

  /**
   * Everything on this page, computed here (ADR-0025).
   *
   * No request produces these numbers any more: the balances, the rollups,
   * the shortfalls and the debts are all `walletsView` and `debtsView` over
   * the local replica, which is the same code `services/api` runs. That is
   * what makes the page work offline — not a fallback, but the only path
   * there is.
   *
   * The account list is a different kind of thing and stays on its own cached
   * read: it is a reference list the picker and the editor need in their own
   * shape (`balances`, `purposeIds`), and it has been offline-capable through
   * `readCached` all along. Only this page's copy of it was not, because it
   * used a bare `apiFetch` that rejects rather than falls back — which is why
   * the whole page rendered blank offline.
   */
  /**
   * The wallet list's hover, once it exists: `loadAll` runs on every sync and
   * the hover is wired further down, so it is reached through this rather
   * than by name.
   */
  const listHover: { refresh?: () => void } = {};

  /**
   * Redraw after something this page wrote — once the replica has it.
   *
   * `loadAll` reads the replica, and the replica hears about a write only
   * when the socket announces it. Redrawing straight after a save therefore
   * drew the entry as it was, and waited for the announcement to put it
   * right; in a window left open for long, whose socket keeps dropping, that
   * could be a while. Reported from use: a changed note that did not appear
   * in the log. So the page asks for the delta itself (joining one already
   * in flight) and draws after it. A failed delta still draws — offline, the
   * replica is what there is.
   */
  async function afterWrite(): Promise<void> {
    await client.replicaSync.sync().catch(() => undefined);
    await loadAll();
  }

  async function loadAll(): Promise<void> {
    const ledger = datasetLedger(client.dataset);
    data = walletsView(ledger);
    debts = debtsView(ledger);

    /*
     * The accounts come off the replica too, like everything else here.
     *
     * They were the last request left in this function, and `loadAll` runs on
     * every sync — so an ordinary afternoon of somebody else's saves meant a
     * round trip per save for a list that had not changed. Worse, it was the
     * one thing on the page that could not follow an account being *renamed*:
     * `readCached` is network-first, so it did re-read, but only when
     * something else had already triggered a load, and a rename alone never
     * triggered one.
     */
    allAccounts = accountsView(ledger);
    moneyAccounts = (allAccounts as unknown as MoneyAccount[])
      .filter((a) => (a.type === 'ASSET' || a.type === 'LIABILITY') && !a.isPlaceholder);

    /*
     * The vocabulary follows too — currencies and tags, both replicated.
     *
     * Read once at mount before this, which was invisible until the rest of
     * the page went live: a tag created on the Settings page in another
     * window would not appear in this filter, and a currency's symbol would
     * keep formatting as a bare code, for as long as the page stayed open.
     *
     * `fillTotalModes` deliberately does NOT run again. It fills a select
     * whose value is the reader's own choice, and refilling it on every sync
     * would either reset that choice or repaint a control somebody may have
     * open — invariant #5, and the reason this is two statements rather than
     * a call to the same function the mount uses.
     */
    currencies = [...ledger.currencies()]
      .map((c) => ({
        id: c.id, code: c.code, name: c.name, symbol: c.symbol ?? '',
        fraction: c.fraction ?? 2, useForTotals: Boolean(c.useForTotals),
      }))
      .sort((a, b) => a.code.localeCompare(b.code));
    for (const c of currencies) moneyByCode.set(c.code, c);

    // Dropping any that have since been deleted rather than filtering by a
    // ghost — the same care `primeCurrencies` takes when restoring them.
    tags = [...ledger.tags()]
      .map((t) => ({ id: t.id, name: t.name, sortOrder: t.sortOrder ?? 0 }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    /*
     * Now that the tag list is actually known, drop a chosen tag that has
     * since been deleted rather than filtering by a ghost.
     *
     * Guarded on the replica having synced at least once, because "no tags"
     * and "no tags YET" are the same empty list and only one of them means
     * the chosen tag is gone. This is the check `primeCurrencies` used to
     * make too early.
     */
    const keep = new Set(keepKnownTags([...selectedTags], tags, !client.dataset.isEmpty()));
    for (const id of [...selectedTags]) if (!keep.has(id)) selectedTags.delete(id);

    /*
     * The tag CONTROLS are redrawn only when what they draw has moved.
     *
     * `loadAll` runs on every sync, and most syncs are somebody saving a
     * transaction — which changes no tag. Redrawing anyway would rebuild the
     * filter popover and the quick form's tag select several times a minute,
     * closing whichever one the reader had open (invariant #5).
     *
     * The key is the vocabulary AND the selection AND the mode, because that
     * is everything these controls render from. It was the vocabulary alone,
     * which is how the filter came to be applied and invisible: a sync
     * landing during mount painted the chips while the stored selection was
     * still being restored, and every later pass saw an unchanged vocabulary
     * and skipped — leaving the count, the chosen chips and the Clear button
     * showing a filter of none while the register showed one of some.
     */
    const controlsKey = tagControlsKey(tags, selectedTags, tagModeValue);
    if (controlsKey !== lastTagControlsKey) {
      lastTagControlsKey = controlsKey;
      renderTagFilter();
      renderTagPanel();
      renderQuickTags();
    }
    budgetPanel.load();
    paintCashTotals();
    renderAttention();
    renderList();
    /*
     * The tooltip answers again for the new figures. Pinned, it is a window
     * onto one chart on one day that stays up while somebody works in another
     * window — and a save made there arrives HERE, by sync. It used to keep
     * showing the old figures until a step through dates happened to ask.
     */
    listHover.refresh?.();
    renderDebts();
    budgetPanel.render();
    renderTabBadges();
    // The options cost nothing and `openLog` needs them; the rows behind them
    // are a request, so they wait until the log is the section being read.
    fillLogSelect();
    if (section === 'log') await renderRegister();
  }

  /*
   * Offline edits the server held because the entry changed meanwhile —
   * shown here, where entries are made, and checked again whenever queued
   * work has been sent and whenever the connection comes back.
   */
  const reviewBanner = mountReviewBanner(el('wlReviewBanner'), {
    token: client.token,
    online: () => !knownOffline(),
    notify: client.notify,
    current: (id) => client.dataset.row<{ id: string; txDate: string; description?: string | null;
      reference?: string | null; currencyCode: string; }>('transaction', id),
    onResolved: () => afterWrite(),
  });
  void reviewBanner.refresh();
  client.onOutboxFlushed(() => { void reviewBanner.refresh(); });
  // Saved here, refused by the server: the note, and the way back into it.
  mountRefusedBanner(el('wlRefusedBanner'), {
    entries: client.refusedEntries,
    onChange: client.onPendingWorkChange,
    discard: client.discardQueued,
    open: (id) => { openTransactionById(id, null); },
  });
  client.transport.onStateChange((state) => { if (state === 'open') void reviewBanner.refresh(); });

  /*
   * Repaint whenever the replica moves.
   *
   * A sync landing is the only thing that changes these numbers now, and it
   * arrives from a debounced delta rather than from anything this page did —
   * somebody else's save, or coming back online. Without this the page would
   * be correct on arrival and stale for as long as it stayed open.
   */
  /*
   * `revision` rather than the bare notification: a delta usually carries no
   * rows and notifies anyway, because its cursor still moved. This page
   * recomputes every wallet, every rollup, the debts, the budgets and the
   * register on each pass, so repeating it several times a minute for no
   * change is work nobody asked for — and each pass rebuilds the table the
   * reader is pointing at.
   */
  let drawnAt = -1;
  client.dataset.subscribe(() => {
    if (client.dataset.revision() === drawnAt) return;
    drawnAt = client.dataset.revision();
    void loadAll();
  });

  /** The tag chips, and which of them are on. */
  /** The button, the count, and the answer beside it. */
  function renderTagFilter(): void {
    // Hidden entirely when the book has no tags: a filter with nothing to
    // filter by is furniture, and this page is already busy.
    el('wlLogTags').hidden = tags.length === 0;

    const chosen = tags.filter((t) => selectedTags.has(t.id));
    const count = el('wlLogTagCount');
    count.hidden = chosen.length === 0;
    count.textContent = String(chosen.length);
    el('wlLogTagBtn').setAttribute('aria-label',
      chosen.length === 0 ? 'Filter by tag'
        : `Filter by tag — ${chosen.length} chosen: ${chosen.map((t) => t.name).join(', ')}`);

    /*
     * The chosen tags stay on the page, outside the popover.
     *
     * A filter you cannot see is a filter you forget you set, and "why is
     * this list missing things" is the question it produces an hour later.
     * Named up to three, then counted — the point is to notice it is on, not
     * to read a list.
     */
    const summary = el('wlLogTagSummary');
    summary.textContent = chosen.length === 0
      ? 'Any tag'
      : tagFilterSummary(chosen.map((t) => t.name), tagMode());
    // Muted while it says "Any tag", like an account field with nothing
    // chosen — the control reads as off rather than as holding a value.
    summary.classList.toggle('wl-acct-btn__name--empty', chosen.length === 0);
    el('wlLogTagClear').hidden = chosen.length === 0;

    /*
     * The open panel's LIST is deliberately left alone.
     *
     * Rebuilding it on every tick destroys the checkbox under the pointer
     * and takes the keyboard focus with it, so ticking three tags in a row
     * means finding your place again twice. The box already shows its own
     * state; only the any/all row depends on how many are on.
     */
    paintTagModeRow();
  }

  /** The any/all choice only means something once two tags are chosen. */
  function paintTagModeRow(): void {
    const row = tagPanel?.querySelector<HTMLElement>('[data-tagf-mode]');
    if (row) row.hidden = selectedTags.size < 2;
  }

  /** The open tag popover, if it is open. */
  let tagPanel: HTMLElement | undefined;

  function closeTagPanel(): void {
    tagPanel?.remove();
    tagPanel = undefined;
    el('wlLogTagBtn').setAttribute('aria-expanded', 'false');
  }

  function renderTagPanel(): void {
    if (!tagPanel) return;
    const query = tagPanel.querySelector<HTMLInputElement>('[data-tagf-search]')?.value.trim().toLowerCase() ?? '';
    const shown = query === '' ? tags : tags.filter((t) => t.name.toLowerCase().includes(query));
    const list = tagPanel.querySelector<HTMLElement>('[data-tagf-list]')!;
    list.innerHTML = shown.length === 0
      ? '<p class="text-secondary small m-2">No tag matches that.</p>'
      : shown.map((t) => {
        const on = selectedTags.has(t.id);
        return `<label class="wl-tagf__row"><input type="checkbox" data-tagf-pick="${escapeHtml(t.id)}"`
          + `${on ? ' checked' : ''}><span>${escapeHtml(t.name)}</span></label>`;
      }).join('');
    paintTagModeRow();
  }

  function openTagPanel(): void {
    if (tagPanel) { closeTagPanel(); return; }
    const anchor = el('wlLogTagBtn');
    tagPanel = document.createElement('div');
    tagPanel.className = 'wl-tagf';
    tagPanel.setAttribute('role', 'dialog');
    tagPanel.setAttribute('aria-label', 'Filter by tag');
    tagPanel.innerHTML = `
      <div class="wl-tagf__head">
        <input type="search" class="wl-tagf__search" data-tagf-search placeholder="Filter tags…"
          aria-label="Search the tags" autocomplete="off">
      </div>
      <div class="wl-tagf__list" data-tagf-list></div>
      <div class="wl-tagf__foot" data-tagf-mode hidden>
        <label class="small text-secondary mb-0" for="wlLogTagMode">Match</label>
        <select class="form-select form-select-sm w-auto" id="wlLogTagMode">
          <option value="any">any of them</option>
          <option value="all">all of them</option>
        </select>
      </div>`;
    popoverHost(anchor).appendChild(tagPanel);
    // The mode select is rebuilt with the panel, so the remembered choice has
    // to be put back rather than assumed to have survived.
    tagPanel.querySelector<HTMLSelectElement>('#wlLogTagMode')!.value = tagModeValue;
    renderTagPanel();
    placePopover(anchor, tagPanel);
    anchor.setAttribute('aria-expanded', 'true');
    tagPanel.querySelector<HTMLInputElement>('[data-tagf-search]')?.focus();
  }

  /**
   * The short form's tags — the full editor's control, not a second one.
   *
   * This was a row of toggle chips copied from the tag FILTER above it, which
   * looked natural and was the wrong sibling to match: toggling a vocabulary
   * is what a filter does, and assigning is a different question. One control
   * for one idea (webledgerTags.ts).
   */
  function renderQuickTags(): void {
    // Hidden entirely while a past version is on screen. Tag links are their
    // own records and are not part of a transaction's snapshot, so what is
    // loaded here is the CURRENT set — showing it beside March's amounts
    // would read as "these were the tags then", which it is not.
    //
    el('wlqTagsWrap').hidden = quickReadOnly || tags.length === 0;
    renderTagSelection(el('wlqTagChips'), el<HTMLSelectElement>('wlqTagPick'), tags, quickTagSelection);
  }

  /**
   * Held here rather than read off the select, because the select now lives
   * inside a popover that only exists while it is open — and the filter has
   * to keep meaning something after it closes.
   */
  let tagModeValue: 'any' | 'all' = 'any';

  function tagMode(): 'any' | 'all' {
    return tagModeValue;
  }

  /** The currency the log is narrowed to, or '' for any. */
  function logCurrency(): string {
    return el<HTMLSelectElement>('wlLogCcy').value;
  }

  /**
   * Fill the currency picker from what this log actually holds.
   *
   * The currencies of these rows, not the book's: offering GBP on a log that
   * has never seen a pound is offering a filter that can only empty the
   * page. Hidden entirely below two, because a choice between "any" and the
   * single currency everything is already in is not a choice.
   *
   * A selection that is no longer present — switch wallet while filtered to
   * a currency the new one does not hold — falls back to any rather than
   * silently showing nothing.
   */
  /**
   * Put the chosen account on the button, and drop it only when the account
   * itself is gone.
   *
   * This used to drop it whenever the log had no row on it, on the same rule
   * as the currency picker's: a filter must not leave a log showing nothing
   * with no visible cause. What that did in practice was make the pick
   * itself vanish — choose `Liabilities`, which nothing in the log touches,
   * and the button went straight back to "Any" as though the click had
   * missed. The cause is visible now without that: the button names the
   * account, the cross beside it clears it, and an emptied list says which
   * account emptied it.
   *
   * The remembered one is honoured only on the first paint, when the button
   * still says "Any" because nothing has drawn yet.
   */
  function paintAccountFilter(): void {
    const btn = el<HTMLButtonElement>('wlLogAccBtn');
    if (logAccountId === '' && !btn.dataset['painted']) {
      logAccountId = client.prefs.get(PREF.walletsLogAccount) ?? '';
    }
    btn.dataset['painted'] = '1';
    if (logAccountId !== '' && !allAccounts.some((a) => a.id === logAccountId)) logAccountId = '';
    /*
     * Through the hidden select, because `paintAccountButton` reads the
     * value from there — one painter for every account control on the page,
     * so the icon, the muted parent segment and the ellipsis rule cannot
     * drift between this field and the forms.
     */
    const select = el<HTMLSelectElement>('wlLogAcc');
    const chosen = allAccounts.find((a) => a.id === logAccountId);
    select.innerHTML = chosen
      ? `<option value="${escapeHtml(chosen.id)}" selected></option>`
      : '<option value="" selected></option>';
    paintAccountButton('wlLogAcc');
    // "Choose account…" is what an empty one says in a form, where picking
    // is the next thing to do. Here an empty filter is the ordinary state.
    if (!chosen) {
      const name = btn.querySelector<HTMLElement>('[data-acct-name]')!;
      name.textContent = 'Any account';
      btn.setAttribute('aria-label', 'Account: any');
      btn.title = 'Every account';
    }
    el('wlLogAccClear').hidden = logAccountId === '';
  }

  function paintCurrencyFilter(rows: readonly RegisterRow[]): void {
    const select = el<HTMLSelectElement>('wlLogCcy');
    // In the Settings page's order, like every other list of currencies.
    const present = sortCurrencies(new Set(rows.map((r) => r.currencyCode).filter(Boolean)));
    const was = select.value;
    el('wlLogCcyWrap').hidden = present.length < 2;
    // On the first paint there is nothing in the control yet, so the
    // remembered choice is what to aim for.
    const aim = was === '' && select.options.length === 0
      ? (client.prefs.get(PREF.walletsLogCurrency) ?? '') : was;
    const wanted = present.includes(aim) ? aim : '';
    el('wlLogCcyClear').hidden = wanted === '';
    const options = ['<option value="">Any</option>',
      ...present.map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`)];
    const next = options.join('');
    if (select.innerHTML !== next) select.innerHTML = next;
    select.value = wanted;
  }

  /**
   * The cash on hand, by currency or converted into one.
   *
   * The prose that used to sit under this — why the wallets cannot quietly
   * stop adding up — is in ADR-0023, which is where an explanation of a
   * design belongs; on the page it was a paragraph nobody reads twice sitting
   * above the thing they came for. The claim is still made by the numbers.
   *
   * Conversion is display only, and it is the only place on this page a rate
   * is applied: wallets, registers, shortfalls and debts are all per currency
   * and stay that way (ADR-0023). `totalsHtml` is shared with the reports so
   * that a total which had to leave a currency out says so here too.
   */
  /**
   * Whose money the totals bar is counting, and what to call it.
   *
   * The bar has always shown the book: every wallet added up, which is the
   * identity the wallets page exists to make visible. On the log with one
   * wallet chosen that is the wrong question — the page is showing you one
   * wallet's entries and the only total in sight belonged to all of them, so
   * "how much does this wallet have" had no answer anywhere on the screen.
   *
   * A named wallet counts everything nested inside it, because that is what
   * `buildRegister` puts in the rows below: the total and the list have to be
   * about the same thing or the bar is simply a different number near some
   * entries.
   *
   * Only on the log, and only for a real choice. The other three sections are
   * about the book, and so is "All wallets together".
   */
  function cashInScope(): { totals: Record<string, string>; label: string } {
    const whole = { totals: data.cash, label: 'Wallets add up to the money on hand' };
    if (section !== 'log' && inspectDay) {
      // The book that evening: the top-level wallets and the money in none,
      // which between them are every money leg.
      const totals: Record<string, string> = {};
      const tops = [...data.wallets.filter((w) => !w.parentId).map((w) => w.id), UNASSIGNED];
      for (const id of tops) {
        for (const [ccy, v] of Object.entries(balancesOn(walletAcct().get(acctKey(id)), inspectDay))) {
          totals[ccy] = String(Number(totals[ccy] ?? 0) + Number(v));
        }
      }
      return { totals, label: `Wallets added up to this on ${inspectDay}` };
    }
    if (section !== 'log') return whole;
    const id = el<HTMLSelectElement>('wlLogSelect').value || 'all';
    if (id === 'all') return whole;
    if (id === UNASSIGNED) return { totals: data.unassigned, label: 'Not earmarked — what is in no wallet' };
    const wallet = data.wallets.find((w) => w.id === id);
    if (!wallet) return whole;
    const nested = data.wallets.some((w) => w.parentId === wallet.id);
    const base = nested ? `In ${wallet.name}, nested wallets included` : `In ${wallet.name}`;
    // The label stays put when the switch flips: the switch beside it says
    // which figure this is, and a heading that grows and shrinks reflows the row.
    return { totals: settledBag(wallet.rollup, wallet.id, settled, data.wallets, debts), label: base };
  }

  function paintCashTotals(): void {
    // One setting behind both switches, so the two always agree.
    el('wlSettledTotalsWrap').hidden = !settledSwitchShown(section, el<HTMLSelectElement>('wlLogSelect').value || 'all');
    el<HTMLInputElement>('wlSettledTotals').checked = settled;
    el<HTMLInputElement>('wlSettledList').checked = settled;
    const mode = el<HTMLSelectElement>('wlTotalMode').value || 'by';
    const scope = cashInScope();
    el('wlTotalsLabel').textContent = scope.label;
    el('wlCashTotals').innerHTML = totalsHtml(scope.totals, mode, fx, (amount, code) => chip(amount, code));
  }

  function renderAttention(): void {
    const items = shortfalls(data.wallets);
    if (items.length === 0) { el('wlAttention').innerHTML = ''; return; }
    el('wlAttention').innerHTML = items.map((s) =>
      `<div class="wl-attn d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div><span class="badge text-bg-warning">Over</span>
          <strong>${escapeHtml(walletPath(data.wallets, s.walletId))}</strong> is over by
          <span class="wl-num">${formatAmount(s.amount, s.currencyCode)}</span> ${escapeHtml(s.currencyCode)}
          <div class="small">The money was spent — it came out of the shared cash. Say whose it was, now or later.</div>
        </div>
        <button class="btn btn-sm btn-warning" type="button"
          data-cover="${escapeHtml(s.walletId)}" data-ccy="${escapeHtml(s.currencyCode)}"
          data-amount="${s.amount}">Cover from…</button>
      </div>`).join('');
    el('wlAttention').querySelectorAll<HTMLButtonElement>('[data-cover]').forEach((b) => {
      b.addEventListener('click', () => openCover(
        b.dataset['cover']!, b.dataset['ccy']!, Number(b.dataset['amount']),
      ));
    });
  }

  /**
   * The wallet list, where every row is also the way into that wallet's log.
   *
   * A `<button>` rather than a div with a click handler: the keyboard, the
   * focus ring and the "this is operable" announcement all come with the
   * element, and three hand-rolled halves of that is how a list becomes
   * mouse-only without anybody noticing. The indent lives on a wrapper so the
   * button can still be full width at its own depth.
   */
  /** The scroll position every chart shares (the tooltip is `showTableTip`'s). */
  /**
   * Where the book stood after each entry, wallet by wallet and currency by
   * currency — set by `renderRegister` while the log shows all wallets
   * together, and read by the hover on a `∑∑` running total.
   */
  let bookAt: Map<string, BookPosition[]> | undefined;
  /** `-1` until the first render has something to measure — see `wireSparkScroll`. */
  const sparkScroll: SparkScroll = { position: -1 };

  /**
   * Scroll them together, and name a value on hover.
   *
   * The scrollbars are hidden on each chart and the position is held here
   * instead: fourteen scrollbars down the side of a wallet list is noise, and
   * they would disagree the moment one of them was dragged. Dragging any
   * chart moves all of them, which is the only behaviour that keeps a shared
   * axis honest. (No wheel — see `wireSparkScroll`.)
   */
  function wireSparklines(): void {
    // Drag, all charts as one — shared with the accounts page, see
    // `wireSparkScroll` for the whys.
    const scrolling = wireSparkScroll(el('wlList'), [...el('wlList').querySelectorAll<HTMLElement>('.wl-spark')], sparkScroll);
    listDragging = () => scrolling.isDragging();
  }

  /** Whether the charts drawn last are being dragged; replaced on each render. */
  let listDragging: () => boolean = () => false;

  // ------------------------------------------------- inspecting one date

  /**
   * The date the wallets are being looked at on, when somebody has clicked
   * a chart — the same mode as on the accounts page (`wireInspectGestures`):
   * a line through every chart, every balance and the totals bar as they
   * stood that evening, the arrow keys or a swipe stepping between the days
   * anything changed, and any click elsewhere returning to today.
   */
  let inspectDay: string | undefined;
  /**
   * Balances as they would stand if every debt between wallets were paid
   * back — the Between wallets list applied to them (`settledBalances`).
   * One setting behind both switches, the list's and the log's totals'.
   */
  let settled = client.prefs.get(PREF.walletsSettled) === '1';
  let walletAcctCache: { revision: number; h: Map<string, AccountHistory[]> } | undefined;
  /** Each wallet's money by currency and account, kept until the replica moves. */
  function walletAcct(): Map<string, AccountHistory[]> {
    if (walletAcctCache?.revision !== client.dataset.revision()) {
      walletAcctCache = { revision: client.dataset.revision(), h: walletAccountHistories(datasetLedger(client.dataset)) };
    }
    return walletAcctCache.h;
  }
  /** The breakdown's row for the switch's correction. An account id is a UUID, never this. */
  const SETTLED_ROW = 'settled-debts';
  /** The book's key for a wallet in `walletAccountHistories` — Unassigned is the empty key there. */
  const acctKey = (id: string): string => (id === UNASSIGNED ? NO_WALLET : id);
  /** The shared axis of what was drawn last, for picking a day off a click. */
  let listSpan: SparkSpan | undefined;

  function inspectWallets(day: string | undefined): void {
    if (day === inspectDay) return;
    inspectDay = day;
    // Leaving the day lets go of a pinned tooltip: it was a window onto it.
    if (!day) walletHover.unpin();
    renderList();
    paintCashTotals();
    walletHover.refresh();
  }

  function stepWallets(direction: 1 | -1): void {
    if (!inspectDay) return;
    const days = new Set<string>();
    for (const series of walletAcct().values()) for (const s of series) for (const p of s.points) days.add(p.date);
    const sorted = [...days].sort();
    const next = direction > 0 ? sorted.find((d) => d > inspectDay!) : [...sorted].reverse().find((d) => d < inspectDay!);
    if (next) inspectWallets(next);
  }

  /** Accounts as rows of a wallet's breakdown: by path, named, no colour. */
  function accountRows(): WalletKeys {
    const ledger = datasetLedger(client.dataset);
    const money = accountsView(ledger).filter((a) => a.type === 'ASSET' || a.type === 'LIABILITY');
    const path = (id: string): string => {
      const byId = new Map(money.map((a) => [a.id, a] as const));
      const all = new Map(accountsView(ledger).map((a) => [a.id, a] as const));
      const parts: string[] = [];
      for (let a = all.get(id), n = 0; a && n < 64; a = a.parentId ? all.get(a.parentId) : undefined, n += 1) parts.unshift(a.name);
      return byId.has(id) || all.has(id) ? parts.join(' ▸ ') : 'Unknown account';
    };
    const keys = money.map((a) => a.id).sort((x, y) => path(x).localeCompare(path(y)));
    return {
      keys: [...keys, SETTLED_ROW],
      nameOf: (id) => (id === SETTLED_ROW ? 'Debts between wallets, settled' : path(id)),
      colourOf: () => '',
      byCurrency: byCurrencyRank(currencyRank(ledger.currencies())),
    };
  }

  /**
   * Hovering a wallet's chart: where that wallet's money was — accounts down
   * the side, currencies across — on the day under the pointer, or on the
   * inspected day when there is one. Wired once; it remembers the pointer so
   * an arrow press can make it answer again without the mouse moving.
   */
  const walletHover = (() => {
    const update = (target: HTMLElement | null, clientX: number): string | undefined => {
      const box = target?.closest<HTMLElement>('.wl-spark');
      const walletId = box?.closest<HTMLElement>('[data-open-wallet]')?.dataset['openWallet'];
      if (!box || !walletId || !listSpan || listDragging()) { hideTableTip(); return undefined; }
      const rect = box.getBoundingClientRect();
      const day = inspectDay ?? dayAt(listSpan, clientX - rect.left + box.scrollLeft);
      const name = walletId === UNASSIGNED ? 'Unassigned' : walletPath(data.wallets, walletId);
      const positions = (walletAcct().get(acctKey(walletId)) ?? []).flatMap((s) => {
        const p = accountPointAt(s, day);
        return p ? [{ currencyCode: s.currencyCode, total: p.total, parts: p.parts }] : [];
      });
      // Settled money sits in no account yet, so it is a row of its own, and
      // the Total is the figure the chart and the chip show.
      for (const [code, v] of Object.entries(shiftsAsOf(walletShifts(walletId, settled, data.wallets, debts), day))) {
        if (toScaled(v) === 0n) continue;
        const at = positions.find((p) => p.currencyCode === code);
        if (at) {
          at.parts = { ...at.parts, [SETTLED_ROW]: v };
          at.total = fromScaled(toScaled(at.total) + toScaled(v));
        } else positions.push({ currencyCode: code, total: v, parts: { [SETTLED_ROW]: v } });
      }
      showTableTip(constellationHtml(`${escapeHtml(name)} · ${escapeHtml(day)}`, positions, accountRows(),
        (code) => moneyByCode.get(code)), rect, clientX);
      // Found again after a redraw by its wallet and currency, so a pinned
      // tooltip follows a step to the same chart.
      return `[data-open-wallet="${CSS.escape(walletId)}"] .wl-spark[data-spark="${CSS.escape(box.dataset['spark'] ?? '')}"]`;
    };
    const state = hoverState(el('wlList'), update);
    el('wlList').addEventListener('mousemove', (event) => state.onMove(event.target as HTMLElement, event.clientX, event.clientY));
    el('wlList').addEventListener('mouseleave', () => state.onLeave());
    return state;
  })();
  listHover.refresh = () => { walletHover.refresh(); };

  wireInspectGestures(el('wlList'), {
    active: () => Boolean(inspectDay),
    pick: (box, clientX) => {
      if (!listSpan) return;
      const rect = box.getBoundingClientRect();
      inspectWallets(dayAt(listSpan, clientX - rect.left + box.scrollLeft));
    },
    step: stepWallets,
    exit: () => inspectWallets(undefined),
    pin: () => { walletHover.togglePin(); },
    chartSelector: '.wl-spark',
    // The charts sit inside the buttons that open a wallet's log, and the
    // click that only means "stop inspecting" must not also open one.
    swallowLeavingClick: true,
  });

  function renderList(): void {
    /*
     * One pass over the ledger for every wallet's history, then one shared
     * span. The span has to come from all of them at once — a wallet that
     * only woke up in August must still show August where the others do, or
     * scrolling them together compares different fortnights.
     */
    const ledger = datasetLedger(client.dataset);
    const ids = [...orderWallets(data.wallets).map(({ wallet }) => wallet.id), UNASSIGNED];
    // With the switch on, each chart is the settled figure over time, so its
    // last point is the chip beside it.
    const histories = new Map(ids.map((id) => [id,
      settledHistory(walletHistory(ledger, id), walletShifts(id, settled, data.wallets, debts))]));
    const span = sparkSpan([...histories.values()]);
    listSpan = span;
    if (!span) inspectDay = undefined;
    // While a day is inspected, every balance is what the wallet held that
    // evening, and the line is drawn through every chart at it.
    const bagOf = (id: string, today: Record<string, string>): Record<string, string> => {
      const base = inspectDay ? balancesOn(walletAcct().get(acctKey(id)), inspectDay) : today;
      return settledBag(base, id, settled, data.wallets, debts, inspectDay);
    };
    el('wlList').classList.toggle('wl-inspecting', Boolean(inspectDay));
    el('wlInspect').hidden = !inspectDay;
    el('wlInspect').innerHTML = inspectDay
      ? `Balances as they stood on <strong>${escapeHtml(inspectDay)}</strong>. `
        + '<span class="text-secondary">← → (or swipe a chart) step to the previous or next change; '
        + 'click outside the charts to return to today.</span>'
      : '';

    const row = (id: string, indent: number, extra: string, inner: string, editable: boolean): string =>
      `<div class="wl-wal-row" style="margin-left:${indent}px"><button type="button" class="wl-wal${extra}"
        data-open-wallet="${escapeHtml(id)}">
        <span class="d-flex flex-wrap justify-content-between align-items-start gap-2">${inner}</span>
      </button>${editable
        ? `<button type="button" class="wl-wal__edit" data-edit-wallet="${escapeHtml(id)}"
            title="Rename or move" aria-label="Rename or move this wallet">&#9998;</button>`
        // Unassigned has nothing to rename — it is the ABSENCE of a wallet,
        // a null `walletId` — but without the button's width reserved its
        // card ran wider than every other row and its chips and charts sat
        // out of line with theirs.
        // The same glyph, hidden: an empty box is only its padding, which
        // left this row fourteen pixels wider than every other one.
        : '<span class="wl-wal__edit wl-wal__edit--placeholder" aria-hidden="true">&#9998;</span>'}</div>`;
    const rows = orderWallets(data.wallets).map(({ wallet, depth }) => {
      // "Over" as it stood on the inspected evening, or as it would stand
      // with the debts settled — always from the figures beside it.
      const bag = bagOf(wallet.id, wallet.rollup);
      const shortIn = inspectDay || settled ? sortCurrencies(Object.keys(bag).filter((c) => Number(bag[c]) < -0.005)) : wallet.shortIn;
      const short = shortIn.length > 0;
      return row(wallet.id, depth * 22, short ? ' wl-wal--over' : '',
        `<span><span class="wl-wal__name">${escapeHtml(wallet.name)}</span>
          ${short ? `<span class="badge text-bg-warning">Over in ${escapeHtml(shortIn.join(', '))}</span>` : ''}
        </span>
        <span class="wl-wal__bal">${balancesHtml(bag, histories.get(wallet.id) ?? [], span, inspectDay)}</span>`, true);
    }).join('');
    // Unassigned gets no edit button because it is not a record: it is the
    // absence of one, a NULL `walletId` (ADR-0023). Nothing to rename.
    const un = row(UNASSIGNED, 0, ' wl-wal--unassigned',
      `<span><span class="wl-wal__name">Unassigned</span>
        <span class="badge text-bg-secondary">not earmarked</span></span>
      <span class="wl-wal__bal">${balancesHtml(bagOf(UNASSIGNED, data.unassigned), histories.get(UNASSIGNED) ?? [], span, inspectDay)}</span>`, false);
    el('wlList').innerHTML = rows + un;
    if (span && inspectDay) scrollToDay(el('wlList').querySelector<HTMLElement>('.wl-spark'), span, inspectDay, sparkScroll);
    if (span) wireSparklines();
    el('wlList').querySelectorAll<HTMLButtonElement>('[data-edit-wallet]').forEach((b) => {
      b.addEventListener('click', () => { void openWalletEditor(b.dataset['editWallet']!); });
    });
    el('wlList').querySelectorAll<HTMLButtonElement>('[data-open-wallet]').forEach((b) => {
      b.addEventListener('click', (event) => {
        // A modified click belongs to the chip tally, which claims it in the
        // capture phase; leaving this one to fire as well would tally a
        // balance and navigate away from it in the same gesture.
        const mouse = event as MouseEvent;
        if (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey) return;
        openLog(b.dataset['openWallet']!);
      });
    });
  }

  // ---------------------------------------------------------- editing a wallet

  /** Whatever the open editor mounted, so closing it can take them down. */
  let editorFields: MountedField[] = [];
  let editingWalletId: string | undefined;

  /**
   * Rename a wallet, or move it somewhere else in the tree.
   *
   * The name goes through `mountCollabField` — the same control the chart of
   * accounts and the settings page use. That is not laziness: a wallet name is
   * `editMode: 'field'` in the manifest, so it already has a lease, a history
   * and a presence story, and a plain input posting to a REST endpoint would
   * have quietly opted out of all three while looking identical.
   *
   * The parent has no such control, so it does by hand what one would do:
   * claim the field, commit against the version we read, release. A select is
   * not a text box — there is no cursor to protect and no reason to hold the
   * lease while the menu is open — so it is claimed at the moment of choosing
   * and let go immediately.
   */
  async function openWalletEditor(walletId: string): Promise<void> {
    const wallet = data.wallets.find((w) => w.id === walletId);
    if (!wallet) return;
    editingWalletId = walletId;
    el('wlEditWarn').innerHTML = '';

    // Itself and everything under it are not offered: a wallet inside its own
    // subtree is a cycle. The manifest hook refuses one anyway, server-side —
    // this is so the choice is never presented, not so the rule is enforced.
    const forbidden = new Set([walletId, ...descendantIds(data.wallets, walletId)]);
    const parent = el<HTMLSelectElement>('wlEditParent');
    parent.innerHTML = '<option value="">— top level —</option>'
      + orderWallets(data.wallets)
        .filter(({ wallet: w }) => !forbidden.has(w.id))
        .map(({ wallet: w }) =>
          `<option value="${escapeHtml(w.id)}">${escapeHtml(walletPath(data.wallets, w.id))}</option>`)
        .join('');
    parent.value = wallet.parentId ?? '';

    await client.session.openRecord('wallet', walletId);
    editorFields.push(mountCollabField(el('wlEditName'), {
      session: client.session, entity: 'wallet', recordId: walletId,
      field: 'name', label: 'Name',
    }));
    editorFields.push(mountCollabField(el('wlEditShort'), {
      session: client.session, entity: 'wallet', recordId: walletId,
      field: 'shortName', label: 'Short',
    }));
    // The colour in force, chosen or not: a picker cannot be empty, and
    // showing black for "nobody chose" would be a colour nobody chose either.
    el<HTMLInputElement>('wlEditColour').value = wallet.color;
    el<HTMLDialogElement>('wlEditDlg').showModal();
  }

  /**
   * Recolour a wallet, the way `moveWallet` reparents one.
   *
   * A colour picker is not a text box: there is no cursor to protect and
   * nothing to hold the lease for while the native panel is open. So the
   * field is claimed at the moment a colour is settled on (`change`, which a
   * picker fires once on close — `input` fires on every step of the drag)
   * and released straight after the commit. Opening the picker and closing
   * it on the same colour fires nothing at all, so a visit writes nothing.
   */
  async function recolourWallet(walletId: string, colour: string): Promise<void> {
    const outcome = await client.session.claim('wallet', walletId, 'color');
    if (!outcome.ok) {
      el('wlEditWarn').innerHTML = '<div class="wl-attn mt-2 small">Somebody else is changing this '
        + 'wallet\'s colour right now. Try again in a moment.</div>';
      el<HTMLInputElement>('wlEditColour').value =
        data.wallets.find((w) => w.id === walletId)?.color ?? colour;
      return;
    }
    await client.session.commitAndRelease('wallet', walletId, 'color', { t: 'string', v: colour.toLowerCase() });
    await afterWrite();
  }

  async function moveWallet(walletId: string, parentId: string): Promise<void> {
    const outcome = await client.session.claim('wallet', walletId, 'parentId');
    if (!outcome.ok) {
      el('wlEditWarn').innerHTML = '<div class="wl-attn mt-2 small">Somebody else is moving this '
        + 'wallet right now. Try again in a moment.</div>';
      // Back to what it actually is, rather than leaving the select showing a
      // move that did not happen.
      el<HTMLSelectElement>('wlEditParent').value =
        data.wallets.find((w) => w.id === walletId)?.parentId ?? '';
      return;
    }
    // `{ t: 'null' }` rather than passing null: `commitAndRelease` reads a
    // null VALUE as "nothing to write" and would only release the lease, so
    // clearing the parent that way would look like it worked and change
    // nothing. This is the value that means "no parent".
    await client.session.commitAndRelease('wallet', walletId, 'parentId',
      parentId ? { t: 'uuid', v: parentId } : { t: 'null', v: null });
    await afterWrite();
  }

  /**
   * Delete the wallet being edited — a sub-wallet as much as a top-level one.
   *
   * There was no way to from the page: the server has refused the unsafe
   * cases all along (still used by an entry, wallets inside it, a debt that
   * has to keep its history), but nothing asked it. Its refusal is shown as
   * it comes, because it says what to do first.
   */
  async function deleteWallet(walletId: string): Promise<void> {
    const name = walletPath(data.wallets, walletId);
    const go = await askConfirm({
      title: `Delete \u201c${name}\u201d?`,
      message: 'Only an empty wallet can be deleted: no entry may still use it, no wallet may be '
        + 'inside it, and it must never have lent or borrowed. It can be brought back from Deleted records.',
      confirm: 'Delete it',
      cancel: 'Keep it',
    });
    if (!go) return;
    const res = await apiFetch(`/apps/webledger/wallets/${encodeURIComponent(walletId)}`, {
      method: 'DELETE', token: client.token,
    }).catch(() => undefined);
    if (!res?.ok) {
      el('wlEditWarn').innerHTML = `<div class="wl-attn mt-2 small">${escapeHtml(
        res ? await failureMessage(res, 'The wallet could not be deleted.') : 'The wallet could not be deleted \u2014 no connection.')}</div>`;
      return;
    }
    el<HTMLDialogElement>('wlEditDlg').close();
    client.notify(`Deleted \u201c${name}\u201d.`);
  }

  function closeWalletEditor(): void {
    for (const field of editorFields) field.destroy();
    editorFields = [];
    if (editingWalletId) client.session.closeRecord('wallet', editingWalletId);
    editingWalletId = undefined;
    el('wlEditName').innerHTML = '';
    el('wlEditShort').innerHTML = '';
    // The name is committed by the field itself, on blur, so a reload here is
    // what brings it into the list and the log's wallet column.
    void afterWrite();
  }

  /**
   * The counts on the section tabs.
   *
   * Shortfalls and debts are the two things on this page that ask something of
   * the reader, and both now live behind a tab. A count on the tab is what
   * keeps them from being out of sight and out of mind — with a word in it,
   * never a bare number in a colour (invariant #11).
   */
  function renderTabBadges(): void {
    const over = shortfalls(data.wallets).length;
    const badge = (id: Section, text: string, cls: string): void => {
      const node = root.querySelector<HTMLElement>(`[data-nav-badge="${id}"]`);
      if (!node) return;
      node.hidden = text === '';
      node.textContent = text;
      node.className = `badge ms-1 ${cls}`;
    };
    const overBudget = overBudgetCount(budgetPanel.budgets());
    badge('wallets', over > 0 ? `${over} over` : '', 'text-bg-warning');
    // "over", not a bare number, for the same reason the wallets badge says it:
    // a count in a colour is a colour carrying the meaning (invariant #11).
    badge('budgets', overBudget > 0 ? `${overBudget} over` : '', 'text-bg-warning');
    badge('debts', debts.length > 0 ? String(debts.length) : '', 'text-bg-secondary');
  }

  /**
   * One debt, and underneath it the transfers that got it there.
   *
   * The page showed the net and nothing else, so there was no way to ask
   * what produced it — reported as exactly that. A net of two loans and a
   * repayment is a number nobody can check, and a number nobody can check is
   * one people stop believing before they stop acting on it.
   *
   * Folded away by default, because the figure is the answer on most days
   * and the working is what you want on the day it looks wrong.
   */
  function debtBreakdownHtml(d: Debt, panelId: string, open: boolean): string {
    const nameOf = (id: string): string => walletPath(data.wallets, id) || 'Unassigned';
    /*
     * The running figure wears the log's `∑`, so it reads as a total without
     * a sentence above the list saying so — there was one, and it was cut as
     * clutter. One `∑` and not the book's `∑∑`: this is the total between two
     * wallets, a slice. What the sentence said is kept on the chip's
     * accessible name, where a reader who cannot see the mark still gets it.
     */
    const owedSays = `what ${nameOf(d.debtorId)} owed after this`;
    /*
     * Newest first, like the log and every other list in this app — and
     * computed oldest first, as it has to be: each running figure is
     * everything up to and including its line. Reversing only the drawing
     * leaves the top line carrying the amount on the row above it, which is
     * where the eye goes next anyway.
     */
    const rows = [...d.lines].reverse().map((line) => {
      // Chips, as every amount in a list here is (see `chip`), so a line can
      // be Cmd/Ctrl-clicked into the tally like a register row. The sign is
      // the minus the chip writes, never its colour alone (invariant #11).
      // A line that moved nothing — a grant between the same two — has no
      // amount to show, and a chip reading 0.00 would look like one.
      const effect = Number(line.effect) === 0 ? '<span class="text-muted">—</span>'
        : chip(line.effect, d.currencyCode);
      return `<li class="wl-debt__line">
          <span class="wl-debt__date">${escapeHtml(line.txDate)}</span>
          <button type="button" class="wl-debt__what" data-debt-tx="${escapeHtml(line.transactionId)}">
            <span class="wl-debt__descr">${escapeHtml(line.description || '(no description)')}</span>
            <span class="wl-debt__kind">${escapeHtml(debtLineSays(line, nameOf, (v) => formatAmount(v, d.currencyCode)))}</span>
          </button>
          <span class="wl-debt__eff">${effect}</span>
          <span class="wl-debt__run">${chip(line.running, d.currencyCode, undefined, { text: TOTAL_MARK, says: owedSays })}</span>
        </li>`;
    }).join('');
    return `<div class="wl-debt__panel" id="${escapeHtml(panelId)}"${open ? '' : ' hidden'}>
        <ul class="wl-debt__lines">${rows}</ul>
      </div>`;
  }

  function renderDebts(): void {
    if (debts.length === 0) {
      el('wlDebts').innerHTML = '<p class="small text-muted mb-0">Nobody owes anybody. '
        + 'Every wallet is living within its own money.</p>';
      return;
    }
    // The expander shares the Holder cell rather than taking a column of its
    // own: a fifth column pushed the Repay button off a phone-width screen,
    // and a table a reader has to scroll sideways answers nothing.
    //
    // Repay shares the Amount cell for the same reason. Four columns were
    // still 406px at a 332px card, so the page scrolled sideways with every
    // panel folded and the button sat half off the edge. In one cell they
    // wrap: beside the figure where there is room, under it where there is
    // not — and a table cell cannot wrap, which is why it is not a column.
    // That alone left three columns at 340px; on a phone the creditor goes
    // under the holder as "owes …", which is the sentence the row is anyway.
    el('wlDebts').innerHTML = `<table class="table table-sm align-middle mb-0"><thead><tr>
        <th>Holder</th><th class="wl-debt__owes">owes</th><th class="text-end">Amount</th></tr></thead><tbody>`
      + debts.map((d, i) => {
        const panelId = `wlDebtLines-${i}`;
        const count = d.lines.length;
        const open = openDebts.has(debtKey(d));
        return `<tr>
          <td class="wl-debt__holder"><button type="button" class="wl-reg__exp"
            data-debt-lines="${escapeHtml(panelId)}" data-debt-key="${escapeHtml(debtKey(d))}"
            aria-expanded="${open}" aria-controls="${escapeHtml(panelId)}"
            aria-label="Show the ${count} ${count === 1 ? 'entry' : 'entries'} behind this"><span class="cf-exp" aria-hidden="true"></span></button>`
          + `<strong>${escapeHtml(walletPath(data.wallets, d.debtorId))}</strong>`
          + `<span class="wl-debt__owes-under">owes ${escapeHtml(walletPath(data.wallets, d.creditorId))}</span></td>
          <td class="wl-debt__owes">${escapeHtml(walletPath(data.wallets, d.creditorId))}</td>
          <td class="text-end"><div class="wl-debt__amt">${chip(d.amount, d.currencyCode)}<button
            class="btn btn-sm btn-outline-success" type="button" data-repay="${i}">Repay</button></div></td></tr>
          <tr class="wl-debt__panelrow"><td colspan="3" class="p-0 border-0">${debtBreakdownHtml(d, panelId, open)}</td></tr>`;
      }).join('')
      + '</tbody></table>';
    el('wlDebts').querySelectorAll<HTMLButtonElement>('[data-repay]').forEach((b) => {
      b.addEventListener('click', () => openRepay(debts[Number(b.dataset['repay'])]!));
    });
    el('wlDebts').querySelectorAll<HTMLButtonElement>('[data-debt-lines]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = el('wlDebts').querySelector<HTMLElement>(`#${CSS.escape(btn.dataset['debtLines']!)}`);
        if (!panel) return;
        const open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        // The chevron turns with aria-expanded (styles.css) — a shape as well
        // as the state, for a reader who cannot see the panel appear
        // (invariant #11).
        // Remembered, so the next `loadAll` finds it open — see `openDebts`.
        const key = btn.dataset['debtKey']!;
        if (open) openDebts.add(key); else openDebts.delete(key);
      });
    });
    el('wlDebts').querySelectorAll<HTMLButtonElement>('[data-debt-tx]').forEach((btn) => {
      btn.addEventListener('click', () => { openTransactionById(btn.dataset['debtTx']!, null); });
    });
  }

  function fillLogSelect(): void {
    const select = el<HTMLSelectElement>('wlLogSelect');
    // Read BEFORE the options are replaced: rebuilding them resets the select
    // to its first option, so this is the only moment the choice still exists.
    const chosen = select.value;
    const ids = ['all', ...orderWallets(data.wallets).map(({ wallet }) => wallet.id), UNASSIGNED];
    select.innerHTML = ['<option value="all">All wallets together</option>']
      .concat(orderWallets(data.wallets).map(({ wallet }) =>
        `<option value="${escapeHtml(wallet.id)}">${escapeHtml(walletPath(data.wallets, wallet.id))}</option>`))
      .concat([`<option value="${UNASSIGNED}">Unassigned</option>`])
      .join('');
    // What was chosen in this page's lifetime wins; the remembered wallet is
    // the fallback, and only while it still exists — a wallet deleted since
    // would otherwise leave the select showing nothing at all.
    const want = chosen || rememberedLog;
    select.value = ids.includes(want) ? want : 'all';
    // "All wallets together" is not a filter, so there is nothing to clear.
    el('wlLogSelectClear').hidden = select.value === 'all';
  }

  /** Which `renderRegister` is the newest; an older one that finishes later draws nothing. */
  let registerDraw = 0;

  /*
   * The last answer to "who holds a lease here", per wallet log. A search
   * redraws on every keystroke, and asking the server again each time would
   * be a request per letter for an answer that a sync redraw refreshes
   * anyway — so a search reuses it (`reuseLocks`).
   */
  let lastLocks: { id: string; locks: RegisterLock[] | undefined } | undefined;
  async function renderRegister(opts: { reuseLocks?: boolean } = {}): Promise<void> {
    const draw = registerDraw += 1;
    fillLogSelect();
    const id = el<HTMLSelectElement>('wlLogSelect').value || 'all';

    /*
     * The register is built here, from the replica (ADR-0025).
     *
     * The rows, the running total and the shortfall episodes are
     * `buildRegister` — the same function `services/api` calls — so the log
     * reads identically with or without a connection, and the tag filter and
     * its running total have rows to work on either way.
     *
     * `locks` is the one thing that cannot be computed locally: who holds an
     * entry is live hold state on the server. It is fetched separately and
     * best-effort, because a log that will not draw because nobody could be
     * asked who is editing is a worse answer than a log with no lock marks.
     */
    const view = buildRegister(datasetLedger(client.dataset), id);
    if (!view) {
      el('wlRegister').innerHTML = '<p class="small text-muted mb-0">That wallet no longer exists.</p>';
      return;
    }
    // The switch, where the log shows it: the running column as the totals
    // above it say, every debt between wallets settled.
    const settledHere = settled && settledSwitchShown('log', id);
    const { rows, shiftAt } = settledRunning(view.rows, settledHere ? walletShifts(id, true, data.wallets, debts) : []);
    // The whole book's position after each entry, for the hover on a `∑∑`.
    // Only for the book: a wallet's own log has one wallet to break down.
    bookAt = id === 'all' ? bookPositions(rows) : undefined;
    const episodes = view.episodes as Episode[];
    const locks = opts.reuseLocks && lastLocks?.id === id ? lastLocks.locks
      : await apiFetch(`/apps/webledger/wallets/${encodeURIComponent(id)}/locks`, { token: client.token })
        .then(async (res) => (res.ok ? ((await res.json()) as { locks: RegisterLock[] }).locks : undefined))
        .catch(() => undefined);
    lastLocks = { id, locks };
    /*
     * Overtaken while waiting: a newer draw started, from newer rows. Two run
     * at once after every save — the save's own and the one the sync's
     * arrival triggers — and the rows are read BEFORE this wait, so whichever
     * answer came back last used to paint over the other. When it was the
     * older one, the log showed the entry as it was before the save.
     */
    if (draw !== registerDraw) return;
    const all = id === 'all';
    const open = episodes.filter((e) => !e.closed).length;
    el('wlLogHelpPanel').innerHTML = all
      ? 'Every wallet together, newest first. Here the running total is simply the cash on hand, so gaps '
        + 'live inside individual wallets rather than in the total.'
      : episodes.length > 0
        ? `Newest first. <strong>${episodes.length}</strong> period${episodes.length === 1 ? '' : 's'} below zero`
          + (open ? `, <strong>${open}</strong> still open.` : ', all since closed.')
          + ' Each one is a moment this wallet was spending money it did not have.'
          // The highlight is the one thing on this page nobody can be told
          // about by looking: it appears on hover, so it is invisible until
          // you have already done the thing it explains.
          + ' <strong>Point at any row marked in red</strong> and the whole stretch'
          + ' it belongs to lights up in blue — from the entry that took this wallet'
          + ' under to the one that brought it back. That is the run to look at when'
          + ' you want to know what the shortfall was spent on, or which payment'
          + ' would have to move to close it.'
        : 'Newest first. This wallet has never gone below zero.';
    /*
     * What the marks on the running chips mean, behind the "?" rather than
     * above the list. It stood there as two lines for a while and was read
     * once and then in the way for ever — and it broke the layout of the row it sat in.
     */
    el('wlLogHelpPanel').innerHTML += '<span class="d-block mt-2">'
      + '<strong class="text-danger">W</strong> beside a running total means the wallet — or '
      + 'the book — is below zero in that currency. '
      + '<strong class="text-danger">A</strong> beside an amount means the account that leg came '
      + 'out of is, which is a different thing: a wallet can be perfectly funded out of a bank '
      + 'account that is empty. Only asset accounts are marked, because a liability is normally '
      + 'a credit balance. Both are counted over every entry on that wallet or that account, '
      + 'including any this filter is hiding.'
      + '</span>'
      + '<span class="d-block mt-2">'
      + `<strong>${TOTAL_MARK}</strong> on a figure means it is a total. `
      + `<strong>${WHOLE_BOOK_MARK}</strong> is the book\u2019s own running total — `
      + 'every wallet together, which is what this list shows when no single '
      + 'wallet is chosen. A tag filter does not change it: the left column still '
      + 'counts every row, including the ones being hidden, and the tag column '
      + `beside it \u2014 ${TOTAL_MARK}, a total of one slice \u2014 counts only the rows the `
      + 'filter is showing.'
      + '</span>';
    // Said plainly and in the open, because the running column will now
    // jump: it is still the wallet's real balance after each row, counted
    // over every entry, including the ones this filter is hiding.
    /*
     * The filter decides what is SHOWN, and nothing else.
     *
     * The index carried alongside each row is its place in the full,
     * unfiltered sequence — which is what the episodes are keyed to and what
     * the wallet's running balance was computed over. Hiding a row changes
     * neither.
     */
    /*
     * Tags first, then currency. The two narrow independently and both are
     * about which ROWS are drawn — neither touches the wallet's running
     * total or the gaps, which go on counting everything, for the reason the
     * note below says out loud.
     */
    const byTag = filterRowsByTags(rows, selectedTags, tagMode());
    const currency = logCurrency();
    const byCurrency = currency === '' ? byTag : byTag.filter((r) => r.currencyCode === currency);
    /*
     * The account, last, and the subtree rather than the one row — see
     * `accountSubtree` — and matched on the whole transaction, category and
     * equity legs included, see `rowsTouchingAccounts`. A collapsed row
     * survives if ANY of its legs is in the set: hiding a transfer because
     * only one of its two ends is the account being looked at would hide the
     * half that answers "and where did it go".
     */
    paintAccountFilter();
    const logAccounts = logAccountId === '' ? undefined : accountSubtree(allAccounts, logAccountId);
    const byAccount = !logAccounts ? byCurrency : rowsTouchingAccounts(
      byCurrency, datasetLedger(client.dataset).splits(), logAccounts);
    /*
     * With an account chosen, the running column is that account's — see
     * `accountRunning` — and with a wallet chosen too, the wallet's part of
     * it. The book's and the wallet's own totals stay in the bar above.
     */
    const logAccountName = logAccountId === '' ? '' : allAccounts.find((a) => a.id === logAccountId)?.name ?? '';
    const acctRun = !logAccounts ? undefined : (() => {
      const ledger = datasetLedger(client.dataset);
      const types = new Map(allAccounts.map((a) => [a.id, a.type]));
      const scopeIds = all ? undefined
        : id === UNASSIGNED ? undefined : new Set([id, ...descendantIds(data.wallets, id)]);
      return accountRunning(ledger.transactions(), walletLegs(ledger), logAccounts, all ? undefined : {
        inWallet: (w) => (scopeIds ? w !== null && scopeIds.has(w) : w === null),
        isMoney: (a) => types.get(a) === 'ASSET' || types.get(a) === 'LIABILITY',
        entries: new Set(rows.map((r) => r.transactionId)),
      });
    })();
    // The hover breaks down the whole book, which the column is not showing now.
    if (acctRun) bookAt = undefined;
    /*
     * The search box, last of all: notes and amounts, matched over the whole
     * entry rather than the one row — see `logSearchMatcher`. Like the
     * others it decides only what is drawn.
     */
    const searchText = el<HTMLInputElement>('wlLogSearch').value;
    el('wlLogSearchClear').hidden = searchText === '';
    const matcher = logSearchMatcher(searchText);
    const bySearch = !matcher ? byAccount : (() => {
      const ledger = datasetLedger(client.dataset);
      const wanted = new Set(byAccount.map((r) => r.transactionId));
      const legsOf = new Map<string, Array<{ memo?: string | null; amount: string; value: string }>>();
      for (const leg of ledger.splits()) {
        if (!wanted.has(leg.transactionId)) continue;
        const list = legsOf.get(leg.transactionId);
        if (list) list.push(leg); else legsOf.set(leg.transactionId, [leg]);
      }
      const found = new Set(ledger.transactions()
        .filter((t) => wanted.has(t.id) && matcher({ ...t, legs: legsOf.get(t.id) ?? [] }))
        .map((t) => t.id));
      return byAccount.filter((r) => found.has(r.transactionId));
    })();
    const shown = new Set(bySearch.map((r) => r.splitId));
    paintCurrencyFilter(rows);
    /*
     * Which entries came from a bank feed, straight off the replica.
     *
     * The account register asks the server for this list; here it is already
     * on the device, so a badge appears offline too. Built once rather than
     * asked per row: `bankInfoOf` walks every reference row, and a log of
     * three hundred entries would walk them three hundred times.
     */
    const fromBank = new Set(
      datasetLedger(client.dataset).transactionBankRefs().map((ref) => ref.transactionId));
    // Only while a filter is on. With none, every row is visible and this
    // would reproduce the column beside it exactly — see `taggedRunning`.
    const tagged = selectedTags.size > 0 ? taggedRunning(rows, shown) : undefined;

    /*
     * "In EUR" converts the running column too, not only the bar.
     *
     * Converting the total at the top and leaving the list in four currencies
     * answered the question in one place and left it unanswerable in the
     * other — which is where somebody is actually looking when they ask how
     * this wallet is doing. See `convertedRunning` for what the figure is and
     * what it is not.
     */
    const into = el<HTMLSelectElement>('wlTotalMode').value;
    const converting = into && into !== 'by' ? into : '';
    const conv = converting && !acctRun ? convertedRunning(rows, converting, fx?.rates ?? {}, undefined, shiftAt) : undefined;
    /*
     * And the tag total with it, counted over the rows the filter is showing.
     *
     * The two columns answer different questions — "where does this wallet
     * stand" and "how much has this project cost" — but they are unreadable
     * for the same reason in a multicurrency book, so they convert together.
     * Leaving one as four chips beside the other as one number would be the
     * worse of both arrangements.
     */
    const taggedConv = tagged && converting
      ? convertedRunning(rows, converting, fx?.rates ?? {}, shown)
      : undefined;

    /*
     * How much of the log is on screen, and why — split by how often each
     * half is worth reading.
     *
     * The arithmetic goes in the bar, next to the controls that produced it:
     * "109/284" is a glance, and it is the thing that stops a filter set last
     * week from reading as a ledger that has lost most of its entries. The
     * sentence underneath it — that the running total and the gaps are still
     * counted over everything, including the rows being hidden — is a caveat
     * worth reading once and not worth a paragraph above the list for ever,
     * so it joins the standing explanation behind the "?" immediately to the
     * right.
     *
     * The label is spelled out on the element rather than left to the slash:
     * a ratio with no words is not an answer to "what am I looking at", and
     * to a screen reader it is two numbers.
     */
    const countEl = el('wlLogFilterCount');
    const filters = [
      ...(selectedTags.size > 0 ? ['tag'] : []),
      ...(logAccountId !== '' ? ['account'] : []),
      ...(currency !== '' ? ['currency'] : []),
      ...(matcher ? ['search'] : []),
    ];
    const filtered = filters.length > 0;
    countEl.hidden = !filtered;
    countEl.textContent = filtered ? `${shown.size}/${rows.length}` : '';
    const countLabel = filtered
      ? `Showing ${shown.size} of ${rows.length} entries, filtered by ${filters.join(' and ')}`
      : '';
    countEl.setAttribute('aria-label', countLabel);
    countEl.title = countLabel;
    /*
     * The running column follows each currency on its own, so the newest line
     * shows only what that entry moved — not where the wallet stands.
     *
     * Which is a trap worth naming, because the newest line is the one
     * everybody reads: a wallet long in euros and dollars and short in
     * forints ends its log on "-552,305 HUF" with a mark beside it, and looks
     * broke while the bar above says it is holding forty thousand. Both are
     * true. Only one of them is the wallet.
     *
     * Said rather than designed away: showing every currency's running on
     * every row would bury the entry that actually moved, and showing it on
     * the top row alone would be a lie under a filter, where the top row is
     * not the end of anything.
     */
    const spansCurrencies = new Set(rows.map((r) => r.currencyCode)).size > 1;
    if (!converting && spansCurrencies) {
      el('wlLogHelpPanel').innerHTML += '<span class="d-block mt-2">'
        + '<strong>Each currency runs on its own line.</strong> The newest entry shows the running '
        + 'total only for the currency it moved, so the last figure in the list is not this '
        + 'wallet&rsquo;s position — the bar at the top is, currency by currency. To read it as one '
        + 'number, switch the totals to a currency and the column converts with it.'
        + '</span>';
    }
    if (converting) {
      /*
       * What the converted column is, said once where the explanation lives.
       *
       * And what it leaves out: a currency with no rate is dropped from the
       * sum, which makes the figure wrong by an amount nobody can see unless
       * it is named. Same stance as the bar's own warning.
       */
      el('wlLogHelpPanel').innerHTML += `<span class="d-block mt-2">`
        + `<strong>${tagged ? 'Both running totals are' : 'Running is'} converted into `
        + `${escapeHtml(converting)}</strong> at today's rate, `
        + 'including rows from months when the rate was something else — so the newest line is '
        + 'the wallet as it stands, and the ones above it read as what they would be worth now. '
        + 'The <strong>!</strong> still means short in a real currency, and says which.'
        + (conv && conv.missing.length > 0
          ? ` <strong>No rate for ${escapeHtml(conv.missing.join(', '))}</strong> — left out of the figure entirely.`
          : '')
        + '</span>';
    }
    if (filtered) {
      el('wlLogHelpPanel').innerHTML += `<span class="d-block mt-2">`
        + `<strong>${escapeHtml(countLabel)}.</strong> `
        + (acctRun
          ? `The running column, marked <strong>${TOTAL_MARK} acct</strong>, is `
            + `<strong>${escapeHtml(logAccountName)}</strong>\u2019s balance after each entry`
            + (all ? '' : `, counting only what belongs to ${escapeHtml(
              id === UNASSIGNED ? 'no wallet' : walletPath(data.wallets, id))}`)
            + ', over every entry including any this filter is hiding. The wallets\u2019 totals are in the bar above'
            + (all ? '' : ', and the gaps still count every entry of the wallet')
          : 'The wallet\u2019s running total and the gaps still count all of them')
        + (selectedTags.size > 0 ? ', and the tag total counts only these.' : '.')
        + '</span>';
    }

    if (rows.length === 0) { el('wlRegister').innerHTML = '<p class="small text-muted mb-0">Nothing here yet.</p>'; return; }

    /*
     * Both totals are renamed when there are two of them.
     *
     * Leaving one of them called plainly "Running" beside a new neighbour
     * would make the reader guess which is which, and guessing wrong here
     * means reading a project's cost as the wallet's balance.
     */
    /*
     * The files on each entry, from the replica (ADR-0025): the thumbnail is
     * on the device, the file is not — a click asks the server for a link.
     * The column is there only while something on screen has a file, the
     * way the tag column is there only while a tag filter is on.
     */
    const ledgerForFiles = datasetLedger(client.dataset);
    const filesOf = new Map([...new Set(rows.filter((r) => shown.has(r.splitId)).map((r) => r.transactionId))]
      .map((txId) => [txId, attachmentsOf(ledgerForFiles, txId)] as const)
      .filter(([, list]) => list.length > 0));
    const hasFiles = filesOf.size > 0;
    const head = `<div class="wl-reg__head"><span>Date</span><span>What</span>`
      + (hasFiles ? '<span>Files</span>' : '')
      // No Wallet column: a leg's wallet is notched into the leg's own chip.
      + '<span class="text-end">Amount</span>'
      /*
       * No mark on the heading. It lived here for a while, while the rows
       * carried it only at phone width — but the mark is a property of a
       * chip now, and one outside a chip in a heading says the opposite of
       * the rule it is meant to teach.
       */
      + `<span class="text-end">${acctRun ? 'Account running' : tagged ? 'Wallet running' : 'Running'}`
      + (converting ? ` <span class="wl-reg__inccy">in ${escapeHtml(converting)}</span>` : '')
      + '</span>'
      + (tagged
        ? `<span class="text-end">Tag running${
          converting ? ` <span class="wl-reg__inccy">in ${escapeHtml(converting)}</span>` : ''}</span>`
        : '')
      + '</div>';

    /*
     * Newest at the top, which is where the reader is.
     *
     * Only the display is reversed. The running total and the episodes are
     * cumulative facts that exist in one direction — each row's running total
     * is everything up to and including it — so the server computes them in
     * date order and this walks the same array backwards. An episode's
     * `fromRow`/`toRow` are indices into that original order, which is why the
     * index is carried alongside the row rather than recomputed here.
     *
     * The two end caps swap their arrows with the order. They point INTO the
     * gap: the row where it began is now at the bottom of the run and the rest
     * of it is above.
     */
    /**
     * One transaction, one row — whatever it is made of.
     *
     * A row used to be a LEG, so a currency conversion or any multi-split
     * entry appeared as two or three rows that are one thing, boxed together
     * to say so. The box was an improvement on nothing and still asked the
     * reader to do the joining: the same date twice, the same description
     * twice, two things to click that open the same form.
     *
     * So the legs are laid out inside one row instead. The numbers are
     * untouched — each leg keeps its own amount, and the running total is
     * still the per-leg arithmetic the register computed. What changes is
     * where they sit.
     *
     * This is deliberately NOT the collapse in `buildRegister`. That one
     * exists because the arithmetic changes: legs that cancel out leave the
     * running total where it was and must not open a shortfall between them.
     * This one is layout, and the domain is the wrong place for layout.
     */
    /**
     * An entry's files as thumbnails: three at most, then how many more.
     * Buttons, not links — the link has to be fetched first (the replica has
     * no signed url), see `wireFileThumbs`.
     */
    const filesCell = (txId: string): string => {
      const list = filesOf.get(txId) ?? [];
      const shownFiles = list.slice(0, 3);
      const more = list.length - shownFiles.length;
      return `<span class="wl-reg__files">${shownFiles.map((a) => {
        const name = `${a.originalName}${a.size ? ` \u00b7 ${formatBytes(a.size)}` : ''}`;
        const face = a.thumb
          ? `<img src="${escapeHtml(a.thumb)}" alt="" loading="lazy">`
          : `<span class="wl-reg__filekind">${escapeHtml(a.mime === 'application/pdf' ? 'PDF' : 'FILE')}</span>`;
        return `<button type="button" class="wl-reg__file" data-att-tx="${escapeHtml(txId)}" data-att-id="${escapeHtml(a.id)}"
          title="${escapeHtml(name)}" aria-label="Open ${escapeHtml(name)} in a new tab">${face}</button>`;
      }).join('')}${more > 0 ? `<span class="small text-muted" aria-label="and ${more} more">+${more}</span>` : ''}</span>`;
    };

    const rowHtml = (group: Array<{ r: RegisterRow; i: number }>): string => {
      // `ordered` is newest-first, so within a transaction the legs arrive
      // backwards. Amounts read better in the order they were entered.
      const inOrder = [...group].reverse();
      const r = inOrder[0]!.r;
      const legCount = group.length;

      const episodeOf = (row: RegisterRow) => episodes.find((e) => e.id === row.episodeId);
      const inGap = group.some(({ r: row }) => row.episodeId);
      const covering = group.some(({ r: row }) => row.isCover);
      const epId = group.find(({ r: row }) => row.episodeId)?.r.episodeId;
      // A merged row can hold the start of a gap and the end of another, so
      // every contained index gets asked and the answers are concatenated.
      const caps = group.map(({ r: row, i }) => episodeCaps(i, episodeOf(row))).join('');

      /*
       * Every transfer says what it is, not only the ones that happen to be
       * covering a gap. Before, a deliberate repurposing between two healthy
       * wallets showed nothing at all: two rows that cancel out, in the same
       * account, with no hint that an earmark had moved or that anybody owed
       * anybody anything.
       */
      const moved = legCount === 1 && r.legs.length > 1;
      /*
       * Which side of the transfer this row is, from its WALLET where it has
       * one: a borrower can pay out with borrowed money, and that outgoing
       * line is still the borrower's ("Loan from Car"), not a loan it made.
       * The sign answers only for a row in neither wallet.
       */
      const incoming = r.walletId === r.transferToWalletId ? true
        : r.walletId === r.transferFromWalletId ? false : Number(r.amount) > 0;
      const otherWalletId = (incoming ? r.transferFromWalletId : r.transferToWalletId) ?? '';
      /*
       * A collapsed row is the whole movement, so it names the kind once and
       * lets the arrow carry the direction; a single leg is read from its own
       * side and keeps saying "Lent to" or "Loan from".
       */
      // When the debt moved the money itself (`debtShiftLegs`), the move's own
      // rows say "Lent to" and "Loan from"; the entry's line beside them would
      // only say it a second time.
      const labelled = !r.transferShifted || r.shiftOf !== null;
      const label = !labelled ? undefined : moved
        // The names only where the wallet column is not there to carry them.
        // In the all-wallets view it already says "Renovation → Maintenance"
        // one cell away, and a chip repeating it is noise beside the one
        // thing the numbers cannot give: whether the money is coming back.
        ? movedLabel(r.transferKind,
          all ? '' : walletPath(data.wallets, r.transferFromWalletId ?? ''),
          all ? '' : walletPath(data.wallets, r.transferToWalletId ?? ''),
          r.transferRole, (v) => formatAmount(v, r.currencyCode))
        : transferLabel(r.transferKind, incoming,
          otherWalletId ? walletPath(data.wallets, otherWalletId) : '',
          r.transferRole, (v) => formatAmount(v, r.currencyCode),
          r.transferRole && r.transferRole.currencyCode === r.currencyCode
            && Number(r.transferRole.amount) < Math.abs(Number(r.amount)) - 0.00005
            ? formatAmount(r.transferRole.amount, r.currencyCode) : undefined);
      const cover = label
        ? `<div class="small mt-1"><span class="badge ${r.transferKind === 'loan' ? 'text-bg-warning' : 'text-bg-secondary'}">`
          + `${escapeHtml(label)}</span>`
          + `${covering ? ' <span class="text-muted">covers this gap</span>' : ''}</div>`
        : '';
      /*
       * The legs behind a collapsed row, foldable in place.
       *
       * A sibling of the row rather than a child of it: the row is a grid of
       * fixed columns, and a full-width panel inside one of them would be
       * squeezed into that column's width.
       */
      const legsId = `wlLegs-${r.splitId}`;
      const expander = moved
        ? `<button type="button" class="wl-reg__exp" data-legs="${escapeHtml(legsId)}"
            aria-expanded="false" aria-controls="${escapeHtml(legsId)}"
            aria-label="Show the two sides of this movement"><span class="cf-exp" aria-hidden="true"></span></button>`
        : '';
      const legsPanel = moved
        ? `<div class="wl-reg__legs" id="${escapeHtml(legsId)}" hidden>`
          + r.legs.map((leg) =>
            '<div class="wl-reg__leg"><span>'
            + escapeHtml(r.legsDifferBy === 'account' ? leg.accountName : leg.walletName)
            + `</span><span>${chip(leg.amount, r.currencyCode)}</span></div>`).join('')
          + '</div>'
        : '';

      /*
       * `moved` is unsigned, because neither sign is true: nothing entered or
       * left what is being looked at. The running total beside it does not
       * move either, which is the same fact said twice — and the word says it
       * a third time for a reader who takes neither (invariant #11).
       */
      /*
       * `A` goes beside the amount, because an account is a property of the
       * leg and the amount column is the only one that has one per leg.
       */
      const accountMark = accountMarkHtml;
      /*
       * What this entry did to the running total, per currency: the running
       * figure after it, less what its rows here moved in that currency.
       */
      const walletMove = (ccy: string): string[] => {
        const lastRow = lastPerCcy.get(ccy);
        if (!lastRow) return [];
        const movedHere = inOrder.filter(({ r }) => r.currencyCode === ccy).reduce((s, { r }) => s + Number(r.amount), 0);
        const who = id === 'all' ? 'The whole book' : id === UNASSIGNED ? 'Unassigned' : walletPath(data.wallets, id);
        return [moveLine(who, String(Number(lastRow.running) - movedHere), lastRow.running, ccy)];
      };
      /*
       * The leg's wallet, notched into the leg's own figure.
       *
       * It had a column of its own — 138px of track that was mostly
       * whitespace, and the first thing to run out of room when a tag filter
       * added a sixth. A wallet is a property OF a leg, so it goes where the
       * leg is: in the chip's top border, which is the notch every field on
       * this page already wears.
       *
       * Costs about seven pixels of height per chip. Gives back a column,
       * and with it the two copies of every name, the breakpoint that chose
       * between them, and the pill that had to look different from an
       * account name because it sat in a column of plain text beside one.
       *
       * Drawn short and spoken long. The notch elides at about seven
       * characters and a collapsed row writes two names and an arrow, so
       * what goes in the border is `wallet.shortName` — but the accessible
       * name has all the room in the world, and a reader who cannot see the
       * notch is the last person who should be handed the abbreviation.
       */
      const legWallet = (row: { walletName: string; walletShort: string }):
      { text: string; says: string } | undefined =>
        (all ? { text: row.walletShort, says: row.walletName } : undefined);
      /*
       * Chip FIRST in the DOM, then the wallet, then the mark.
       *
       * At phone width `.wl-reg__runline` is `row-reverse`, so the first
       * child is drawn rightmost — which is what keeps the figures on one
       * straight edge whatever hangs off their left. Written in the order
       * the wide layout wants and reversed for the narrow one, rather than
       * the other way round, because only the narrow one reverses.
       */
      /*
       * A debt with none of this wallet's money in the entry
       * (`RegisterRow.debtAmount`): the debt's figure, drawn as a moved one
       * is — the wallet's own money did not change — and the word under it
       * says which way it runs, so the dimmed chip is not read as a payment.
       */
      const debtWord = r.transferKind === 'grant'
        ? (incoming ? 'given to it' : 'given away')
        : (incoming ? 'borrowed' : 'lent');
      const amountCell = r.debtAmount !== null
        ? `<span class="wl-reg__runline">${chip(
          r.debtAmount, r.currencyCode, 'cf-cur-chip--moved', legWallet(r))}${SHORTFALL_SLOT}</span>`
          + `<span class="small wl-reg__moved">${debtWord}</span>`
        : moved
        ? `<span class="wl-reg__runline">${chip(
          r.moved ?? '0', r.currencyCode, 'cf-cur-chip--moved', legWallet(r))}${
          accountMark(r)}</span>`
          + '<span class="small wl-reg__moved">moved</span>'
        : inOrder.map(({ r: row }) =>
          `<span class="wl-reg__runline">${chip(
            row.amount, row.currencyCode, undefined, legWallet(row))}${
            accountMark(row)}</span>`).join('');

      /*
       * One running total per CURRENCY, not per leg.
       *
       * A leg's running total is cumulative, so two legs in the same currency
       * give two numbers of which only the second means anything — "where
       * this stands after the entry". Two legs in different currencies give
       * two numbers that both do, which is the conversion this exists for:
       * 150.00 USD out and 46,348.03 HUF in leave both currencies somewhere
       * new. So: the last value in each currency, in the order they appear.
       */
      const lastPerCcy = new Map<string, RegisterRow>();
      for (const { r: row } of inOrder) lastPerCcy.set(row.currencyCode, row);

      /*
       * One per draw, not one per row: the scope cannot change inside a list.
       *
       * The tag filter does not change it. It did for a moment — the two
       * columns reading ∑∑ and ∑ looked like a comparison of the marks
       * rather than of what each counts — but the left column IS the book's
       * running total when every wallet is chosen, filter or no filter: it
       * counts every row, including the ones being hidden. Marking it ∑
       * beside the tag's ∑ said the two were totals of the same kind of
       * thing, which is the one claim that is actually false.
       */
      const mark = runningMark(id);
      const scopeSentence = acctRun
        ? `Running total for ${logAccountName}`
          + (all ? '' : `, only what belongs to ${id === UNASSIGNED ? 'no wallet' : walletPath(data.wallets, id)}`)
        : runningScopeSays(
          id, id === 'all' || id === UNASSIGNED ? undefined : walletPath(data.wallets, id))
          + (settledHere ? ', if every debt between wallets were settled' : '');
      /*
       * The account's figure: one per currency the entry moved on it, or one
       * converted. No W beside it — an account is not a wallet, and a
       * wallet's share of one account below zero is ordinary (paid in here,
       * spent from there); the A beside the amount is the account's mark.
       */
      const acctAt = acctRun?.get(r.transactionId);
      const acctNotch = { text: `${TOTAL_MARK} acct`, says: 'account total' };
      const acctCell = !acctRun ? ''
        : !acctAt ? '<span class="wl-reg__runline">\u2014</span>'
          : converting
            ? `<span class="wl-reg__runline">${chip(
              convertTotals(acctAt.held, converting, fx?.rates ?? {}).amount.toFixed(4),
              converting, undefined, acctNotch)}${SHORTFALL_SLOT}</span>`
            : acctAt.moved.map((m) => `<span class="wl-reg__runline">${chip(
              m.running, m.currencyCode, undefined, acctNotch)}${SHORTFALL_SLOT}</span>`).join('');
      const runLines = acctRun ? (converting || !acctAt ? 1 : acctAt.moved.length) : lastPerCcy.size;

      /*
       * Converted: ONE line for the whole wallet, not one per currency.
       *
       * That is the point of asking for it — the per-currency lines are the
       * thing you cannot add up in your head. The shortfall mark stays per
       * currency all the same, and names the currencies it means: a wallet
       * can read healthy in euros while being short in forints, and that is
       * the fact ADR-0023 exists to keep visible.
       */
      const runningCell = acctRun ? acctCell : conv
        ? (() => {
          const last = [...inOrder].pop()!.r;
          const at = conv.byRow.get(last.splitId);
          const shortIn = at?.shortIn ?? [];
          const whose = shortfallSubject(id);
          return `<span class="wl-reg__runline">${chip(at?.amount ?? '0', converting, undefined, { text: mark })}`
            + (shortIn.length > 0
              ? shortfallMarkHtml('W', `${whose} in minus in ${shortIn.join(', ')}`, shortIn.flatMap(walletMove))
              : SHORTFALL_SLOT)
            + '</span>';
        })()
        : [...lastPerCcy.values()].map((row) => {
          const over = Number(row.running) < -0.00005;
          const whose = shortfallSubject(id);
          return `<span class="wl-reg__runline">${chip(row.running, row.currencyCode, undefined, { text: mark })}`
            // The slot is always there, filled or not: without it a row that
            // gains a mark pulls its chip left and the column stops lining up.
            + (over ? shortfallMarkHtml('W', `${whose} in minus in ${row.currencyCode}`, walletMove(row.currencyCode)) : SHORTFALL_SLOT)
            + '</span>';
        }).join('');
      /*
       * One figure when converted, for the same reason the wallet's is one:
       * the per-currency lines are what you cannot add up in your head.
       *
       * Read off the row's LAST leg, which is where this row's accumulation
       * ends — not `lastPerCcy`, which is one entry per currency and would
       * print the same converted total two or three times over.
       */
      const tagCell = tagged
        ? (taggedConv
          ? `<span>${chip(
            taggedConv.byRow.get(inOrder[inOrder.length - 1]!.r.splitId)?.amount ?? '0',
            converting, undefined, { text: `${TOTAL_MARK} tag`, says: 'tag total' })}</span>`
          : [...lastPerCcy.values()]
            .map((row) => `<span>${chip(
              tagged.get(row.splitId) ?? '0', row.currencyCode, undefined,
              { text: `${TOTAL_MARK} tag`, says: 'tag total' })}</span>`).join(''))
        : '';

      // Stacked only when there is something to stack, so a one-leg row keeps
      // the baseline alignment every other cell is laid out against.
      const stack = (cell: string, many: boolean): string =>
        (many ? `<span class="wl-reg__stack">${cell}</span>` : cell);

      /*
       * The wallet column follows the amounts: one name when every leg shares
       * it, otherwise one per leg, lined up with the figure it belongs to.
       * Naming them in a single cell would leave the reader guessing which
       * amount came out of which wallet.
       */
      const describe = legCount > 1
        ? `Open transaction of ${r.txDate}, ${legCount} legs`
        : `Open transaction of ${r.txDate}`;
      // In the description, as the account register and legacy both have it:
      // a bank origin is a fact about this entry, not a column's worth of data.
      const bank = fromBank.has(r.transactionId) ? bankInfoBadgeHtml(r.transactionId) : '';

      /*
       * The two sides are wrappers that are not there on a wide screen —
       * `display:contents` drops them out of the box tree, so the six cells
       * are direct members of the row's grid exactly as before.
       *
       * They exist for phone width, where the row is two columns that flow
       * INDEPENDENTLY. One grid with shared rows could not do that: a cell of
       * two stacked currency chips makes its row as tall as itself, and the
       * date and the description on the other side are pushed apart by the
       * difference — which is what made one entry read as two.
       */
      return `<div class="wl-reg__row${inGap ? ' wl-reg__row--gap' : ''}${covering ? ' wl-reg__row--cover' : ''}"
          ${epId ? `data-ep="${escapeHtml(epId)}"` : ''}
          data-tx="${escapeHtml(r.transactionId)}" data-split="${escapeHtml(r.splitId)}"
          data-date="${escapeHtml(r.txDate)}" draggable="true"
          tabindex="0" role="button"
          aria-label="${escapeHtml(describe)}">
        <span class="wl-reg__side">
          <span class="small wl-reg__date">${escapeHtml(r.txDate)}</span>
          <span class="wl-reg__what"><span data-state-slot></span>${expander}${escapeHtml(r.description || r.accountName)}${bank}
            ${(() => {
    // A debt's earmark move (`debtShiftLegs`) went to no category: the money
    // stayed on its account and only changed wallets, which the label says.
    const path = r.shiftOf !== null ? r.accountName : moneyPath(r.accountName, r.counterpart, r.amount);
    return path ? `<span class="small text-muted">· ${escapeHtml(path)}</span>` : '';
  })()}
            ${caps}${cover}</span>
          ${hasFiles ? filesCell(r.transactionId) : ''}
        </span>
        <span class="wl-reg__side wl-reg__side--nums">
          <span class="text-end wl-reg__amt">${stack(amountCell, legCount > 1)}</span>
          <span class="text-end wl-reg__run"
            aria-label="${escapeHtml(scopeSentence)}">${
  stack(runningCell, runLines > 1)}</span>
          ${tagged ? `<span class="text-end wl-reg__tag">${stack(tagCell, lastPerCcy.size > 1)}</span>` : ''}
        </span>
      </div>${legsPanel}`;
    };

    /*
     * The legs of one transaction are contiguous and stay so when the order
     * is reversed, so grouping is a scan of neighbours rather than a regroup
     * that would have to reorder anything.
     *
     * `buildRegister` sorts by date, `seq`, transaction id and only then
     * split id. The transaction id in that sort is what makes the adjacency
     * true: without it two entries sharing a date and a null `seq`
     * interleave, which is how this found a real ordering bug on its first
     * run.
     */
    const ordered = rows.map((r, i) => ({ r, i })).filter(({ r }) => shown.has(r.splitId)).reverse();
    if (ordered.length === 0) {
      /*
       * Name the filters that are actually on.
       *
       * This said "no entry here carries that tag" whatever had emptied the
       * list, because the tag filter was the only one when it was written.
       * With four of them, blaming the wrong one sends somebody to clear a
       * filter that was never set while the one that did it stays on.
       */
      const names = filters.map((f) => (f === 'tag'
        ? (selectedTags.size > 1 && tagMode() === 'all' ? 'all of those tags' : 'that tag')
        : (f === 'account'
          ? (allAccounts.find((a) => a.id === logAccountId)?.name ?? 'that account')
          : (f === 'search' ? `\u201c${searchText.trim()}\u201d` : 'that currency'))));
      const said = names.length === 0
        ? 'Nothing here yet.'
        : `Nothing here matches ${names.length === 1 ? names[0]
          : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`}.`;
      el('wlRegister').innerHTML = `<p class="small text-muted mb-0">${escapeHtml(said)}</p>`;
      return;
    }
    const chunks: string[] = [];
    for (let k = 0; k < ordered.length;) {
      let last = k;
      while (last + 1 < ordered.length
        && ordered[last + 1]!.r.transactionId === ordered[k]!.r.transactionId) last += 1;
      chunks.push(rowHtml(ordered.slice(k, last + 1)));
      k = last + 1;
    }
    const body = chunks.join('');

    el('wlRegister').innerHTML =
      `<div class="wl-reg${all ? ' wl-reg--all' : ''}${tagged ? ' wl-reg--tag' : ''}${hasFiles ? ' wl-reg--files' : ''}">${head}${body}</div>`;
    wireEpisodeHighlight();
    wireFileThumbs();
    wireRowOpen();
    wireLegExpanders();
    wireRowDrag();
    // Re-applied on every render, because a save, a filter change and a live
    // update all rebuild this list. The sweep is played only when a save put
    // the row there — arriving at a row you just changed is the moment worth
    // pointing at; a background refresh is not.
    // Waiting for the row first — on a slow connection the save is drawn
    // before the entry has arrived — then following it for a while from the
    // moment it is there.
    if (flashNext) { followUntil = Date.now() + ARRIVE_MS; sweepPending = true; flashNext = false; }
    const following = Date.now() < followUntil;
    if (applyMarker({ flash: following && sweepPending, scroll: following }) && following && sweepPending) {
      sweepPending = false;
      followUntil = Date.now() + FOLLOW_MS;
    }
    /*
     * Back where the reader was, unless a save is being followed — pointing
     * at the saved entry IS the answer to "where am I", and restoring an
     * older position on top of it would undo it.
     *
     * After the rows exist, because a position means nothing against a list
     * that has not been drawn.
     */
    const scrollKey = PREF.scrollWalletLog(id);
    logScroll.key(scrollKey);
    logScroll.settling();
    if (following) logScroll.start();
    else void restoreScroll(client.prefs, scrollKey, logScroller).then(() => logScroll.start());
    // Seed from what was just fetched and re-subscribe: nothing replays a
    // lease taken before this render, and the server's fan-out registry is
    // per-connection, so a reconnect has to ask again.
    const ids = [...new Set(rows.map((r) => r.transactionId))];
    /*
     * Seeded with what the response actually carries, not with bare ids.
     *
     * It used to seed `{ id }` and nothing else, so every row rendered free
     * and the held ones flipped a second later when the socket delivered
     * what the list could have said in the first place — and named them
     * "Someone else" until presence caught up, since the name is resolved
     * out of a roster this page has only just joined. `locks` is optional so
     * a cached response from before this change still parses.
     */
    records.seed(locks ?? ids.map((id) => ({ id })));
    records.subscribe(ids);
    paintRowStates();
  }

  /**
   * A thumbnail in the log opens its file in a new tab.
   *
   * The tab opens first, blank, while this is still the click — after
   * the `await` it would be a pop-up and Safari would block it — and gets
   * the file once the server has signed a link. `stopPropagation` because the
   * row around it opens the entry, which is not what was asked.
   */
  function wireFileThumbs(): void {
    el('wlRegister').querySelectorAll<HTMLButtonElement>('[data-att-id]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        const txId = btn.dataset['attTx']!;
        const attId = btn.dataset['attId']!;
        const win = openAttachmentTab();
        // Refused — a pop-up blocker. Said, rather than loading the file over
        // the app, which is the one place it was asked not to go.
        if (!win) {
          fail('The browser blocked the new tab. Allow pop-ups for this site to open files from the log.');
          return;
        }
        void (async () => {
          const res = await apiFetch(`/apps/webledger/transactions/${txId}/attachments`, { token: client.token })
            .catch(() => undefined);
          const list = res?.ok ? ((await res.json()) as { attachments: Array<{ id: string; url: string }> }).attachments : [];
          const file = list.find((a) => a.id === attId);
          if (!file?.url) {
            win.close();
            fail(res ? 'That file could not be found any more.' : 'Opening a file needs a connection.');
            return;
          }
          win.location.href = appHref(file.url);
        })();
      });
      // The row is `role="button"` and swallows Enter and Space to open the
      // entry; this one has to claim its own first.
      btn.addEventListener('keydown', (event) => { event.stopPropagation(); });
    });
  }

  /**
   * Unfold the two sides of a collapsed row, in place.
   *
   * `stopPropagation`, because the button sits inside a row that opens the
   * transaction: without it, asking to see the legs would open the editor
   * over the top of the answer.
   */
  function wireLegExpanders(): void {
    el('wlRegister').querySelectorAll<HTMLButtonElement>('[data-legs]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        const panel = el('wlRegister').querySelector<HTMLElement>(`#${CSS.escape(btn.dataset['legs']!)}`);
        if (!panel) return;
        const open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        // The chevron turns with aria-expanded (styles.css) — a shape as well
        // as the state, for a reader who cannot see the panel appear
        // (invariant #11).
      });
      // The row is `role="button"` and swallows Enter and Space to open the
      // editor; this one has to claim its own first.
      btn.addEventListener('keydown', (event) => { event.stopPropagation(); });
    });
  }

  /**
   * Who is in each transaction right now, on the rows that show them.
   *
   * The same tracker the register and the reports use, rather than a second
   * implementation: seeding, re-subscribing, TTL sweeping and reading
   * `leaseFreed.reason` correctly are four bugs each, and the second copy's
   * would be found by a user (webledgerRecordState.ts).
   */
  function paintRowStates(): void {
    el('wlRegister').querySelectorAll<HTMLElement>('[data-tx]').forEach((row) => {
      const id = row.dataset['tx']!;
      const holder = records.lockedBy.get(id);
      /*
       * Saved on this device and not sent yet (ADR-0011, 2026-10-21): the row
       * shows the unsent version, and says so — a create the server has never
       * seen, or an edit of one it has.
       */
      const unsent = client.dataset.isPendingRow('transaction', id);
      const created = unsent && !client.dataset.anyRow('transaction', id);
      const state = recordState({
        tx: { id, pendingSync: created }, holder, owner: holder ? undefined : records.ownedBy.get(id),
        held: undefined, queuedEdit: unsent && !created,
        refused: client.refusedEntries().find((r) => r.recordId === id)?.message,
      });
      // Toggled one by one, not assigned: the gap and cover classes are this
      // page's own and must survive a repaint that has something to say only
      // about who is editing.
      applyRecordState(row, state);
    });
  }

  /**
   * Open the transaction behind a register row.
   *
   * A row is a projection of one leg, not the whole document, so the editor is
   * handed the record fetched fresh rather than something reassembled from
   * what happens to be on screen — the rule the account register already
   * follows. Nothing wallet-specific is needed to make the wallet editable:
   * `walletId` is an ordinary split field, so the existing editor saves it.
   */
  /**
   * Drag to reorder within one day.
   *
   * The gesture itself is `wireDayDrag`, shared with the account register —
   * including the rule that a drag may not cross days, which is refused
   * during the drag rather than reported after it.
   *
   * One row per transaction here, even when it shows several legs, so the
   * element being dragged is the thing `seq` orders.
   */
  function wireRowDrag(): void {
    wireDayDrag({
      container: el('wlRegister'),
      rowSelector: '[data-tx][data-date]',
      idOf: (row) => row.dataset['tx'],
      dateOf: (row) => row.dataset['date'],
      onMove: (date, movedId, targetId) => { void applyReorder(date, movedId, targetId); },
    });
  }

  async function applyReorder(date: string, movedId: string, targetId: string): Promise<void> {
    /*
     * The whole day, from the ledger — not `rows`, which is this log after
     * its wallet and its tag filter have had their say. The server refuses
     * anything that is not a permutation of the entire day, so a filtered
     * log could never produce one.
     */
    const orderedIds = dayOrderAfterMove(
      datasetLedger(client.dataset).transactions(), date, movedId, targetId,
    );
    if (!orderedIds) return;

    clearPageNotice(root); // this attempt gets to speak for itself
    const res = await apiFetch('/apps/webledger/transactions/reorder', {
      method: 'POST', token: client.token, body: { date, orderedIds },
    }).catch(() => undefined);
    if (!res?.ok) {
      // Nothing is repainted until `loadAll` below, so a failure leaves the
      // log looking exactly as it did before the drag.
      fail(res ? await failureMessage(res, 'Could not save the new order.')
        : 'Could not save the new order.');
      return;
    }
    /*
     * Renumber the day locally too, and that is the whole of why this looked
     * broken.
     *
     * The log is drawn from the local replica (ADR-0025), not from the
     * response, and the replica cannot be asked for this change yet: the
     * feed holds its cursor a minute behind the present on purpose, so a
     * write made a second ago is not offered to anybody. Repainting after the
     * POST therefore drew the order the device still had — the one just
     * replaced. The reorder worked every time; the rows simply never moved,
     * which from the outside is the same thing as it not working.
     *
     * So the same renumbering the server did is applied here — 1..N across
     * the day, the server's own rule, which is safe to compute locally
     * because the server has just agreed both sides hold the same day: a
     * request that was not a permutation of it would have been refused.
     *
     * The delta will bring the same values round again later and overwrite
     * these with themselves.
     */
    const dayRows = datasetLedger(client.dataset).transactions().filter((t) => t.txDate === date);
    const renumbered: Array<Record<string, unknown> & { id: string }> = [];
    orderedIds.forEach((id, index) => {
      const row = dayRows.find((t) => t.id === id);
      if (row !== undefined) renumbered.push({ ...row, seq: index + 1 });
    });
    await client.dataset.applyChanges({ transaction: renumbered });
    // `renderRegister`, not `loadAll`: the log is what changed, and
    // `loadAll` only repaints it when the page's own section check agrees —
    // which is how the rows stayed put even once the replica held the new
    // order. Nothing else on the page moves when a day is resequenced.
    await renderRegister();
  }

  /**
   * The transactions this log is showing, in the order it shows them.
   *
   * Read off the DOM rather than from the register's rows, and that is the
   * point: the rows on screen are already the wallet's, already past the tag
   * filter, already in display order and already one per transaction however
   * many legs it draws. Any list rebuilt from the data behind them would
   * have to reproduce all four, and would drift the first time one of them
   * changed.
   */
  function visibleTransactionIds(): string[] {
    const rows = el('wlRegister').querySelectorAll<HTMLElement>('[data-tx]');
    return [...new Set([...rows].map((row) => row.dataset['tx']).filter(
      (id): id is string => id !== undefined && id !== ''))];
  }

  /*
   * Which way round the two directions are.
   *
   * `idOf` counts rows as the log draws them, and the log draws newest
   * first — so one step DOWN it is one step back in time. The buttons, the
   * arrows on them and the swipes all speak of older and younger, and this
   * pair is the single place that knows which array direction each of those
   * is. Spelling it `-1` at every call site is how they came to disagree.
   */
  const OLDER = 1;
  const YOUNGER = -1;

  /**
   * What the forms step through. `-1` is up the list as drawn, `+1` is down.
   *
   * `go` marks the row and flashes it into view before the editor opens, so
   * a transaction further down the page than the modal arrives already
   * pointed at — the reader looks up from the form and the list agrees with
   * it.
   */
  const registerNeighbours = {
    idOf: (txId: string, delta: -1 | 1): string | undefined => {
      const ids = visibleTransactionIds();
      const at = ids.indexOf(txId);
      return at < 0 ? undefined : ids[at + delta];
    },
    go: (txId: string): void => { openTransactionById(txId, null); },
  };

  function wireRowOpen(): void {
    const open = (row: HTMLElement): void => {
      openTransactionById(row.dataset['tx']!, row.dataset['split'] ?? null);
    };
    wireRowOpenHandlers(open);
  }

  function openTransactionById(txId: string, splitId: string | null): void {
    void (async () => {
      /*
       * Whatever form is up goes first, and is waited for.
       *
       * Bootstrap will not open one modal while another is still hiding —
       * the second show is swallowed and the reader is left looking at the
       * page with no form at all. Stepping between two entries does exactly
       * that whenever the two need different forms, which on this log is
       * common: a wallet transfer cannot be said on the short form and its
       * neighbour usually can.
       *
       * Closing the full editor first is right for its own sake too: its
       * teardown runs on `hidden`, so re-opening it without closing would
       * stack a second set of handlers on the same static elements.
       */
      await closeOpenTransactionForm();
      // Marked before anything is fetched, so the row is already answering
      // "which one did I press" while the editor is still on its way — and
      // still answering it after the editor closes without saving. The leg,
      // not the transaction: see `applyMarker`.
      currentTxId = txId;
      currentSplitId = splitId;
      // Flashed, not merely set: arriving here from the Next button means the
      // row may be anywhere on the page, including off it.
      applyMarker({ flash: true, scroll: true });
      void (async () => {
        /*
         * The replica first, the server only for what it alone knows
         * (ADR-0025).
         *
         * The transaction and its legs are already here, so a row opens
         * offline and opens without a round trip online. What the server adds
         * is who holds the lease and whose unfinished edit it is — both
         * best-effort, because refusing to open an entry for want of knowing
         * whether somebody else has it would be a strictly worse answer than
         * opening it without that warning.
         */
        clearPageNotice(root);
        const local = transactionWithSplits(datasetLedger(client.dataset), txId);
        /*
         * Unsent work on it — waiting, or refused — and the local copy is the
         * one to open: it shows that work (`Dataset.setPendingRows`), where
         * the server's copy would quietly show what it was before.
         */
        const unsent = local !== undefined && client.dataset.isPendingRow('transaction', txId);
        const res = unsent ? undefined : await apiFetch(`/apps/webledger/transactions/${txId}`, { token: client.token })
          .catch(() => undefined);
        if (!res?.ok && !local) {
          // Without a notice here, clicking a row simply appears to do nothing.
          fail(await failureMessage(res, 'Could not open that transaction.'));
          return;
        }
        const body = res?.ok
          ? await res.json() as { transaction: Parameters<typeof openTransactionEditor>[2] }
          : { transaction: local as unknown as Parameters<typeof openTransactionEditor>[2] };
        // The short form first, for the shapes it can say honestly — held by
        // somebody else or not; anything else — more than two legs, mixed
        // currencies, a wallet transfer — goes to the full editor, which is
        // built for exactly those cases.
        if (await openQuickEdit(body.transaction as never, { unsent })) return;
        await openTransactionEditor(
          root, client, body.transaction, allAccounts, currencies,
          (savedId) => { currentTxId = savedId; flashNext = true; void afterWrite(); },
          { ...simpleEditorFor(body.transaction as never), neighbours: registerNeighbours },
        );
      })();
    })();
  }

  function wireRowOpenHandlers(open: (row: HTMLElement) => void): void {
    el('wlRegister').querySelectorAll<HTMLElement>('[data-tx]').forEach((row) => {
      row.addEventListener('click', (event) => {
        // A modified click belongs to the chip tally, which claims it in the
        // capture phase. This guard keeps the two from both firing.
        const mouse = event as MouseEvent;
        if (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey) return;
        open(row);
      });
      // The rows were already focusable for the gap highlight; Enter and Space
      // are what turn that into an actual control rather than a tab stop.
      row.addEventListener('keydown', (event) => {
        const { key } = event;
        if (key !== 'Enter' && key !== ' ') return;
        event.preventDefault();
        open(row);
      });
    });
  }

  /** Hovering or focusing any part of a gap lights up the whole of it. */
  function wireEpisodeHighlight(): void {
    const toggle = (epId: string, on: boolean): void => {
      el('wlRegister').querySelectorAll(`[data-ep="${epId}"]`)
        .forEach((n) => { n.classList.toggle('wl-reg__row--hl', on); });
    };
    el('wlRegister').querySelectorAll<HTMLElement>('[data-ep]').forEach((n) => {
      const epId = n.dataset['ep']!;
      n.addEventListener('mouseenter', () => { toggle(epId, true); });
      n.addEventListener('mouseleave', () => { toggle(epId, false); });
      n.addEventListener('focus', () => { toggle(epId, true); });
      n.addEventListener('blur', () => { toggle(epId, false); });
    });
  }

  // ------------------------------------------------------------- transfers

  let transferCtx: { mode: 'cover' | 'repay'; toWalletId: string; fromWalletId?: string; ccy: string } | undefined;

  function rollupOf(walletId: string, ccy: string): number {
    return Number((data.wallets.find((w) => w.id === walletId)?.rollup ?? {})[ccy] ?? 0);
  }

  /**
   * Money leaving a wallet that holds none of that currency is not a payment,
   * it is a second borrowing wearing a repayment's clothes. Warn, never block —
   * the same rule the spend path follows.
   */
  function updateWarning(): void {
    const warn = el('wlTransferWarn');
    if (!transferCtx) { warn.innerHTML = ''; return; }
    const amount = Number(el<HTMLInputElement>('wlTransferAmount').value.replace(/[\s\u202f,]/g, ''));
    const payer = transferCtx.mode === 'repay'
      ? transferCtx.fromWalletId
      : el<HTMLSelectElement>('wlTransferFrom').value;
    if (!payer || !Number.isFinite(amount) || amount <= 0) { warn.innerHTML = ''; return; }
    const before = rollupOf(payer, transferCtx.ccy);
    const after = before - amount;
    if (after >= -0.00005) { warn.innerHTML = ''; return; }
    const name = escapeHtml(walletPath(data.wallets, payer));
    const ccy = escapeHtml(transferCtx.ccy);
    const body = transferCtx.mode === 'repay'
      ? (before < -0.00005
        ? `<strong>${name}</strong> is itself still <span class="wl-num">${formatAmount(-before, transferCtx.ccy)}</span> ${ccy} `
          + 'short. Paying back now does not clear the debt — it deepens this wallet\u2019s own gap, so whoever '
          + 'covers it next is really the one repaying.'
        : `This would leave <strong>${name}</strong> <span class="wl-num">${formatAmount(-after, transferCtx.ccy)}</span> ${ccy} short. `
          + 'The debt moves rather than clears.')
      : (before < -0.00005
        ? `<strong>${name}</strong> has nothing spare in ${ccy} — it is <span class="wl-num">${formatAmount(-before, transferCtx.ccy)}</span> `
          + 'short itself. It can still lend, but it will need covering in turn.'
        : `Lending this much would leave <strong>${name}</strong> <span class="wl-num">${formatAmount(-after, transferCtx.ccy)}</span> ${ccy} `
          + 'short, so it will need covering in turn.');
    warn.innerHTML = `<div class="wl-attn mt-2 small"><span class="badge text-bg-warning">Careful</span> ${body}
      <span class="d-block mt-1">You can record it anyway.</span></div>`;
  }

  function openCover(walletId: string, ccy: string, amount: number): void {
    transferCtx = { mode: 'cover', toWalletId: walletId, ccy };
    el('wlTransferTitle').textContent = `Cover ${walletPath(data.wallets, walletId)}`;
    el('wlTransferIntro').innerHTML = `<strong>${escapeHtml(walletPath(data.wallets, walletId))}</strong> is over by `
      + `<span class="wl-num">${formatAmount(amount, ccy)}</span> ${escapeHtml(ccy)}. Whose money actually paid for it?`;
    // A parent's balance already contains its child's shortfall, so "Renovation
    // lends to Renovation / 68" is not a loan, it is the same money twice.
    const kin = new Set([walletId, ...ancestorIds(data.wallets, walletId), ...descendantIds(data.wallets, walletId)]);
    el<HTMLSelectElement>('wlTransferFrom').innerHTML = orderWallets(data.wallets)
      .filter(({ wallet }) => !kin.has(wallet.id))
      .map(({ wallet }) => {
        const has = Number(wallet.rollup[ccy] ?? 0);
        const note = has <= 0 ? 'nothing spare'
          : has < amount ? `${formatAmount(has, ccy)} ${ccy} spare — not the whole amount`
            : `${formatAmount(has, ccy)} ${ccy} spare`;
        return `<option value="${escapeHtml(wallet.id)}">${escapeHtml(walletPath(data.wallets, wallet.id))} (${escapeHtml(note)})</option>`;
      }).join('');
    el('wlTransferFromWrap').hidden = false;
    el('wlTransferKindWrap').hidden = false;
    el<HTMLInputElement>('wlTransferAmount').value = String(amount);
    updateWarning();
    clearDialogNotice('wlTransfer');
    el<HTMLDialogElement>('wlTransferDlg').showModal();
  }

  function openRepay(debt: Debt): void {
    transferCtx = { mode: 'repay', toWalletId: debt.creditorId, fromWalletId: debt.debtorId, ccy: debt.currencyCode };
    el('wlTransferTitle').textContent = `Repay ${walletPath(data.wallets, debt.creditorId)}`;
    el('wlTransferIntro').innerHTML = `<strong>${escapeHtml(walletPath(data.wallets, debt.debtorId))}</strong> is holding `
      + `<span class="wl-num">${formatAmount(debt.amount, debt.currencyCode)}</span> ${escapeHtml(debt.currencyCode)} of `
      + `<strong>${escapeHtml(walletPath(data.wallets, debt.creditorId))}</strong>\u2019s money. Pay back how much?`;
    el('wlTransferFromWrap').hidden = true;
    el('wlTransferKindWrap').hidden = true;
    el<HTMLInputElement>('wlTransferAmount').value = debt.amount;
    updateWarning();
    clearDialogNotice('wlTransfer');
    el<HTMLDialogElement>('wlTransferDlg').showModal();
  }

  async function submitTransfer(): Promise<void> {
    if (!transferCtx) return;
    const amount = Number(el<HTMLInputElement>('wlTransferAmount').value.replace(/[\s\u202f,]/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) { refuseInDialog('wlTransfer', 'Enter an amount.'); return; }
    const ccy = transferCtx.ccy;
    // Park the earmark shift on an account that actually holds this currency.
    const host = moneyAccounts.find((a) => a.currencyCode === ccy);
    if (!host) {
      refuseInDialog('wlTransfer', `No account holds ${ccy}, so there is nowhere to record it.`);
      return;
    }

    const repay = transferCtx.mode === 'repay';
    const fromWalletId = repay ? transferCtx.fromWalletId! : el<HTMLSelectElement>('wlTransferFrom').value;
    const toWalletId = transferCtx.toWalletId;
    // A repayment is a loan the other way round: the direction says which,
    // and the debts page works out "paid back" from it (`transferRoles`).
    const kind = repay ? 'loan' : el<HTMLSelectElement>('wlTransferKind').value;
    const label = repay ? 'repayment' : kind === 'loan' ? 'loan' : 'given for good';

    const res = await apiFetch('/apps/webledger/wallet-transfers', {
      method: 'POST', token: client.token,
      body: {
        transactionId: crypto.randomUUID(), splitIds: [crypto.randomUUID(), crypto.randomUUID()],
        kind, fromWalletId, toWalletId, accountId: host.id, currencyCode: ccy, amount,
        txDate: todayIso(),
        note: `${walletPath(data.wallets, fromWalletId)} → ${walletPath(data.wallets, toWalletId)} (${label})`,
      },
    });
    if (!res.ok) {
      refuseInDialog('wlTransfer', await failureMessage(res, 'The transfer could not be saved.'));
      return;
    }
    el<HTMLDialogElement>('wlTransferDlg').close();
    client.notify('Recorded.');
    await afterWrite();
  }

  async function createWallet(): Promise<void> {
    const name = el<HTMLInputElement>('wlNewName').value.trim();
    if (!name) { refuseInDialog('wlNew', 'A wallet needs a name.'); return; }
    const shortName = el<HTMLInputElement>('wlNewShort').value.trim();
    const color = el<HTMLInputElement>('wlNewColour').value;
    const parentId = el<HTMLSelectElement>('wlNewParent').value || undefined;
    const res = await apiFetch('/apps/webledger/wallets', {
      method: 'POST', token: client.token,
      body: {
        id: crypto.randomUUID(), name, color,
        ...(shortName ? { shortName } : {}),
        ...(parentId ? { parentId } : {}),
      },
    });
    if (!res.ok) {
      refuseInDialog('wlNew', await failureMessage(res, 'The wallet could not be created.'));
      return;
    }
    el<HTMLDialogElement>('wlNewDlg').close();
    await afterWrite();
  }

  /* =====================================================================
     QUICK ENTRY — the short way in, ported from the prototype.

     Three shapes cover almost everything these users type: money out,
     money in, and cash moving between two of their own accounts. Each
     becomes an ordinary two-leg balanced transaction, so nothing here is a
     parallel way of recording anything — the full editor opens on the same
     row afterwards and sees exactly what a transaction always looks like.

     The wallet goes on the MONEY leg only. That is the whole invariant in
     one line, and it is why the preview shows both legs: the category leg
     deliberately carries no wallet, and a reader should be able to see that
     rather than take it on trust.
  ===================================================================== */

  /**
   * What the short form is currently doing: a new entry, or one being edited.
   *
   * An edit holds a real record lease for as long as the dialog is open —
   * the same lease the full editor takes. Anything less would let two people
   * type into one transaction with no sign of each other, and the server
   * would refuse the second save anyway (invariant #2), just far too late to
   * be useful to anybody.
   */
  interface QuickEdit {
    txId: string;
    baseVersion: number;
    /**
     * Opened, or carried on, with no connection (ADR-0011): nothing is
     * reserved, and Save keeps the entry on this device and sends it when it
     * can — with this `baseVersion`, so a change somebody else made meanwhile
     * comes back for review instead of being overwritten.
     */
    offline?: boolean;
    /** Opened on its unsent version (waiting, or refused); Save puts it back in the queue. */
    unsent?: boolean;
    /**
     * The transaction's own currency. Kept because history formats a split's
     * `value` with THIS currency's fraction and symbol, not the book's
     * default — the difference is how `-469.0000` becomes `-Ft 469` instead
     * of `-€469.00`.
     */
    currencyCode: string;
    /** Split ids and versions, so a save updates these legs rather than making new ones. */
    money: { id: string; version: number };
    other: { id: string; version: number };
  }
  let quickEdit: QuickEdit | undefined;

  /**
   * Which leg is which, and what version each is at — the pair a save
   * compare-and-swaps against.
   *
   * One function because two places need exactly it: opening a transaction,
   * and coming back to the live record after looking at a past version. The
   * second of those used to repaint the fields and leave these behind.
   */
  function splitRefs(
    splits: ReadonlyArray<{ id: string; version: number }>,
    moneyIndex: number,
  ): Pick<QuickEdit, 'money' | 'other'> {
    const money = splits[moneyIndex]!;
    const other = splits[moneyIndex === 0 ? 1 : 0]!;
    return {
      money: { id: money.id, version: money.version },
      other: { id: other.id, version: other.version },
    };
  }
  /** The history panel for whatever the short form is holding, rebuilt per transaction. */
  let quickTimeline: HistoryPanel | undefined;
  /** True while the form is showing a past version rather than the record. */
  let quickReadOnly = false;

  /**
   * Show and hide, through Bootstrap rather than the native dialog API.
   *
   * Named rather than inlined because `showModal()`/`close()` used to be
   * scattered over eight call sites, and every one of them would have had to
   * learn the new mechanism. The focus is ours to set: a native `<dialog>`
   * honours `autofocus` on open, a Bootstrap modal does not, and without it
   * focus stays wherever it was — which after clicking a register row is a
   * row behind the modal.
   */
  function openQuickForm(focusId: 'wlqDate' | 'wlqAmount'): void {
    const modalEl = el('wlQuickDlg');
    // `shown.bs.modal`, not straight after `show()`: Bootstrap moves focus to
    // the modal itself as part of showing it, and anything set before that
    // is taken back. A NEW entry starts at its date, the first field and the
    // one that says which day this is; an existing one at its amount, which
    // is what you opened it to change.
    modalEl.addEventListener('shown.bs.modal', () => {
      el<HTMLInputElement>(focusId).focus();
    }, { once: true });
    bsModal(modalEl)?.show();
  }

  function closeQuickForm(): void {
    bsModal(el('wlQuickDlg'))?.hide();
  }

  /**
   * Close it, and wait until it has actually gone.
   *
   * Bootstrap will not open one modal while another is still hiding — the
   * second show is swallowed and you are left looking at the page with no
   * form at all. Stepping from the short form to an entry it cannot express
   * does exactly that: close, open the full editor, and nothing appears.
   *
   * `hidden.bs.modal` is the event that says the transition is over. The
   * timeout is a floor, not a guess: a modal that was never shown fires
   * nothing, and waiting for ever on it would be worse than opening a moment
   * early.
   */
  /** Whichever form is up, gone and finished going. */
  async function closeOpenTransactionForm(): Promise<void> {
    const hide = (el: HTMLElement): void => { bsModal(el)?.hide(); };
    await Promise.all(['wlQuickDlg', 'txModal'].map(
      (id) => closeModalAndWait(root.querySelector<HTMLElement>(`#${id}`), hide)));
  }

  /**
   * Files on the short form.
   *
   * Same bar as the full editor's, mounted rather than rebuilt — a receipt
   * belongs to the transaction, not to whichever form you happened to open
   * it in, and the reason for attaching one is usually the reason you opened
   * the short form in the first place.
   *
   * On a new entry the files are held and sent once Save has created it
   * (`holdUntilSaved`): a file needs a transaction id, and that entry has
   * none until then. Hidden while a past version is
   * showing, too: attachments are not part of a transaction's snapshot, so
   * what would be listed is the CURRENT set beside March's amounts.
   */
  /*
   * The bank badge and its hover panel, for the log and the short form alike.
   *
   * Mounted on `root` rather than on the register, so the one listener covers
   * the rows AND the dialog inside it. Both are re-rendered constantly — a
   * save, a filter, a live update — and a delegated listener on a container
   * that stays put needs no re-wiring, which is the same reason the rows
   * themselves are delegated.
   *
   * `localEntries` answers from the replica, so the panel opens offline and
   * without a round trip online. The reference rows travel; only the consent
   * internals stayed on the server (ADR-0025).
   */
  mountBankInfo(root, client.token, {
    localEntries: (txId) => bankInfoOf(datasetLedger(client.dataset), txId),
  });

  const quickFiles = mountAttachmentBar(root, 'wlq', {
    // Tiles from the local replica first, so they show offline too (ADR-0025):
    // name, size and thumbnail are on the device; only the signed url is not.
    localFiles: (txId2) => attachmentsOf(datasetLedger(client.dataset), txId2).map((a) => ({
      id: a.id, transactionId: txId2, originalName: a.originalName, mime: a.mime,
      size: a.size ?? 0, thumb: a.thumb ?? '', created: null, url: '',
    })),
    txId: () => (quickReadOnly ? undefined : quickEdit?.txId),
    token: client.token,
    canEdit: () => !quickReadOnly && quickLeaseId() !== undefined,
    // A new entry holds its files until Save has created it.
    holdUntilSaved: () => !quickReadOnly && quickEdit === undefined,
    notify: (m) => client.notify(m),
  });

  /**
   * The short form's hold on the entry it edits — the same shared piece the
   * full editor uses (webledgerHold.ts, ADR-0003 as amended 2026-10-22).
   */
  let quickHold: RecordHold | undefined;

  function quickLeaseId(): string | undefined {
    return quickEdit ? quickHold?.leaseId() : undefined;
  }

  /**
   * A draft of this entry somebody interrupted by taking it over — kept in
   * the outbox, the same store the full editor keeps its own in, so it
   * survives a reload, marks the row in the log, and is never sent on its own.
   */
  let quickHeld: OutboxDocumentWrite | undefined;
  /** Who took it, for the banner; separate from the hold, which may have moved on since. */
  let quickStolenBy: PresenceUser | undefined;
  /** Who this tab took it from — the other half of the same event. */
  let quickTookFrom: PresenceUser | undefined;

  /** Somebody else has the entry right now: read-only, with Take over. */
  function quickTheirs(): boolean {
    const v = quickHold?.view;
    return v?.kind === 'theirs' || v?.kind === 'refused';
  }

  const quickPerson = (h: Holder): PresenceUser | undefined =>
    h.person ?? client.session.presence('transaction', quickEdit?.txId ?? '').find((p) => p.id === h.userId);

  /**
   * The entry as the server has it now, in the form — versions included, so
   * the next save compares against what is really there. `unfit` when it has
   * become a shape the short form cannot say (somebody added a leg in the
   * full editor): the caller hands it over.
   */
  async function reloadQuick(): Promise<'ok' | 'failed' | 'unfit'> {
    const id = quickEdit?.txId;
    if (!id) return 'failed';
    const res = await apiFetch(`/apps/webledger/transactions/${id}`, { token: client.token }).catch(() => undefined);
    if (!res?.ok) return 'failed';
    const { transaction } = await res.json() as { transaction: Parameters<typeof fitsQuickForm>[0] & {
      id: string; version: number; txDate: string; description: string;
      splits: Array<{ id: string; version: number; accountId: string; value: string; walletId?: string | null }> } };
    const fit = fitsQuickForm(transaction);
    if (!fit) return 'unfit';
    // Only if the form is still on the same entry: the reader may have
    // stepped on during the await.
    if (quickEdit?.txId !== id) return 'failed';
    paintQuickFields(transaction, fit);
    quickEdit = { ...quickEdit, baseVersion: transaction.version, ...splitRefs(transaction.splits, fit.moneyIndex) };
    quickOpenedWith = quickSnapshot();
    renderQuickPreview();
    return 'ok';
  }

  /** What differs between the kept draft and the form now — the strips, and how many. */
  function quickHeldDiffs(): HeldDifference[] {
    if (!quickHeld) return [];
    const now = buildQuickEntry();
    if (typeof now === 'string') return [];
    const accountName = (id: string): string => allAccounts.find((a) => a.id === id)?.name ?? id.slice(0, 8);
    return heldDifferences(quickHeld.payload as QueuedTransactionPayload,
      now.body as unknown as QueuedTransactionPayload, accountName)
      .map((d) => ({ ...d, label: d.label === 'Description' ? 'Note' : d.label === 'Split' ? 'Amount and accounts' : d.label }));
  }

  /**
   * The notice, the banner and the strips — everything the short form says
   * about whose entry this is. Returns how many kept values still differ.
   */
  function paintQuickHold(): number {
    const notice = el('wlqHoldNotice');
    const view = quickHold?.view;
    if (quickEdit && view && quickTheirs()) {
      notice.hidden = false;
      notice.innerHTML = holdNoticeHtml(view, 'entry', 'wlqTakeOverBtn');
    } else {
      notice.hidden = true;
      notice.innerHTML = '';
    }

    const banner = el('wlqHeldBanner');
    const strips = el('wlqHeldFields');
    const diffs = quickHeldDiffs();
    const readOnly = quickTheirs();
    const who = quickStolenBy;
    if (quickHeld && diffs.length > 0) {
      const mine = diffs.length === 1 ? '1 unsaved change' : `${diffs.length} unsaved changes`;
      banner.className = 'alert alert-warning py-2 px-3 small';
      banner.hidden = false;
      banner.innerHTML =
        `${who ? `${presenceDotHtml(who)} <strong>${escapeHtml(who.name)}</strong>` : 'Someone'} took this entry over. `
        + `Your ${mine} ${diffs.length === 1 ? 'is' : 'are'} kept — nothing was lost, and the form shows what `
        + `${who ? escapeHtml(who.name) : 'they'} ${readOnly ? 'is doing, live' : 'did'}.`
        + (readOnly
          ? ' <button type="button" class="btn btn-sm btn-warning ms-2" id="wlqTakeBackBtn">Take it back</button>'
          : ' <button type="button" class="btn btn-sm btn-warning ms-2" id="wlqKeepAllMineBtn">Keep all mine</button>')
        + ' <button type="button" class="btn btn-sm btn-outline-secondary ms-1" id="wlqDiscardMineBtn">Discard mine</button>';
      strips.innerHTML = diffs.map((d) => '<div class="wl-held">'
        + `<span aria-hidden="true">⚠</span><b>${escapeHtml(d.label)}, yours</b>`
        + (who && readOnly ? `<span class="wl-held__who">${presenceDotHtml(who)} ${escapeHtml(who.name)} has it</span>` : '')
        + `<span class="wl-held__v" title="${escapeHtml(d.mine)}">${escapeHtml(d.mine)}</span>`
        // "Keep mine" only means anything on a form you can type into.
        + (readOnly ? '' : `<button type="button" class="btn btn-warning" data-qkeep="${escapeHtml(d.where)}">Keep mine</button>`)
        + '</div>').join('');
    } else if (quickTookFrom) {
      banner.className = 'alert alert-info py-2 px-3 small';
      banner.hidden = false;
      banner.innerHTML = `${presenceDotHtml(quickTookFrom)} You took this entry over from `
        + `<strong>${escapeHtml(quickTookFrom.name)}</strong>, who was part-way through an edit. `
        + 'They can see what you type, and can take it back.';
      strips.innerHTML = '';
    } else {
      banner.hidden = true;
      banner.innerHTML = '';
      strips.innerHTML = '';
    }
    return quickHeld ? diffs.length : 0;
  }

  /** The kept draft has been applied or thrown away: stop holding it. */
  async function releaseQuickHeld(): Promise<void> {
    if (quickHeld) await client.outbox.removeDocument(quickHeld.id);
    quickHeld = undefined;
    quickStolenBy = undefined;
    paintQuickHold();
  }

  /**
   * Somebody took this entry over while it was open here (or had it when a
   * save went to ask). Snapshot first, flip second: once the form is
   * read-only their changes start arriving in these controls, and anything
   * not captured by then is gone (ADR-0003, 2026-09-12). Only a draft that
   * differs is kept — taking over from somebody who typed nothing leaves
   * nothing behind.
   */
  async function keepQuickDraftTakenBy(txId: string, by: Holder): Promise<void> {
    if (!quickEdit || quickEdit.txId !== txId) return;
    const taker = quickPerson(by);
    quickStolenBy = by.userId === client.userId
      ? { id: by.userId, name: 'Your other tab', initials: '↔', colorHex: '#8a8f98' }
      : taker;
    quickTookFrom = undefined;
    const built = buildQuickEntry();
    if (typeof built !== 'string' && quickSnapshot() !== quickOpenedWith) {
      const { leaseId: _token, ...payload } = built.body;
      quickHeld = await client.outbox.enqueueDocument({
        entity: 'transaction', recordId: txId, op: 'update', payload,
        clock: client.session.nextClock(),
        held: { takenBy: by.userId, takenByName: quickStolenBy?.name ?? 'Someone', at: new Date().toISOString() },
      });
    }
    setQuickReadOnly(true);
    if (await reloadQuick() === 'unfit') { handOver(false)(); return; }
    if (paintQuickHold() === 0 && quickHeld) await releaseQuickHeld();
    const name = quickStolenBy?.name ?? 'Someone';
    client.notify(quickHeld ? `${name} took this entry over. Your unsaved changes are kept.` : `${name} took this entry over.`);
  }

  /** Take over, or take back: always offered, because losing it is survivable (2026-09-12). */
  async function quickTakeOver(): Promise<void> {
    if (!quickHold) return;
    const before = quickHold.view;
    const outcome = await quickHold.takeOver();
    if (!outcome.ok) { paintQuickHold(); return; }
    const reloaded = await reloadQuick();
    if (reloaded === 'unfit') { handOver(false)(); return; }
    setQuickReadOnly(false);
    if (quickHeld) {
      paintQuickHold();
      client.notify('You have this entry back. Your kept changes are offered beside the current values — keep the ones you want, then save.');
      return;
    }
    if (before.kind === 'theirs' && !before.sameUser) quickTookFrom = quickPerson(before.holder);
    paintQuickHold();
    client.notify(quickTookFrom
      ? `You took this entry over from ${quickTookFrom.name}. They keep their unsaved changes and can take it back.`
      : 'This entry is yours to edit now.');
  }

  /** It came free while this form watched, and this tab has it now: reload, editable. */
  async function quickPickUpFreed(): Promise<void> {
    if (quickTimeline?.viewing() !== undefined) return;
    const reloaded = await reloadQuick();
    if (reloaded === 'unfit') { handOver(false)(); return; }
    setQuickReadOnly(false);
    paintQuickHold();
    client.notify(quickHeld
      ? 'You have this entry back. Your kept changes are offered beside the current values.'
      : 'This entry is editable again.');
  }

  /** Put one kept value back into the form — `txDate`, `description`, a leg — or `all` of them. */
  async function applyQuickHeld(where: string): Promise<void> {
    if (!quickHeld || quickTheirs()) return;
    const draft = queuedPayloadToDto(quickHeld.payload);
    if (where === 'txDate') {
      el<HTMLInputElement>('wlqDate').value = draft.txDate;
    } else if (where === 'description') {
      el<HTMLInputElement>('wlqNote').value = draft.description;
    } else {
      // The legs — or everything: the amount, the accounts and the wallet
      // are one leg pair on this form, and only make sense together.
      const shape = where === 'all'
        ? draft
        : { ...draft, txDate: el<HTMLInputElement>('wlqDate').value, description: el<HTMLInputElement>('wlqNote').value };
      const fit = fitsQuickForm(shape);
      if (fit) paintQuickFields(shape, fit);
    }
    refreshQuickForm();
    renderQuickPreview();
    if (paintQuickHold() === 0) {
      await releaseQuickHeld();
      client.notify('Your changes are back in the form. Save to keep them.');
    }
  }

  async function releaseQuickLease(): Promise<void> {
    // `false`: the form is being let go of, so there is nothing to put back —
    // and putting it back would re-fetch a transaction nobody is looking at.
    quickTimeline?.close(false);
    quickTimeline = undefined;
    setQuickReadOnly(false);
    // Emptied, not just hidden: this is an `aria-live` region, and leaving
    // last time's sentence in it means the next thing to unhide it announces
    // something that is no longer true.
    el('wlqNotice').hidden = true;
    el('wlqNotice').className = 'alert alert-info py-2 px-3 small';
    el('wlqNotice').innerHTML = '';
    if (!quickEdit) return;
    const id = quickEdit.txId;
    quickEdit = undefined;
    const held = quickHold;
    quickHold = undefined;
    // The kept draft stays in the outbox — it is the one thing nobody else
    // can reproduce — and comes back the next time the entry is opened.
    quickHeld = undefined;
    quickStolenBy = undefined;
    quickTookFrom = undefined;
    paintQuickHold();
    // Leave the presence roster before dropping the hold, so the row does
    // not briefly show a nameless holder on its way to showing nobody.
    client.session.closeRecord('transaction', id);
    held?.release();
    held?.dispose();
  }

  /**
   * Can the short form say everything this transaction says?
   *
   * Two legs, one currency, one of them money — and NOT two legs on the same
   * account, which is what a wallet transfer looks like. A transfer also owns
   * a `walletTransfer` row recording whether it was a loan, and editing its
   * legs through a form that knows nothing about that would leave the two
   * disagreeing. Those go to the full editor, which at least changes nothing
   * behind the reader's back.
   */
  /**
   * Can the short form say this transaction?
   *
   * Derived from the shape, deliberately, rather than read from a marker
   * stored on the transaction. A marker would be a second source of truth
   * for something the legs already state, and it would go stale the moment
   * anybody edited the transaction in the full editor or an importer wrote
   * one — at which point the form would open a transaction it cannot
   * actually represent, or refuse one it can. The shape cannot lie about
   * itself.
   *
   * The rule is the sign rule from `buildQuickEntry`, read backwards. One
   * leg is negative and one positive; the negative one is where the money
   * left. What the two ends ARE then names the form:
   *
   *   money out of an asset into an expense   Spend
   *   money out of income into an asset       Money in
   *   anything else, two legs                 Move
   *
   * Spend and Money in are preferred where they fit, because they ask two
   * questions instead of four; a Move is the general case and can say
   * everything, including the same account twice with different earmarks.
   */
  function fitsQuickForm(tx: {
    id?: string;
    currencyCode: string;
    splits: Array<{ id?: string; accountId: string; currencyCode: string; value?: string; walletId?: string | null }>;
  }): { kind: 'spend' | 'income' | 'move'; moneyIndex: number } | undefined {
    if (tx.splits.length !== 2) return undefined;
    const values = tx.splits.map((sp) => Number(sp.value ?? 0));
    const fromIndex = values[0]! < 0 ? 0 : values[1]! < 0 ? 1 : -1;
    // Two legs of the same sign is not a shape with a "from" and a "to", and
    // a pair of zeroes is not a transaction anybody typed here.
    if (fromIndex < 0 || values[0]! === values[1]!) return undefined;
    const toIndex = fromIndex === 0 ? 1 : 0;

    /*
     * ONE of the legs has to be in the transaction's own currency — either
     * one, and it no longer matters which.
     *
     * `value` is denominated in the transaction's currency, so the form can
     * only balance the pair if a leg is actually in it: that leg's amount IS
     * the value, and the other leg's amount is what landed. With neither in
     * it, the two amounts are a rate between two currencies and say nothing
     * about the third.
     *
     * This used to insist on the leaving leg, which rejected the mirror
     * image — money leaving in dollars and arriving in the currency the
     * entry is counted in, which is exactly how the importer wrote several
     * of them. Both legs carry their own currency now, so both ways round
     * are the same shape read from different ends.
     */
    if (tx.splits[0]!.currencyCode !== tx.currencyCode
      && tx.splits[1]!.currencyCode !== tx.currencyCode) return undefined;

    const types = tx.splits.map((sp) => allAccounts.find((a) => a.id === sp.accountId)?.type);
    if (types.some((t) => t === undefined)) return undefined;
    const isMoney = (i: number): boolean => types[i] === 'ASSET' || types[i] === 'LIABILITY';

    const sameAccount = tx.splits[0]!.accountId === tx.splits[1]!.accountId;
    const shape: { kind: 'spend' | 'income' | 'move'; moneyIndex: number } =
      !sameAccount && isMoney(fromIndex) && types[toIndex] === 'EXPENSE'
        ? { kind: 'spend', moneyIndex: fromIndex }
        : !sameAccount && types[fromIndex] === 'INCOME' && isMoney(toIndex)
          ? { kind: 'income', moneyIndex: toIndex }
          // Everything else the form can make: two accounts of any types, or
          // one account with the earmark moving inside it.
          : { kind: 'move', moneyIndex: fromIndex };

    /*
     * And each account has to hold the currency its own leg is in.
     *
     * The same test `buildQuickEntry` applies, asked before opening rather
     * than after pressing Save — because the two disagreed, and the way that
     * showed up was the worst available: the form opened, accepted an edit,
     * and then refused to save it with the reason only in the preview and the
     * screen-reader region. From the chair it was a Save button that did
     * nothing.
     *
     * Leg by leg rather than against the entry's currency: every tab has
     * "Arrives as", so dollars leaving and forints booked to the expense is a
     * spend this form can say.
     */
    const legs = tx.splits.map((sp, i) => ({
      account: allAccounts.find((a) => a.id === tx.splits[i]!.accountId),
      currencyCode: sp.currencyCode || tx.currencyCode,
    }));
    if (legs.some((l) => !l.account)) return undefined;
    if (!quickFormCanHold(legs as Array<{ account: AccountDto; currencyCode: string }>)) return undefined;
    return quickFormCanSayDebts(tx, shape) ? shape : undefined;
  }

  /**
   * Whether the short form can say this entry's debts between wallets.
   *
   * It has room for one, measured the way its tab measures it
   * (`quickDebtMove`). Saving rewrites the entry's debts to what the form
   * shows, so opening one it cannot show would quietly drop or change it on
   * the next save — and a debt nobody is reminded of is the one thing the
   * whole feature exists to prevent. Those go to the full editor.
   */
  function quickFormCanSayDebts(
    tx: Parameters<typeof fitsQuickForm>[0], shape: { kind: QuickKind; moneyIndex: number },
  ): boolean {
    if (!tx.id) return true;
    const debts = debtsOfTx(tx.id);
    if (debts.length === 0) return true;
    if (debts.length > 1) return false;
    const wt = debts[0]!;
    const money = tx.splits[shape.moneyIndex]!;
    const other = tx.splits[shape.moneyIndex === 0 ? 1 : 0]!;
    const moneyWallet = money.walletId ?? null;
    // On either of the two legs: the one that left or the one that arrived,
    // which is the currency choice the form offers.
    const measured = !wt.amountSplitId || wt.amountSplitId === money.id || wt.amountSplitId === other.id;
    if (!measured) return false;
    if (shape.kind === 'move') {
      return wt.fromWalletId === moneyWallet && wt.toWalletId === (other.walletId ?? null);
    }
    // A spend says both ways round: "Paid for" (this wallet lent) and
    // "Borrowed from" (this wallet borrowed).
    if (shape.kind === 'spend') return wt.fromWalletId === moneyWallet || wt.toWalletId === moneyWallet;
    return wt.toWalletId === moneyWallet;
  }

  type QuickKind = 'spend' | 'income' | 'move';

  function quickKind(): QuickKind {
    const checked = root.querySelector<HTMLInputElement>('input[name="wlqKind"]:checked');
    return (checked?.value as QuickKind | undefined) ?? 'spend';
  }

  /** The currency the form is currently entering, which every leg will carry. */
  function quickCurrency(): string {
    return el<HTMLSelectElement>('wlqCcy').value || currencies[0]?.code || '';
  }

  /**
   * Whether an account can carry a leg in this currency.
   *
   * `isMulticurrency` is the account saying so itself. Otherwise its declared
   * currency is the only one it holds — and nothing on the server enforces
   * that (the flag governs whether the declared currency may be CHANGED once
   * there are transactions), so this is the client keeping the books honest
   * rather than the client being prevented.
   */
  function holdsCurrency(account: AccountDto, code: string): boolean {
    return account.isMulticurrency || account.currencyCode === code;
  }

  /**
   * Choosing an account the current currency does not suit moves the
   * currency to the account's own.
   *
   * The alternative was leaving the pickers filtered, which hid most of the
   * chart. This keeps every account reachable and still never produces an
   * entry whose legs claim a currency their account does not hold — you
   * pick a HUF account while it says EUR, and it says HUF.
   *
   * A multicurrency account is left alone: it holds whatever is chosen, so
   * there is nothing to correct and moving the currency would undo a choice
   * the user had just made deliberately.
   */
  function followAccountCurrency(selectId: string, accountId: string): void {
    const account = allAccounts.find((a) => a.id === accountId);
    if (!account || holdsCurrency(account, quickCurrency())) return;
    const select = el<HTMLSelectElement>('wlqCcy');
    if (!currencies.some((c) => c.code === account.currencyCode)) return;
    /*
     * The far end follows only when the near end could have sent it.
     *
     * Picking Car (forints) after paying from a dollar account is a
     * conversion, and moving the amount to forints would leave the dollar
     * account holding a currency it does not — the spend could then not be
     * said at all. "Arrives as" locks to the destination's currency instead.
     * Money in from a multi-currency source into a dollar account is not a
     * conversion anybody meant, so there the amount still follows.
     */
    if (selectId === destinationField()) {
      const from = allAccounts.find((a) => a.id === el<HTMLSelectElement>(sourceField()).value);
      if (from && !holdsCurrency(from, account.currencyCode)) return;
    }
    select.value = account.currencyCode;
  }

  /** The field holding the account the money leaves: the source on Money in, the money account otherwise. */
  function sourceField(): string {
    return quickKind() === 'income' ? 'wlqCategory' : 'wlqAccount';
  }

  /** The field holding the account the money arrives in. */
  function destinationField(): string {
    const kind = quickKind();
    return kind === 'move' ? 'wlqAccount2' : kind === 'income' ? 'wlqAccount' : 'wlqCategory';
  }

  /**
   * Whether "Arrives as" is asked on this tab.
   *
   * Always on a Move, where converting is half of what the tab is for. On
   * Spend and Money in only where the destination allows a choice — it can
   * hold several currencies, or holds one other than the amount's — so the
   * everyday forint spend into a forint category keeps its two questions.
   */
  function arrivesShown(): boolean {
    if (quickKind() === 'move') return true;
    const to = allAccounts.find((a) => a.id === el<HTMLSelectElement>(destinationField()).value);
    return Boolean(to && (to.isMulticurrency || to.currencyCode !== quickCurrency()));
  }

  /**
   * Accounts of these types. Every one of them, whatever the currency says.
   *
   * This filtered by the chosen currency for a while, so that picking USD
   * offered only the accounts a USD entry could land in. It made the picker
   * a different thing from the full editor's — most of the chart simply
   * absent, with nothing to say why, and a tree too short to scroll. Hiding
   * an account is a worse answer than explaining it.
   *
   * What keeps the books honest instead is `onPick`: choose an account that
   * does not hold the current currency and the currency moves to the
   * account's, rather than the account being unavailable or the entry
   * becoming unsaveable.
   */
  function accountsOfType(types: readonly string[]): AccountDto[] {
    return allAccounts
      .filter((a) => types.includes(a.type) && !a.isPlaceholder)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function fillAccountSelect(id: string, types: readonly string[]): void {
    const select = el<HTMLSelectElement>(id);
    const keep = select.value;
    select.innerHTML = accountsOfType(types)
      .map((a) => `<option value="${escapeHtml(a.id)}">${escapeHtml(a.name.trim())}`
        + `${types.length > 1 ? '' : ` (${escapeHtml(a.currencyCode)})`}</option>`).join('');
    if (keep) select.value = keep;
    // The select is hidden; the button is what anyone actually reads.
    paintAccountButton(id);
    pickableByField.set(id, pickableAccounts(types));
  }

  /**
   * What the tree offers for one field: the accounts it may hold, plus the
   * folders above them.
   *
   * The folders are not decoration. `buildAccountTree` re-roots anything whose
   * parent is missing from the list it is given, so filtering to the bookable
   * leaves alone would turn the chart into a flat list of names — which is the
   * `<select>` this control exists to replace, with a worse layout.
   *
   * Filtered rather than showing the whole chart, because a field that offers
   * a choice it cannot accept is a trap: "Paid from" holds money accounts, and
   * picking an expense account there could only be ignored.
   */
  function pickableAccounts(types: readonly string[]): AccountDto[] {
    const byId = new Map(allAccounts.map((a) => [a.id, a] as const));
    const keep = new Map<string, AccountDto>();
    for (const account of accountsOfType(types)) {
      keep.set(account.id, account);
      const seen = new Set<string>();
      let parentId = account.parentId;
      while (parentId && !seen.has(parentId)) {
        seen.add(parentId);
        const parent = byId.get(parentId);
        if (!parent) break;
        keep.set(parent.id, parent);
        parentId = parent.parentId;
      }
    }
    return [...keep.values()];
  }

  /** Icon, full path and title on the button, from whatever the select holds. */
  function paintAccountButton(selectId: string): void {
    const btn = root.querySelector<HTMLElement>(`[data-acct-btn="${selectId}"]`);
    if (!btn) return;
    const id = el<HTMLSelectElement>(selectId).value;
    const account = allAccounts.find((a) => a.id === id);
    const short = account ? accountShortLabel(allAccounts, account.id) : undefined;
    const path = short?.full ?? '';
    const name = btn.querySelector<HTMLElement>('[data-acct-name]')!;
    name.innerHTML = short ? accountLabelHtml(short.label) : 'Choose account…';
    name.classList.toggle('wl-acct-btn__name--empty', !account);
    btn.querySelector<HTMLElement>('[data-acct-icon]')!.innerHTML =
      account ? inlineAccountIcon(allAccounts, account.id) : '';
    btn.title = path;
    btn.setAttribute('aria-label', account ? `Account: ${path}` : 'Choose an account');
  }

  /** The tree's contents per field, rebuilt whenever that field's list is. */
  const pickableByField = new Map<string, AccountDto[]>();

  /** Every type there is — what a Move offers, because a move can be anything to anything. */
  const ALL_TYPES = ['ASSET', 'LIABILITY', 'INCOME', 'EXPENSE', 'EQUITY'] as const;

  function refreshQuickForm(): void {
    const kind = quickKind();
    fillQuickCurrencies();
    /*
     * Spend and Money in know what belongs on each side, so they narrow the
     * pickers to it. A Move does not and should not: "from here to there" is
     * the whole of what it claims, and which of those is an asset, a
     * category or an equity account is the ledger's business rather than a
     * question to put to the person typing.
     *
     * The signs work out the same either way, which is the part that looked
     * like it would need account-type logic and does not. A leg the money
     * LEAVES is negative and a leg it ARRIVES in is positive, for every type
     * there is: spending is asset negative / expense positive, income is
     * income negative / asset positive, borrowing is liability negative /
     * asset positive. Double entry already encodes the direction in the
     * sign, so the form only has to know which end is which.
     */
    fillAccountSelect('wlqAccount', kind === 'move' ? ALL_TYPES : ['ASSET', 'LIABILITY']);
    fillAccountSelect('wlqAccount2', ALL_TYPES);
    fillAccountSelect('wlqCategory', kind === 'income' ? ['INCOME'] : ['EXPENSE']);

    const options = orderWallets(data.wallets).map(({ wallet }) =>
      `<option value="${escapeHtml(wallet.id)}">${escapeHtml(walletPath(data.wallets, wallet.id))}</option>`).join('');
    for (const id of ['wlqWallet', 'wlqWallet2']) {
      const select = el<HTMLSelectElement>(id);
      const keep = select.value;
      // "Not earmarked" is a real answer at either end: money can arrive
      // somewhere without being spoken for, and a move can be the act of
      // un-earmarking it.
      select.innerHTML = `<option value="">— not earmarked —</option>${options}`;
      if (keep) select.value = keep;
    }

    el('wlqAccountLbl').textContent =
      kind === 'spend' ? 'Paid from' : kind === 'income' ? 'Received into' : 'From account';
    el('wlqWalletLbl').textContent = kind === 'move' ? 'From wallet' : 'Wallet';
    el('wlqCategoryLbl').textContent = kind === 'income' ? 'Source' : 'Category';
    el('wlqCategoryWrap').hidden = kind === 'move';
    el('wlqAccount2Wrap').hidden = kind !== 'move';
    el('wlqWallet2Wrap').hidden = kind !== 'move';
    paintMoveExtras();
    renderQuickTags();
    renderQuickPreview();
  }

  /**
   * The currency choices, and which one is chosen.
   *
   * Every currency in the book is offered. What narrows things is the other
   * direction: the account pickers are filtered to accounts that can hold
   * whatever is chosen here, so the choice is always one that leads
   * somewhere rather than one that has to be taken back.
   */
  function fillQuickCurrencies(): void {
    const select = el<HTMLSelectElement>('wlqCcy');
    const keep = select.value;
    select.innerHTML = currencies
      .map((c) => `<option value="${escapeHtml(c.code)}">${escapeHtml(c.code)}</option>`).join('');
    select.value = keep && currencies.some((c) => c.code === keep) ? keep : commonestCurrency();
  }

  /**
   * What to start on: the currency most of the money accounts are kept in.
   *
   * The list is in whatever order the book stores its currencies, which here
   * is alphabetical — so the untouched default was AUD, a currency this book
   * has no account in at all. Counting the accounts asks the ledger what it
   * is mostly denominated in, which is the right guess and needs nothing
   * remembered or configured.
   */
  function commonestCurrency(): string {
    const tally = new Map<string, number>();
    for (const account of allAccounts) {
      if (account.isPlaceholder) continue;
      if (account.type !== 'ASSET' && account.type !== 'LIABILITY') continue;
      tally.set(account.currencyCode, (tally.get(account.currencyCode) ?? 0) + 1);
    }
    const best = [...tally.entries()].sort((a, b) => b[1] - a[1])[0];
    return best?.[0] ?? currencies[0]?.code ?? '';
  }

  interface QuickEntry {
    /**
     * `amount` is what the account sees and what the preview shows; `value`
     * is what the transaction balances on. They differ only on the arriving
     * leg of a conversion, and keeping both is what lets the preview show
     * "-100.00 EUR / +38,500.00 HUF" AND still say truthfully that it
     * balances — summing the visible figures would call every conversion
     * broken.
     */
    legs: Array<{ name: string; accountId: string; amount: number; value: number; ccy: string; wallet: string | null }>;
    /** The debt between wallets in words, or empty when there is none. */
    debtSays: string;
    /** The debt as figures, for the shortfall warning: it may be what covers it. */
    /** `part` of a leg of `of`, in `ccy` — the leg the debt is measured on. */
    debt?: { kind: string; fromWalletId: string; toWalletId: string; part: number; of: number; ccy: string } | undefined;
    body: Record<string, unknown>;
  }

  /** Returns the entry, or a sentence saying why there isn't one yet. */
  function buildQuickEntry(): QuickEntry | string {
    const kind = quickKind();
    const amount = Number(el<HTMLInputElement>('wlqAmount').value.replace(/[\s\u202f,]/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) return 'Enter an amount.';
    const ccy = quickCurrency();
    if (!ccy) return 'Choose a currency.';
    const account = allAccounts.find((a) => a.id === el<HTMLSelectElement>('wlqAccount').value);
    if (!account) return 'Choose an account.';
    // The money account sends the amount on a Spend or a Move; on Money in it
    // is where the money arrives, and is checked against that below.
    if (kind !== 'income' && !holdsCurrency(account, ccy)) return `${account.name.trim()} does not hold ${ccy}.`;
    const walletId = el<HTMLSelectElement>('wlqWallet').value || null;
    const note = el<HTMLInputElement>('wlqNote').value.trim();
    const date = el<HTMLInputElement>('wlqDate').value || todayIso();

    let other: AccountDto | undefined;
    let otherWallet: string | null = null;
    if (kind === 'move') {
      other = allAccounts.find((a) => a.id === el<HTMLSelectElement>('wlqAccount2').value);
      if (!other) return 'Choose where the money is going.';
      otherWallet = el<HTMLSelectElement>('wlqWallet2').value || null;
      /*
       * Same account, same wallet — and nothing moved, UNLESS the currency
       * did.
       *
       * A conversion inside one account is the exception this guard did not
       * know about: CHF 100 becoming Ft.41,070 in the same place is an event
       * worth recording, and the ledger already holds several written by the
       * importer. Refusing it meant such an entry could be opened on this
       * form and then never saved — including a save that changed only the
       * note or the date.
       */
      if (movesNothing(other.id === account.id, otherWallet === walletId, targetCurrency() !== ccy)) {
        return 'Pick a different account or a different wallet — otherwise nothing moves.';
      }
      /*
       * A parent's balance already contains its child's, so "Renovation
       * lends to Renovation / 68" is not a loan, it is the same money
       * counted twice. The rule the Cover flow applies to its own list.
       */
      if (walletId && otherWallet) {
        const kin = new Set([
          ...ancestorIds(data.wallets, otherWallet), ...descendantIds(data.wallets, otherWallet),
        ]);
        if (kin.has(walletId)) {
          return 'Those two wallets are one inside the other, so this would be the same money twice.';
        }
      }
    } else {
      other = allAccounts.find((a) => a.id === el<HTMLSelectElement>('wlqCategory').value);
      if (!other) return kind === 'income' ? 'Choose a source.' : 'Choose a category.';
      if (kind === 'income' && !holdsCurrency(other, ccy)) return `${other.name.trim()} does not hold ${ccy}.`;
    }

    /*
     * The first account is the one the money LEAVES, except for Money in,
     * where it is the one it arrives in. That is the whole sign rule, and it
     * holds for every account type — which is why a Move needs no per-type
     * logic (see `refreshQuickForm`).
     *
     * A Move used to take `+1` here while its field said "From account", so
     * the money arrived in the account it claimed to be leaving. Nothing
     * caught it because both legs were money accounts and the pair still
     * balanced: the transaction was valid and backwards. It matters more now
     * that a Move also carries the wallets and the record of who owes whom.
     */
    const moneySign = kind === 'income' ? 1 : -1;

    /*
     * A conversion: one economic event, two currencies, and the rate is the
     * pair of amounts rather than a number stored anywhere.
     *
     * `value` is in the TRANSACTION's currency and is what has to balance;
     * `amount` is in the account's own. So the arriving leg carries `value`
     * equal and opposite to the leg that left — the transaction balances in
     * the currency it is denominated in — and an `amount` of whatever
     * actually landed. Those two numbers on that one leg are the rate, and
     * reading them back is how the form reopens this.
     */
    const target = targetCurrency();
    let targetAmount = amount;
    if (target !== ccy) {
      targetAmount = Number(el<HTMLInputElement>('wlqTargetAmount').value.replace(/[\s\u202f,]/g, ''));
      if (!Number.isFinite(targetAmount) || targetAmount <= 0) {
        return `Enter how much arrives in ${target}.`;
      }
    }
    // Where it arrives has to hold what arrives — on every tab now, since
    // every tab can say a conversion.
    const destination = kind === 'income' ? account : other;
    if (!holdsCurrency(destination, target)) return `${destination.name.trim()} does not hold ${target}.`;

    /*
     * The transaction's own currency, and what the legs balance in.
     *
     * On an edit it is whatever the record already says. Re-denominating a
     * transaction somebody opened to fix a note would rewrite what every
     * stored `value` means, so the form never does it — the decision taken
     * when this was designed, and the reason this is read rather than
     * computed. On a new entry there is nothing to preserve and the money
     * leg's currency is it.
     *
     * `value` is in that currency on BOTH legs, so it can only come from a
     * leg that is actually denominated in it. One always is: either the
     * money leaving or the money arriving. If neither were, the pair of
     * amounts would be a rate between two currencies and say nothing about
     * the third, and the transaction could not be balanced at all — so that
     * is refused rather than guessed.
     */
    const txCcy = quickEdit?.currencyCode || ccy;
    const balancing = balancingAmount(
      { ccy, amount }, { ccy: target, amount: targetAmount }, txCcy,
    );
    if (balancing === undefined) {
      return `Neither side is in ${txCcy}, which is what this entry is counted in.`;
    }

    const signed = quickLegs(kind, { amount, ccy }, { amount: targetAmount, ccy: target });
    const moneyLeg = { accountId: account.id, amount: signed.money.amount, walletId };
    const otherLeg = { accountId: other.id, amount: signed.other.amount, walletId: otherWallet };
    const moneyValue = moneySign * balancing;

    const fixed = (n: number): string => n.toFixed(4);
    // What each leg is denominated in. Each carries its OWN currency now:
    // the money may leave in one and arrive in another, either way round.
    const legCcy = (isOther: boolean): string => (isOther ? signed.other.ccy : signed.money.ccy);
    // Minted before the body, because the debt names the leg it is measured
    // on. On an edit the legs keep their ids.
    const legIds = {
      money: quickEdit?.money.id ?? crypto.randomUUID(),
      other: quickEdit?.other.id ?? crypto.randomUUID(),
    };

    /*
     * The debt between wallets, if one is asked for: a wallet to owe it, and
     * no more than the leg it is measured on. The server holds the same line;
     * saying it here keeps Save from being a button that silently declines.
     */
    const asked = quickDebtAsked();
    const debt = quickDebtMove(kind, asked, { money: walletId, other: otherWallet }, legIds);
    if (asked.kind && kind !== 'move' && !asked.forWallet) {
      return kind !== 'spend' ? 'Say which wallet the money belongs to.'
        : debtChoice(asked.kind).inward ? 'Say which wallet it was borrowed from.' : 'Say which wallet it was paid for.';
    }
    let debtSays = '';
    let debtFigures: QuickEntry['debt'];
    if ('fromWalletId' in debt) {
      const legSize = Math.abs(debt.amountSplitId === legIds.money ? signed.money.amount : signed.other.amount);
      const legCode = debt.amountSplitId === legIds.money ? signed.money.ccy : signed.other.ccy;
      const part = debt.amount === null ? legSize : Number(debt.amount);
      if (!Number.isFinite(part) || part <= 0) return 'How much is owed has to be more than zero \u2014 or empty for all of it.';
      if (part > legSize + 0.00005) {
        return `No more than ${formatAmount(legSize, legCode)} ${legCode} can be owed \u2014 that is all there is.`;
      }
      // What is recorded, plainly. Whether it pays back, lends or both is
      // the helper's to say under the debt row, from where the two stood.
      debtSays = `${walletPath(data.wallets, debt.fromWalletId)} \u2192 ${walletPath(data.wallets, debt.toWalletId)}: `
        + `${formatAmount(part, legCode)} ${legCode}, `
        + (debt.kind === 'grant' ? 'handed over for good' : 'owed back');
      debtFigures = {
        kind: debt.kind, fromWalletId: debt.fromWalletId, toWalletId: debt.toWalletId, part, of: legSize, ccy: legCode,
      };
    }

    return {
      debtSays,
      debt: debtFigures,
      legs: [
        {
          name: account.name.trim(), accountId: account.id, amount: moneyLeg.amount, value: moneyValue,
          ccy: legCcy(false), wallet: walletId,
        },
        {
          name: other.name.trim(), accountId: other.id, amount: otherLeg.amount, value: -moneyValue,
          ccy: legCcy(true), wallet: otherWallet,
        },
      ],
      body: {
        // Members of this same save, so the entry, its tags and the record of
        // whose money it became land together or not at all (ADR-0021).
        tags: tagSelectionMembers(quickTagSelection),
        ...walletMoveBody(kind, walletId, otherWallet, legIds),
        id: quickEdit ? quickEdit.txId : crypto.randomUUID(),
        txDate: date,
        reference: '',
        description: note || (kind === 'move' ? `${account.name.trim()} → ${other.name.trim()}` : other.name.trim()),
        currencyCode: txCcy,
        ...(quickEdit
          ? { baseVersion: quickEdit.baseVersion, ...(quickLeaseId() ? { leaseId: quickLeaseId() } : {}) }
          : {}),
        // On an edit the legs keep their ids, so this updates the two rows
        // that are there rather than adding two more beside them.
        splits: [
          { leg: moneyLeg, ref: quickEdit?.money, id: legIds.money },
          { leg: otherLeg, ref: quickEdit?.other, id: legIds.other },
        ].map(({ leg, ref, id }, index) => ({
          id,
          ...(ref ? { op: 'update', baseVersion: ref.version } : { op: 'create' }),
          accountId: leg.accountId,
          memo: '',
          // `value` balances the transaction and is always in ITS currency;
          // `amount` is what the account actually saw, in its own.
          value: fixed(index === 1 ? -moneyValue : moneyValue),
          amount: fixed(leg.amount),
          currencyCode: legCcy(index === 1),
          walletId: leg.walletId,
        })),
      },
    };
  }

  function renderQuickPreview(): void {
    paintQuickDebtHelp();
    const built = buildQuickEntry();
    const preview = el('wlqPreview');
    const warn = el('wlqWarn');
    const status = el('wlqStatus');
    if (typeof built === 'string') {
      status.innerHTML = `<p class="small text-muted mb-0">${escapeHtml(built)}</p>`;
      preview.innerHTML = '';
      el('wlqPreview').hidden = true;
      warn.innerHTML = '';
      return;
    }
    el('wlqPreview').hidden = false;
    const sum = built.legs.reduce((t, l) => t + l.value, 0);
    // Accounts first: each line's account before, the line, and after.
    const ledgerNow = datasetLedger(client.dataset);
    const at = { txDate: el<HTMLInputElement>('wlqDate').value || todayIso(), transactionId: quickEdit?.txId };
    preview.innerHTML = accountAssistantHtml(built.legs.map((l) => ({
      accountName: allAccounts.find((a) => a.id === l.accountId)
        ? accountShortLabel(allAccounts, l.accountId)?.full ?? l.name : l.name,
      currencyCode: l.ccy,
      before: Number(accountBalanceBefore(ledgerNow, l.accountId, l.ccy, at)),
      amount: l.amount,
      walletShort: l.wallet ? cellName(l.wallet) : undefined,
      walletName: l.wallet ? walletPath(data.wallets, l.wallet) : undefined,
    })), (n, code, notch, className) => chip(n, code, className, notch));
    // Always in view, open or not: whether it balances, and what it owes.
    status.innerHTML = `<div>${Math.abs(sum) < 0.00005 ? '✓ balances to zero' : '✗ does not balance'}</div>`
      + (built.debtSays ? `<div>\u21c4 ${escapeHtml(built.debtSays)}</div>` : '');

    // Would this put the wallet under? Said, never enforced — the same rule
    // the rest of this page follows, and for the same reason: entry works
    // offline, so the balance on this device may be stale.
    const moneyLeg = built.legs[0]!;
    if (moneyLeg.wallet && moneyLeg.amount < 0) {
      /*
       * What the wallet holds on the entry's OWN date, just before it — not
       * today. Asked from use: a conversion dated 8 May that left Renovation
       * holding 21,000 EUR was flagged "would put Renovation over", because
       * by October Renovation was short, while the log rightly marked
       * nothing on that row. The warning is about this entry, so it asks
       * this entry's moment, the way the log's W does.
       *
       * Without the entry itself (`walletBalanceBefore` leaves it out),
       * which also keeps an edit from counting its own withdrawal twice.
       */
      const kin = new Set([moneyLeg.wallet, ...descendantIds(data.wallets, moneyLeg.wallet)]);
      const plain = Number(walletBalanceBefore(ledgerNow, kin, moneyLeg.ccy, at));
      /*
       * And with "If debts were settled" on, as the log and the bar read it:
       * every debt line from the entries before this one, paid back. The
       * entry's own debt is left out of that the same way its legs are —
       * `enteredBefore` says no to the entry itself.
       */
      const earlier = enteredBefore(ledgerNow, at);
      const have = plain + walletShifts(moneyLeg.wallet, settled, data.wallets, debts)
        .filter((sh) => sh.currencyCode === moneyLeg.ccy && earlier(sh.transactionId))
        .reduce((t, sh) => t + Number(sh.delta), 0);
      // Every line of the entry that lands in this wallet and currency, not
      // only the first: a move between two accounts of one wallet takes
      // nothing out of it.
      const moved = built.legs
        .filter((l) => l.wallet && kin.has(l.wallet) && l.ccy === moneyLeg.ccy)
        .reduce((t, l) => t + l.amount, 0);
      // Money borrowed INTO this wallet for this payment is what covers it
      // (`debtShiftLegs` moves it), so it counts before the warning does.
      const d = built.debt;
      // Not with the switch on: settled, a loan is paid back, so what it
      // borrowed covers nothing — which is what the log will say after it.
      // In the payment's own currency, as its share: a debt owed in the
      // forints that arrived covers the same fraction of the euros that left
      // (`debtShiftLegs`).
      const borrowedIn = !settled && d && d.toWalletId === moneyLeg.wallet && d.of > 0
        ? (d.part / d.of) * Math.abs(moneyLeg.amount) : 0;
      const after = have + moved + borrowedIn;
      /*
       * And the way out, one click away: borrow exactly what is missing. On a
       * Spend only — that is where "we pay from A and borrow the rest from B"
       * is said. It sets "Borrowed from" and the amount; which wallet it
       * comes from is still the reader's to pick.
       */
      // Hidden with the switch on, where borrowing could not make it go away.
      const offer = quickKind() === 'spend' && !quickReadOnly && !settled
        ? ` <button type="button" class="btn btn-link btn-sm p-0 align-baseline" data-borrow-short="${
          (borrowedIn - after).toFixed(4)}">Borrow ${borrowedIn > 0 ? 'the rest' : 'it'} from another wallet</button>`
        : '';
      warn.innerHTML = after < -0.00005
        ? `<div class="wl-attn mt-2 small"><span class="badge text-bg-warning">Heads up</span> This would put `
          + `<strong>${escapeHtml(walletPath(data.wallets, moneyLeg.wallet))}</strong> `
          + `${formatAmount(-after, moneyLeg.ccy)}&nbsp;${escapeHtml(moneyLeg.ccy)} over on ${escapeHtml(at.txDate)}`
          + `${settled ? ', if every debt between wallets were settled' : ''}.${offer} Or save it as it is — `
          + `you will be asked who covered it, and you can answer later.</div>`
        : '';
    } else {
      warn.innerHTML = '';
    }
  }

  /**
   * What the short form says about its hold, beside Save: nothing while it
   * holds the entry, and whether it is still reserved while offline. A hold
   * no longer lapses (ADR-0003, 2026-10-22), so there is no "lost" state left
   * to explain — whose entry it is otherwise is `paintQuickHold`'s.
   */
  function paintQuickLeaseState(): void {
    // Restoring needs a lease, so the timeline's Restore buttons come and go
    // with one.
    if (quickTimeline?.isOpen()) quickTimeline.repaint();
    // Losing the hold takes the remove buttons with it, the same as Save.
    quickFiles.render();
    const box = el('wlqLease');
    const save = el<HTMLButtonElement>('wlqSave');
    save.disabled = false;
    if (!quickEdit) { box.innerHTML = ''; return; }
    const view = quickHold?.view;
    const refusedWhy = client.refusedEntries().find((r) => r.recordId === quickEdit!.txId)?.message;
    if (refusedWhy !== undefined) {
      box.innerHTML = '<div class="wl-attn small"><span class="badge text-bg-danger">Not accepted</span> '
        + `The server refused this version: ${escapeHtml(refusedWhy.replace(/\.\s*$/, ''))}. Change it and Save to send it again, `
        + 'or discard it from the note at the top of the page.</div>';
      return;
    }
    /*
     * ADR-0011: editing without a connection has to say whether the entry is
     * reserved, or it reads as reserved, like online. Since 2026-10-22 it can
     * be: a hold this tab had stays its own while the connection is away.
     */
    if (view?.kind === 'offline' || (quickEdit.offline && view?.kind !== 'mine')) {
      const reserved = view?.kind === 'offline' && view.held;
      const lead = quickEdit.unsent ? 'Not sent yet. ' : '';
      box.innerHTML = `<div class="wl-attn small"><span class="badge text-bg-secondary">${reserved ? 'Offline' : 'Not reserved'}</span> `
        + lead
        + (reserved ? 'Still reserved for you, unless somebody takes it over. ' : 'Not reserved for you. ')
        + 'Save keeps it on this device and sends it when the connection is back; if somebody '
        + 'changed it meanwhile, you will be asked which version to keep.</div>';
      return;
    }
    box.innerHTML = '';
  }

  /**
   * What the quick form held when it was filled, for telling "changed" from
   * "merely opened".
   *
   * Read off the controls, never off the entry the form would send: a body
   * is built for sending, so it mints ids for what it is about to create —
   * a wallet move among them — and two bodies built a second apart from an
   * untouched form differ in every one. Stepping asked to save changes
   * nobody had made, every time, on every entry carrying an earmark move.
   */
  let quickOpenedWith = '';

  function quickSnapshot(): string {
    return formSnapshot(el('wlQuickDlg'));
  }

  function paintQuickNav(): void {
    const nav = el('wlqNav');
    nav.hidden = quickEdit === undefined;
    quickOpenedWith = quickSnapshot();
    if (nav.hidden || !quickEdit) return;
    el<HTMLButtonElement>('wlqPrev').disabled =
      registerNeighbours.idOf(quickEdit.txId, OLDER) === undefined;
    el<HTMLButtonElement>('wlqNext').disabled =
      registerNeighbours.idOf(quickEdit.txId, YOUNGER) === undefined;
  }

  async function stepQuick(delta: -1 | 1): Promise<void> {
    if (!quickEdit) return;
    const goTo = registerNeighbours.idOf(quickEdit.txId, delta);
    if (goTo === undefined) return;
    if (quickSnapshot() !== quickOpenedWith) {
      // Two answers, neither of which discards anything: Cancel is already
      // what leaving on purpose means.
      const go = await askConfirm({
        title: 'Save your changes first?',
        message: 'This entry has changes that have not been saved. '
          + 'Cancel stays here and leaves them on the form.',
        confirm: 'Save and continue',
        cancel: 'Stay here',
      });
      if (!go) return;
      // Asked of the save itself rather than guessed at from the form
      // afterwards: a refusal can land as a page notice, as the panel's own
      // notice, or as nothing visible at all when the lease has lapsed, and
      // every one of those used to read here as success — so the step went
      // ahead and took the reason off the screen with it.
      if (!await saveQuickEntry()) return;
    }
    registerNeighbours.go(goTo);
  }

  /**
   * Say why the short form declined, where the person who pressed it is looking.
   *
   * `notify` alone writes to the shared `.cf-sr` region, which is visually
   * hidden — so a refused save was announced to a screen reader and to nobody
   * else. The preview says it too, but it was already saying it before the
   * click, so pressing Save changed nothing on screen at all. A button that
   * silently declines is indistinguishable from a broken one.
   *
   * Named for Save because that is where it was first needed, and used by
   * everything else in this form that can say no — a lapsed hold, an account
   * the field will not take. They all belong in the same box.
   */
  function refuseQuickSave(message: string): void {
    client.notify(message);
    const notice = el('wlqNotice');
    notice.className = 'alert alert-danger py-2 px-3 small';
    notice.innerHTML = `<span aria-hidden="true">\u26a0</span> ${escapeHtml(message)}`;
    notice.hidden = false;
  }

  /**
   * Keep the entry on this device and send it when the server can be reached
   * (ADR-0011, amendment of 2026-10-21).
   *
   * It shows at once — in the log, the totals and everywhere else that reads
   * the local copy — marked as waiting to be sent, and goes the moment there
   * is a connection, from whichever page is open. An edit carries the version
   * it was made on; if somebody changed the entry meanwhile it comes back for
   * review rather than overwriting them.
   *
   * Files cannot travel this way — they go to the server's disk, not into
   * the entry — so they are not kept, and that is said rather than implied.
   */
  async function queueQuickEntry(body: Record<string, unknown>, editing: QuickEdit | undefined): Promise<boolean> {
    // No lease travels with it: an offline write is guarded by its version.
    const { leaseId: _lease, ...payload } = body;
    void _lease;
    await client.queueTransaction(editing ? 'update' : 'create', payload as unknown as QueuedTransactionPayload);
    const lostFiles = !editing ? quickFiles.heldCount() : 0;
    currentTxId = String(payload['id']);
    const legs = (payload['splits'] as Array<{ id: string }>).map((l) => l.id);
    if (!currentSplitId || !legs.includes(currentSplitId)) currentSplitId = legs[0] ?? null;
    flashNext = true;
    await releaseQuickLease();
    el<HTMLInputElement>('wlqAmount').value = '';
    el<HTMLInputElement>('wlqNote').value = '';
    forgetTargetAmounts();
    closeQuickForm();
    client.notify('Saved on this device. It will be sent when the connection is back.');
    if (lostFiles > 0) {
      fail(`Saved on this device, without its ${lostFiles === 1 ? 'file' : `${lostFiles} files`}: `
        + 'attaching needs a connection. Attach them to the entry once it has been sent.');
    }
    await loadAll();
    return true;
  }

  async function saveQuickEntry(): Promise<boolean> {
    // This attempt gets to speak for itself; a refusal must never outlive the
    // thing it was about.
    if (el('wlqNotice').classList.contains('alert-danger')) {
      el('wlqNotice').hidden = true;
      el('wlqNotice').className = 'alert alert-info py-2 px-3 small';
      el('wlqNotice').innerHTML = '';
    }
    if (quickTimeline?.viewing() !== undefined) {
      refuseQuickSave('This is a past version. Use \u201cBack to current\u201d to edit it, '
        + 'or restore this version from the history.');
      return false;
    }
    let built = buildQuickEntry();
    if (typeof built === 'string') { refuseQuickSave(built); return false; }


    const editing = quickEdit;
    /*
     * A hold to save under (webledgerHold.ts). Normally this tab has it;
     * without a connection the entry is saved on this device and sent when
     * it can — asked: "we save locally and synchronise when coming online".
     * Refused means somebody else has it now: what was typed is kept beside
     * their version, exactly as when they take it over while it is open.
     */
    if (editing && !editing.offline && quickHold) {
      const ensured = await quickHold.ensure();
      if ('offline' in ensured) {
        editing.offline = true;
      } else if (!ensured.ok) {
        if (ensured.view.kind === 'theirs') {
          await keepQuickDraftTakenBy(editing.txId, ensured.view.holder);
        } else {
          refuseQuickSave('You may not change this entry.');
        }
        return false;
      } else if (!('leaseId' in built.body)) {
        // Built again, now that there is a hold to send with it.
        built = buildQuickEntry();
        if (typeof built === 'string') { refuseQuickSave(built); return false; }
      }
    }
    if (editing?.offline || (!editing && knownOffline())) return queueQuickEntry(built.body, editing);
    let res: Response;
    try {
      res = editing
        ? await apiFetch(`/apps/webledger/transactions/${editing.txId}`, {
          method: 'PUT', token: client.token, body: built.body,
        })
        : await apiFetch('/apps/webledger/transactions', {
          method: 'POST', token: client.token, body: built.body,
        });
    } catch {
      // Never reached the server. It used to throw out of here, leaving the
      // form open and saying nothing; now it is kept and sent later.
      return queueQuickEntry(built.body, editing);
    }
    if (!res.ok) {
      refuseQuickSave(await failureDetail(res, 'The entry could not be saved'));
      return false;
    }
    /*
     * Follow what was just saved. On a new entry that is a row the reader has
     * never seen, which is exactly the one worth pointing at — and the ids are
     * the client-minted ones already in the body (invariant #8).
     *
     * `splits[0]` is the money leg, by construction in `buildQuickEntry`, and
     * a money leg is what the register shows. On an edit the leg keeps its id,
     * so this also holds the reader on the same row they opened.
     */
    currentTxId = String(built.body['id']);
    // Saved on the server directly, so a refused unsent version of it is moot.
    await client.clearRefused(currentTxId);
    // Whatever a kept draft was proposing has now been decided, one way or
    // the other, by the values that just went to the server.
    if (quickHeld) await releaseQuickHeld();
    const legs = (built.body['splits'] as Array<{ id: string }>).map((l) => l.id);
    // Unless the reader is already on one of these legs. Editing a transfer
    // between two money accounts keeps BOTH its legs in the register, and
    // moving the marker to the other one after a save would answer "where was
    // I" with the wrong row.
    if (!currentSplitId || !legs.includes(currentSplitId)) currentSplitId = legs[0] ?? null;
    flashNext = true;
    // Files held on a new entry go now that it exists (`holdUntilSaved`).
    const fileFailed = !editing && quickFiles.heldCount() > 0 ? await quickFiles.sendHeld(currentTxId) : '';
    await releaseQuickLease();
    el<HTMLInputElement>('wlqAmount').value = '';
    el<HTMLInputElement>('wlqNote').value = '';
    forgetTargetAmounts();
    if (fileFailed) {
      /*
       * The entry is saved and a file is not. Back on the form — the saved
       * entry now, so its own files bar is there to try again — with the
       * reason in the form's box, rather than closing on a "Saved." that is
       * only half true.
       */
      await closeOpenTransactionForm();
      await afterWrite();
      const res = await apiFetch(`/apps/webledger/transactions/${currentTxId}`, { token: client.token })
        .catch(() => undefined);
      const body = res?.ok ? await res.json() as { transaction: Parameters<typeof openQuickEdit>[0] } : undefined;
      if (body && await openQuickEdit(body.transaction)) {
        refuseQuickSave(`Saved \u2014 but a file was not attached. ${fileFailed} Attach it again here.`);
      } else {
        fail(`Saved \u2014 but a file was not attached. ${fileFailed} Open the entry to attach it again.`);
      }
      return true;
    }
    closeQuickForm();
    client.notify(editing ? 'Updated.' : 'Saved.');
    await afterWrite();
    return true;
  }

  /**
   * The form's fields, from a transaction-shaped thing.
   *
   * Separate from opening because the history panel paints past versions
   * through it too, and a second copy of "which select holds the category"
   * would drift from this one the first time a field moved.
   */
  function paintQuickFields(
    tx: {
      id?: string; txDate: string; description: string; currencyCode: string;
      splits: Array<{
        id?: string; accountId: string; value: string; amount?: string; currencyCode?: string; walletId?: string | null;
      }>;
    },
    fit: { kind: 'spend' | 'income' | 'move'; moneyIndex: number },
  ): void {
    const money = tx.splits[fit.moneyIndex]!;
    const other = tx.splits[fit.moneyIndex === 0 ? 1 : 0]!;
    // Which end the money left and which it reached: the amount is the one,
    // "Arrives as" the other, on every tab (`quickLegs`).
    const sent = fit.kind === 'income' ? other : money;
    const arrived = fit.kind === 'income' ? money : other;

    /*
     * Kind and currency first, then rebuild, then fill.
     *
     * The order is not cosmetic. `refreshQuickForm` rebuilds every select
     * from the kind and the currency, and a value assigned before that is
     * assigned to a list that does not contain it yet — the assignment
     * silently becomes "", and the rebuild then leaves the field on its
     * first option. Two accounts both reading "Opening Balance" is what that
     * looks like from outside.
     */
    el<HTMLInputElement>(`wlq${fit.kind === 'spend' ? 'Spend' : fit.kind === 'income' ? 'Income' : 'Move'}`).checked = true;
    fillQuickCurrencies();
    // The sending leg's OWN currency, which is not always the transaction's:
    // an entry can leave in dollars and be counted in forints. The
    // transaction's own currency is kept on `quickEdit` and is what the save
    // puts back (the decision never to re-denominate an existing record).
    el<HTMLSelectElement>('wlqCcy').value = sent.currencyCode || tx.currencyCode;
    refreshQuickForm();

    el<HTMLSelectElement>('wlqAccount').value = money.accountId;
    if (fit.kind === 'move') el<HTMLSelectElement>('wlqAccount2').value = other.accountId;
    else el<HTMLSelectElement>('wlqCategory').value = other.accountId;
    el<HTMLSelectElement>('wlqWallet').value = money.walletId ?? '';
    el<HTMLSelectElement>('wlqWallet2').value = fit.kind === 'move' ? other.walletId ?? '' : '';

    // The debt between wallets, if this entry carries one — off the replica,
    // where the register reads it too. `fitsQuickForm` has already made sure
    // there is at most one and that it is one this tab can say.
    const move = tx.id ? debtOf(tx.id) : undefined;
    // Into this wallet on a spend is "Borrowed from" / "Grant from".
    const borrowed = fit.kind === 'spend' && move !== undefined && move.toWalletId === (money.walletId ?? null);
    const storedKind = move ? normalTransferKind(move.kind) : '';
    paintMoveExtras();
    el<HTMLSelectElement>('wlqXferKind').value = !borrowed ? storedKind
      : storedKind === 'grant' ? 'grant-in' : 'borrow';
    el<HTMLInputElement>('wlqXferAmount').value = move?.amount ? String(Number(move.amount)) : '';
    paintMoveExtras();
    if (move && fit.kind !== 'move') {
      el<HTMLSelectElement>('wlqXferFor').value = fit.kind === 'spend' && !borrowed ? move.toWalletId : move.fromWalletId;
    }
    // Which of the two amounts it is counted on; set again below, once the
    // arriving currency is painted and the picker has both to offer.
    const measure: 'sent' | 'arrived' = !move?.amountSplitId ? defaultDebtMeasure(fit.kind)
      : move.amountSplitId === sent.id ? 'sent' : 'arrived';
    el<HTMLSelectElement>('wlqXferCcy').value = measure;

    /*
     * A conversion arrives as a different currency, and the arriving leg
     * says how much: its `value` balances the transaction in the
     * transaction's currency while its `amount` is what that account saw.
     * Reading the second number back is what makes a conversion something
     * this form can open as well as create.
     */
    /*
     * The arriving side, in its own currency — set before `paintMoveExtras`,
     * which rebuilds the picker and clears the amount under it whenever the
     * currency changes.
     */
    paintMoveExtras();
    if (arrivesShown() && arrived.currencyCode) paintTargetCurrency(arrived.currencyCode);
    el<HTMLInputElement>('wlqTargetAmount').value =
      toFixedFor(Math.abs(Number(arrived.amount ?? arrived.value)), arrived.currencyCode || tx.currencyCode);

    // The selects are hidden, so filling them paints nothing on its own.
    for (const id of ['wlqAccount', 'wlqAccount2', 'wlqCategory']) paintAccountButton(id);
    // To the currency's own decimals, as the split table renders them: an
    // amount that comes back as "4467.9" reads as a different number from
    // the "4,467.90" beside it in the register.
    // The money leg's own amount in its own currency — `value` is the
    // transaction-currency figure and is only the same number when the two
    // currencies are.
    el<HTMLInputElement>('wlqAmount').value =
      toFixedFor(Math.abs(Number(sent.amount ?? sent.value)), sent.currencyCode || tx.currencyCode);
    el<HTMLInputElement>('wlqNote').value = tx.description ?? '';
    el<HTMLInputElement>('wlqDate').value = tx.txDate;
    paintDebtCurrency();
    el<HTMLSelectElement>('wlqXferCcy').value = measure;
    renderQuickPreview();
  }

  /** This transaction's debts between wallets, off the replica. */
  function debtsOfTx(txId: string): WalletTransferRow[] {
    return datasetLedger(client.dataset).walletTransfers().filter((wt) => wt.transactionId === txId);
  }

  /** Its one debt between wallets, if it has exactly one. */
  function debtOf(txId: string): WalletTransferRow | undefined {
    const all = debtsOfTx(txId);
    return all.length === 1 ? all[0] : undefined;
  }

  /**
   * The short form, showing a past version.
   *
   * Never `disabled` (invariant 4) — every control keeps its place in the tab
   * order and its value readable, which is the whole point of looking at an
   * old version. Save goes away rather than going grey: there is no version
   * of "save" that means anything here, and a greyed button invites a click
   * that will never work.
   */
  function setQuickReadOnly(on: boolean): void {
    quickReadOnly = on;
    for (const id of ['wlqAmount', 'wlqNote', 'wlqDate']) applyFieldReadOnly(el(id), on);
    applyFieldReadOnly(el('wlqXferAmount'), on);
    for (const id of ['wlqAccount', 'wlqAccount2', 'wlqCategory', 'wlqWallet', 'wlqWallet2', 'wlqCcy', 'wlqXferKind', 'wlqXferFor', 'wlqXferCcy']) {
      applySelectReadOnly(el<HTMLSelectElement>(id), on);
    }
    for (const id of ['wlqAccountBtn', 'wlqAccount2Btn', 'wlqCategoryBtn']) {
      applyButtonReadOnly(root.querySelector<HTMLElement>(`#${id}`), on);
    }
    el('wlqSave').hidden = on;
    quickFiles.render();
    // The kind switches rewrite which accounts the form is asking for, which
    // is an edit however it is spelled.
    root.querySelectorAll<HTMLInputElement>('input[name="wlqKind"]')
      .forEach((r) => { r.onclick = on ? (e) => e.preventDefault() : null; });
    renderQuickTags();
  }

  /**
   * The parts of a Move that only make sense once you can see both ends.
   *
   * "Whose money is it now" is a question about EARMARKS, not accounts:
   * moving cash from one bank account to another changes nothing about who
   * it belongs to, and asking would be noise. It appears exactly when the
   * money is spoken for at both ends and those ends differ, which is the
   * only case where somebody can come to owe somebody.
   */
  /**
   * The amount inputs' step, from the currency each is denominated in.
   *
   * The same control the split table uses: a number input with no spinners,
   * right-aligned, stepping by the smallest amount the currency has. Without
   * the step a two-decimal amount is reported invalid the moment somebody
   * types the cents, and with a fixed `0.01` a zero-decimal currency invites
   * a fraction it cannot hold.
   */
  /** A figure written out with the decimals its currency actually has. */
  function toFixedFor(value: number, code: string): string {
    return value.toFixed(currencies.find((c) => c.code === code)?.fraction ?? 2);
  }

  function paintAmountSteps(): void {
    const fractionOf = (code: string): number =>
      currencies.find((c) => c.code === code)?.fraction ?? 2;
    el('wlqAmount').setAttribute('step', stepForFraction(fractionOf(quickCurrency())));
    const target = targetCurrency();
    el('wlqTargetAmount').setAttribute('step', stepForFraction(fractionOf(target ?? quickCurrency())));
  }

  /**
   * The "Arrives as" picker: what it offers, and whether it may be touched.
   *
   * A multi-currency destination may receive anything, so the picker is
   * open. A single-currency one holds exactly its own, so the picker says so
   * and is disabled — the user's rule: pre-select the destination's own
   * currency, and keep it read-only where the account allows no choice.
   *
   * `readonly` is not available on a `select`, so this is `disabled` plus
   * `aria-disabled` — the one place invariant #4's "never disabled" does not
   * reach, because there is no choice being removed from the tab order, only
   * a single option being stated. A disabled select is still announced and
   * still shows its value.
   *
   * The amount is cleared whenever the currency under it changes. 41,070
   * forints relabelled "EUR" reads as a number somebody meant, and saving it
   * would book it; an empty field is the better answer. While the two
   * currencies match there is nothing to convert, so the arriving amount
   * simply follows the one being sent.
   */
  /**
   * What was last typed against each arriving currency, so switching away
   * and back does not throw it away.
   *
   * Cleared with the form, not with the currency: the point is that moving
   * from forints to dollars and back offers the forints again rather than an
   * empty box. The number is offered, not imposed — it sits in the field for
   * the user to accept or overtype.
   */
  const targetAmounts = new Map<string, string>();

  /**
   * The arriving currency the USER chose, as against one an account forced.
   *
   * A single-currency destination leaves no choice: the picker is set to its
   * currency and locked. Moving on from there to an account that can hold
   * anything should not leave the forced currency behind as though it had
   * been asked for — what the person last actually picked is what they still
   * mean, and it comes back as soon as a destination can take it.
   *
   * A record being opened counts as a choice: its own arriving currency is
   * what the entry says, and is what should return if the destination wanders
   * through an account that cannot hold it.
   */
  let chosenTargetCcy: string | undefined;

  /** A new entry starts with no remembered amounts and no carried-over currency. */
  function forgetTargetAmounts(): void {
    targetAmounts.clear();
    chosenTargetCcy = undefined;
    // The figure itself too: the last entry's "Arrives as" is next to never
    // this one's, and a number already sitting there gets saved unread.
    el<HTMLInputElement>('wlqTargetAmount').value = '';
    const select = el<HTMLSelectElement>('wlqTargetCcy');
    select.value = '';
    delete select.dataset['ccy'];
  }

  function paintTargetCurrency(prefer?: string): void {
    const select = el<HTMLSelectElement>('wlqTargetCcy');
    const to = allAccounts.find((a) => a.id === el<HTMLSelectElement>(destinationField()).value);
    const was = select.value;

    if (prefer !== undefined) chosenTargetCcy = prefer;
    const allowed = to && !to.isMulticurrency ? [to.currencyCode] : currencies.map((c) => c.code);
    select.innerHTML = allowed
      .map((code) => `<option value="${escapeHtml(code)}">${escapeHtml(code)}</option>`).join('');
    /*
     * `prefer` is what a record being loaded says, and it is passed in rather
     * than assigned to the select beforehand: this function builds the
     * options, so before it runs there are none, and setting `.value` on an
     * empty select silently does nothing. That is how a transaction that
     * arrives in forints came up reading EUR.
     */
    select.value = chooseTargetCurrency({
      allowed, prefer, chosen: chosenTargetCcy, was, fallback: to?.currencyCode ?? quickCurrency(),
    });

    const locked = Boolean(to && !to.isMulticurrency);
    select.disabled = locked || quickReadOnly;
    select.setAttribute('aria-disabled', String(locked));

    const amountEl = el<HTMLInputElement>('wlqTargetAmount');
    const previous = select.dataset['ccy'] ?? '';
    select.dataset['ccy'] = select.value;
    if (prefer === undefined && previous !== '' && previous !== select.value) {
      /*
       * The amount belongs to the currency it was typed in. 41,070 forints
       * relabelled "USD" reads as a number somebody meant, so it does not
       * follow the currency — but it is not thrown away either: it is kept
       * against the currency it was for, and offered again if that currency
       * comes back.
       */
      if (amountEl.value !== '') targetAmounts.set(previous, amountEl.value);
      amountEl.value = targetAmounts.get(select.value) ?? '';
    }
    // Nothing to convert: the money arrives as the same currency it left in,
    // and asking for the number twice would be asking for a mistake.
    const mirrored = select.value === quickCurrency();
    amountEl.readOnly = mirrored || quickReadOnly;
    amountEl.setAttribute('aria-readonly', String(mirrored));
    if (mirrored) amountEl.value = el<HTMLInputElement>('wlqAmount').value;
  }

  function paintMoveExtras(): void {
    const kind = quickKind();
    const isMove = kind === 'move';
    const from = el<HTMLSelectElement>('wlqWallet').value;
    const to = el<HTMLSelectElement>('wlqWallet2').value;
    /*
     * Who owes whom. A Move asks it once both its wallets are chosen and
     * differ; Spend and Money in once the money has a wallet at all, and
     * then ask which OTHER wallet it was for.
     */
    el('wlqXferKindWrap').hidden = isMove ? !from || !to || from === to : !from;
    el('wlqXferKind').setAttribute('aria-label', isMove ? 'Does the receiving wallet owe this back'
      : kind === 'spend' ? 'Was this paid for another wallet' : 'Was this received for another wallet');
    // The choices for this tab. A choice another tab made carries over where
    // it means the same thing — "Borrowed from" on a Spend is a loan on a Move.
    const kindSelect = el<HTMLSelectElement>('wlqXferKind');
    const choices = debtChoices(kind);
    const keepKind = kindSelect.value;
    const signature = choices.map((c) => c[0]).join('|');
    if (kindSelect.dataset['choices'] !== signature) {
      kindSelect.dataset['choices'] = signature;
      kindSelect.innerHTML = choices.map(([value, label, title]) =>
        `<option value="${escapeHtml(value)}" title="${escapeHtml(title)}">${escapeHtml(label)}</option>`).join('');
      const carried = choices.some((c) => c[0] === keepKind) ? keepKind
        : debtChoice(keepKind).kind;
      kindSelect.value = choices.some((c) => c[0] === carried) ? carried : '';
    }
    const inward = debtChoice(kindSelect.value).inward;
    el('wlqXferForLbl').textContent = kind === 'spend' ? (inward ? 'From' : 'For') : 'Belongs to';
    const owed = kindSelect.value !== '';
    el('wlqXferForWrap').hidden = isMove || !owed;
    el('wlqXferAmountWrap').hidden = !owed;
    // Every wallet but this one and its own family: a parent's balance
    // already holds its child's, so a debt between them is one sum twice.
    const forSelect = el<HTMLSelectElement>('wlqXferFor');
    const keepFor = forSelect.value;
    const kin = from ? new Set([from, ...ancestorIds(data.wallets, from), ...descendantIds(data.wallets, from)]) : new Set<string>();
    forSelect.innerHTML = '<option value="">— which wallet? —</option>' + orderWallets(data.wallets)
      .filter(({ wallet }) => !kin.has(wallet.id))
      .map(({ wallet }) => `<option value="${escapeHtml(wallet.id)}">${escapeHtml(walletPath(data.wallets, wallet.id))}</option>`)
      .join('');
    if (keepFor && [...forSelect.options].some((o) => o.value === keepFor)) forSelect.value = keepFor;

    paintAmountSteps();
    const arrives = arrivesShown();
    el('wlqTargetWrap').hidden = !arrives;
    if (arrives) paintTargetCurrency();
    // After the arriving currency: the debt may be counted in either.
    paintDebtCurrency();
  }

  /**
   * The currency the money arrives in, when that is not the one it left in.
   *
   * `undefined` means no conversion: the destination holds the currency being
   * sent, so one amount describes both ends. Otherwise the destination's own
   * currency is what arrives, and the form has to ask how much — because the
   * two amounts together ARE the rate and there is nowhere else to get one.
   */
  /**
   * The currency the money arrives in — now simply what the picker says.
   *
   * This used to be inferred: ask the destination account what it can hold,
   * and conclude a conversion only when it could not hold what was being
   * sent. That answered the question for a new entry and the wrong one for a
   * stored entry, and it could not see a conversion inside one
   * multi-currency account at all, because such an account holds everything.
   * Two bugs came out of it in one day.
   *
   * A form that asks cannot be wrong about what it was told.
   */
  function targetCurrency(): string {
    // Not asked on this tab: the money arrives as it left.
    if (!arrivesShown()) return quickCurrency();
    return el<HTMLSelectElement>('wlqTargetCcy').value || quickCurrency();
  }

  /**
   * The sidecar, when there is one to write.
   *
   * It rides in the transaction's own commit rather than going through
   * `/wallet-transfers`, which can only describe both legs on a single
   * account. A move on this form can cross two — Renovation's cash in the
   * bank becoming Maintenance's cash in hand is still a loan — and the
   * sidecar only ever cared about the two wallets.
   *
   * "Just moving it" sends an EMPTY kind rather than sending nothing. The
   * two are different questions and were being answered the same way: an
   * absent `walletMove` means "this save has nothing to say about the
   * earmark move" — which is right for a form that does not offer one — and
   * an empty kind means "there is no longer one of these".
   *
   * Sending nothing is why picking "Just moving it" on an entry that was
   * already a loan appeared to do nothing at all. The save went through, the
   * old sidecar was simply never mentioned, and the row went on saying it
   * was a loan.
   */
  function walletMoveBody(
    kind: QuickKind, moneyWallet: string | null, otherWallet: string | null,
    legIds: { money: string; other: string },
  ): { walletMove: Record<string, unknown> } {
    return {
      walletMove: {
        // Only ever used when the server finds no record to update — it
        // reconciles against what is already there (`walletMoveMembers`),
        // which is what stops a save adding a second sidecar beside the
        // first.
        id: crypto.randomUUID(),
        ...quickDebtMove(kind, quickDebtAsked(), { money: moneyWallet, other: otherWallet }, legIds),
      },
    };
  }

  /** What the debt row says, as typed. */
  function quickDebtAsked(): { kind: string; forWallet: string; amount: string; measure: 'sent' | 'arrived' | '' } {
    return {
      kind: el('wlqXferKindWrap').hidden ? '' : el<HTMLSelectElement>('wlqXferKind').value,
      forWallet: el<HTMLSelectElement>('wlqXferFor').value,
      amount: el<HTMLInputElement>('wlqXferAmount').value,
      measure: el<HTMLSelectElement>('wlqXferCcy').value as 'sent' | 'arrived' | '',
    };
  }

  /**
   * The debt row's currency picker: the amount that left and the one that
   * arrived, when those are two currencies. Hidden when they are one — then
   * there is nothing to choose and the debt is in that currency either way.
   */
  function paintDebtCurrency(): void {
    const select = el<HTMLSelectElement>('wlqXferCcy');
    const sent = quickCurrency();
    const arrived = targetCurrency();
    const keep = select.value || defaultDebtMeasure(quickKind());
    select.innerHTML = `<option value="sent">${escapeHtml(sent)}</option>`
      + `<option value="arrived">${escapeHtml(arrived)}</option>`;
    select.value = keep;
    select.hidden = sent === arrived;
  }

  /** The figure typed into one of the form's amount fields, or undefined. */
  function typedAmount(id: string): number | undefined {
    const n = Number(el<HTMLInputElement>(id).value.replace(/[\s\u202f,]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }

  function cellName(id: string): string {
    const wallet = data.wallets.find((w) => w.id === id);
    return wallet?.shortName || wallet?.name || walletPath(data.wallets, id);
  }

  /**
   * Where the two wallets stand and what this entry would do to it — under
   * the debt row, from the replica, as the form is typed (webledgerDebtHelper).
   */
  function paintQuickDebtHelp(): void {
    const box = el('wlqXferHelp');
    const asked = quickDebtAsked();
    const kind = quickKind();
    const debt = quickDebtMove(kind, asked,
      { money: el<HTMLSelectElement>('wlqWallet').value || null, other: el<HTMLSelectElement>('wlqWallet2').value || null },
      { money: 'money', other: 'other' });
    if (!('fromWalletId' in debt)) { box.innerHTML = ''; return; }
    // Which of the two typed amounts the debt is counted on.
    const sentSide = (debt.amountSplitId === 'money') === (kind !== 'income');
    const currencyCode = sentSide ? quickCurrency() : targetCurrency();
    const whole = sentSide ? typedAmount('wlqAmount')
      : (targetCurrency() === quickCurrency() ? typedAmount('wlqAmount') : typedAmount('wlqTargetAmount'));
    const part = debt.amount === null ? undefined : Number(debt.amount);
    box.innerHTML = debtHelperHtml({
      fromName: walletPath(data.wallets, debt.fromWalletId),
      toName: walletPath(data.wallets, debt.toWalletId),
      // The table is narrow: its cells take the short name, or the wallet's
      // own name without its parents.
      fromShort: cellName(debt.fromWalletId),
      toShort: cellName(debt.toWalletId),
      // The form's own Wallet: where the money left, or on Money in where it arrived.
      ownSide: debt.toWalletId === el<HTMLSelectElement>('wlqWallet').value ? 'to' : 'from',
      kind: normalTransferKind(debt.kind),
      currencyCode,
      amount: part ?? whole,
      legSize: whole,
      standing: standingBefore(transferAmounts(datasetLedger(client.dataset)), debt.fromWalletId, debt.toWalletId, {
        txDate: el<HTMLInputElement>('wlqDate').value || todayIso(),
        transactionId: quickEdit?.txId,
      }),
      choices: [...new Set([quickCurrency(), targetCurrency()])],
      chip: (n, code, className) => chip(n, code, className),
    });
  }

  function quickHistoryContext(history: TransactionHistory | undefined): HistoryContext {
    return {
      accountName: (id) => allAccounts.find((a) => a.id === id)?.name ?? id.slice(0, 8),
      tagName: (id) => tags.find((t) => t.id === id)?.name,
      walletName: (id) => (data.wallets.some((w) => w.id === id) ? walletPath(data.wallets, id) : undefined),
      currency: (c) => currencies.find((cur) => cur.code === c),
      txCurrencyCode: quickEdit?.currencyCode ?? '',
      splitNumber: splitNumbering(history?.commits ?? []),
      tagOfLink: tagLinkTargets(history?.commits ?? []),
    };
  }

  /**
   * History for the short form, on the short form.
   *
   * Handing over to the full editor to answer "what did this look like last
   * week" loses the form you were working in to read three lines. The panel
   * itself is shared with the full editor, so the parts that could corrupt a
   * ledger — refusing a restore without a lease, never building a
   * compare-and-swap out of a past version's split versions — exist once.
   *
   * What is particular here is `showAsOf`: this form has two legs, and a past
   * version may have five. It says so rather than painting two of them.
   */
  function makeQuickTimeline(txId: string): HistoryPanel {
    return createHistoryPanel({
      txId, token: client.token, client,
      mount: el('wlqTimeline'),
      toggleBtn: el('wlqHistory'),
      fullPageHref: appHref(`/webledger/transactions/${txId}/history`),
      context: quickHistoryContext,
      baseVersion: () => quickEdit?.baseVersion ?? 0,
      leaseId: () => quickLeaseId(),
      canEdit: () => true,
      currentSplits: () => (quickEdit ? [quickEdit.money, quickEdit.other] : []),
      showAsOf: (snapshot) => {
        /*
         * A history value as the text this form shows.
         *
         * `String()` directly on `FieldValue['v']` stopped being safe when
         * the `json` type arrived (ADR-0026): its `v` is `unknown`, so a
         * value that was never meant to be text would have been rendered as
         * `[object Object]` rather than refused. Everything this form reads
         * is a scalar, so anything else becomes the fallback.
         */
        const text = (value: FieldValue | undefined, fallback: string): string => {
          const raw = value?.v;
          if (typeof raw === 'string') return raw;
          if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
          return fallback;
        };
        const splits = [...snapshot.splits.entries()].map(([id, f]) => ({
          id,
          accountId: text(f['accountId'], ''),
          value: text(f['value'], '0'),
          // A past version of a conversion holds its rate in these two, the
          // same as the live record does.
          amount: text(f['amount'], text(f['value'], '0')),
          currencyCode: text(f['currencyCode'], ''),
          walletId: f['walletId']?.v === undefined ? null : text(f['walletId'], '') || null,
        }));
        const asTx = {
          txDate: text(snapshot.fields['txDate'], ''),
          description: text(snapshot.fields['description'], ''),
          currencyCode: text(snapshot.fields['currencyCode'], ''),
          splits,
        };
        const fit = fitsQuickForm(asTx);
        if (!fit) {
          // Five legs, or two currencies, or a shape this form has no words
          // for. The full editor has as many rows as a version needs, so go
          // there with the history already open rather than explaining.
          client.notify('That version has a shape this short form cannot show \u2014 opening the full editor.');
          handOver(true)();
          return false;
        }
        paintQuickFields(asTx, fit);
        setQuickReadOnly(true);
        return true;
      },
      backToCurrent: async () => {
        // Editable again only if the entry is this tab's — somebody else's
        // stays read-only, as it was before the past version was opened.
        setQuickReadOnly(quickTheirs());
        el('wlqNotice').hidden = true;
        /*
         * The versions too, not only the values.
         *
         * This path is how the form comes back from a past version — and a
         * RESTORE comes back through it, having just moved the record on by
         * a version. Repainting the fields and leaving `quickEdit` pointing
         * at the version from before left the form unable to save at all:
         * the next Save went out with a base the server had already passed,
         * came back `stale-base`, and said "The entry could not be saved"
         * about a form that looked perfectly ordinary. The full editor does
         * this already — its `populate` sets both — and this is the same
         * thing said on this form. `reloadQuick` does both.
         */
        await reloadQuick();
        paintQuickHold();
      },
      notice: (html) => {
        const box = el('wlqNotice');
        if (html === null) { box.hidden = true; return null; }
        // Back to informational: `refuseQuickSave` turns this box red, and a
        // lease notice inheriting that would announce "somebody is editing
        // this" in the colour of a failure.
        box.className = 'alert alert-info py-2 px-3 small';
        box.hidden = false;
        box.innerHTML = html;
        return box;
      },
      // A failure from the lease machinery is still a failure of this form,
      // so it lands in this form's notice rather than only being announced.
      showError: (message) => { refuseQuickSave(message); },
      notify: (message) => client.notify(message),
      onRestored: () => { flashNext = true; void afterWrite(); },
      escapeHtml, presenceDotHtml, timeAgo,
    });
  }

  /**
   * The way back from the full editor, offered only when there is one.
   *
   * The short form belongs to this page, so no other caller of the editor
   * can hand back — and the button stays hidden there rather than offering
   * something that cannot happen. `fitsQuickForm` is the same test that
   * decides whether clicking a row opens the short form in the first place,
   * so the button appears exactly when the short form could have been used.
   */
  function simpleEditorFor(tx: Parameters<typeof fitsQuickForm>[0] & { id: string }):
  { simpleEditor: (txId: string) => void } | Record<string, never> {
    if (!fitsQuickForm(tx)) return {};
    return {
      simpleEditor: (txId) => {
        void (async () => {
          clearPageNotice(root);
          const res = await apiFetch(`/apps/webledger/transactions/${txId}`, { token: client.token })
            .catch(() => undefined);
          if (!res?.ok) { fail(await failureMessage(res, 'Could not open that transaction.')); return; }
          const body = await res.json() as { transaction: Parameters<typeof openQuickEdit>[0] };
          // Somebody may have taken it in the meantime, and the shape may
          // have changed under an edit made in the full editor just now —
          // `openQuickEdit` re-checks both and says no by returning false.
          if (!await openQuickEdit(body.transaction)) {
            // The full editor has already gone and the short form refused to
            // take over, so nothing at all is now open. Saying so is the
            // difference between a decision and a dead button.
            fail('That transaction cannot be opened on the short form any more.');
          }
        })();
      },
    };
  }

  /**
   * Open the short form on an existing transaction.
   *
   * Returns false only when the transaction is a shape this form cannot say;
   * the caller falls back to the full editor. Held by somebody else, it opens
   * here read-only, saying who and since when, with Take over — as the full
   * editor does (asked: "i see no reason to open something that qualifies for
   * the short form in the full editor").
   */
  async function openQuickEdit(tx: {
    id: string; txDate: string; description: string; currencyCode: string; version: number;
    splits: Array<{ id: string; accountId: string; value: string; currencyCode: string; walletId?: string | null; version: number }>;
  }, opts: { unsent?: boolean } = {}): Promise<boolean> {
    const fit = fitsQuickForm(tx);
    if (!fit) return false;

    /*
     * Join the record's presence roster BEFORE claiming.
     *
     * `RecordStateTracker` resolves a lease holder's name out of
     * `session.presence(...)`; with nobody in the roster it falls back to
     * "Someone else", which is strictly worse than a name and was what this
     * form used to show everyone else while it was open. Before the claim,
     * so the `leaseTaken` broadcast finds somebody to name.
     */
    await client.session.openRecord('transaction', tx.id);
    /*
     * With no connection there is nothing to reserve with, and it opens
     * anyway (ADR-0011): editable, saying it is not reserved, saved on this
     * device. It went to the full editor before, which could do this; the
     * short form could not.
     */
    // An entry with unsent work stays with it: saved back into the queue, not
    // written over the server's copy behind it.
    const offline = knownOffline() || client.transport.state !== 'open' || opts.unsent === true;
    const hold = new RecordHold({
      client: { leaseClient: client.leaseClient, transport: client.transport, session: client.session, userId: client.userId },
      entity: 'transaction', recordId: tx.id,
      onView: () => { paintQuickLeaseState(); if (quickHold === hold) paintQuickHold(); },
      onTaken: (by) => keepQuickDraftTakenBy(tx.id, by),
      onFreed: () => quickPickUpFreed(),
    });
    quickHold?.dispose();
    quickHold = hold;
    // Somebody else has it, connected or not: the form opens read-only and
    // says who, since when, with Take over — the full editor's behaviour,
    // on the form that fits the entry.
    await hold.open(undefined, offline);
    quickTookFrom = undefined;
    // A draft of ours somebody interrupted earlier, from this session or one
    // before: offered again, beside the values as they are now.
    quickHeld = heldFor(await client.outbox.allDocuments(), tx.id);
    quickStolenBy = quickHeld?.held ? {
      id: quickHeld.held.takenBy, name: quickHeld.held.takenByName,
      initials: quickHeld.held.takenByName.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase() || '?',
      colorHex: '#8a8f98',
    } : undefined;

    quickEdit = {
      txId: tx.id, baseVersion: tx.version, currencyCode: tx.currencyCode,
      ...splitRefs(tx.splits, fit.moneyIndex),
      ...(offline ? { offline: true } : {}),
      ...(opts.unsent ? { unsent: true } : {}),
    };

    paintQuickFields(tx, fit);
    setQuickReadOnly(quickTheirs());
    quickTimeline?.close(false);
    quickTimeline = makeQuickTimeline(tx.id);
    // Offline, from the local copy — which also has any tag change saved
    // here and not sent yet.
    await loadTagSelection(client.token, tx.id, quickTagSelection,
      () => datasetLedger(client.dataset).transactionTags().filter((t) => t.transactionId === tx.id));
    renderQuickTags();
    await quickFiles.reload();
    /*
     * Said to a screen reader, not shown. On screen it was the same three
     * words on every entry, above an entry that is plainly being edited —
     * reported as permanent, useless information. It still names the dialog
     * (`aria-labelledby`), which is the one job it does that nothing else on
     * screen does.
     */
    el('wlqTitle').textContent = 'Edit this entry';
    el('wlqTitle').classList.add('visually-hidden');
    // Off the replica, so it shows offline and costs no round trip. The panel
    // itself is wired once on `root` and reaches in here.
    el('wlqBank').innerHTML =
      bankInfoOf(datasetLedger(client.dataset), tx.id).length > 0 ? bankInfoBadgeHtml(tx.id) : '';
    paintQuickLeaseState();
    el('wlqFull').hidden = false;
    el('wlqHistory').hidden = false;
    paintQuickNav();
    el('wlqFull').dataset['tx'] = tx.id;
    renderQuickPreview();
    paintQuickHold();
    openQuickForm('wlqAmount');
    return true;
  }

  /*
   * Clicking the bar's own space scrolls back to the top.
   *
   * Pinned, it is the only part of the page furniture still on screen once
   * the log is a few hundred rows down — so it is also the natural thing to
   * reach for when what you actually want is the totals and the nav above
   * it. Clicks on anything interactive are left alone; this is the gap
   * between them.
   */
  el('wlPane-log').querySelector('.wl-log__bar')?.addEventListener('click', (event) => {
    if ((event.target as HTMLElement).closest('button, select, input, a, label')) return;
    setHeaderCollapsed(false);
  });

  /*
   * The totals and the nav fold away while the list is being read, and come
   * back when it is not.
   *
   * They used to leave because the PAGE had scrolled, which meant getting
   * them back scrolled the list too — straight to the beginning of the
   * ledger, losing whatever you were looking at. With the list on its own
   * scroller the two are independent: folding the header only changes the
   * region's height, and a region's scrollTop survives that, so the row at
   * the top before is the row at the top after.
   */
  /** 0 = fully open, 1 = folded down to the filter row. */
  let foldAt = 0;
  /** Held open by a click, until the next downward scroll releases it. */
  let foldPinned = false;
  let lastRegScroll = 0;

  /** How far the list must scroll to fold the panel away completely. */
  function foldSpan(): number {
    let total = 0;
    for (const wrap of root.querySelectorAll<HTMLElement>('.wl-head-fold')) {
      total += (wrap.firstElementChild as HTMLElement | null)?.scrollHeight ?? 0;
    }
    return Math.max(1, total);
  }

  /**
   * Fold the panel to `t`, where 0 is open and 1 is gone.
   *
   * A fraction rather than a flag, because the panel is meant to go with the
   * list rather than snap once it has passed a line: `0.4fr` on a one-row
   * grid is forty per cent of the content's height, and the wrapper clips
   * what will not fit.
   *
   * `animate` is off while it is following a finger and on when a click asks
   * for it, which is the difference between tracking and sliding.
   */
  function setFold(t: number, animate: boolean): void {
    foldAt = Math.min(1, Math.max(0, t));
    for (const wrap of root.querySelectorAll<HTMLElement>('.wl-head-fold')) {
      wrap.classList.toggle('wl-head-fold--anim', animate);
      wrap.style.gridTemplateRows = `${(1 - foldAt).toFixed(3)}fr`;
    }
    sizeRegisterScroller();
  }

  function setHeaderCollapsed(on: boolean): void {
    foldPinned = !on;
    setFold(on ? 1 : 0, true);
  }

  /**
   * As tall as whatever is left of the window.
   *
   * Measured rather than written as a `calc`: what sits above this is a
   * nav that wraps, a totals strip that wraps, and a filter row — three
   * things whose height depends on the width and on how many currencies
   * this ledger holds.
   */
  function sizeRegisterScroller(): void {
    const region = root.querySelector<HTMLElement>('#wlRegScroll');
    if (!region || section !== 'log') return;
    const top = region.getBoundingClientRect().top;
    region.style.maxHeight = `${Math.max(240, window.innerHeight - top - 16)}px`;
    region.style.height = region.style.maxHeight;
  }

  el('wlQuickBtnLog').addEventListener('click', () => { el('wlQuickBtn').click(); });

  // Reading the list folds the header away; coming back to the top of it
  // brings the header back, which is where somebody is heading anyway.
  /*
   * The panel follows the list.
   *
   * Scrolling down folds it away a pixel at a time; scrolling back to the
   * top opens it again. A click pins it open — and the NEXT downward scroll
   * releases that pin, which is what "reduces itself again" asks for. Without
   * the pin, clicking to open while the list sits at 900px would be undone
   * by the same rule on the very next frame.
   */
  el('wlRegScroll').addEventListener('scroll', () => {
    const at = el('wlRegScroll').scrollTop;
    const down = at > lastRegScroll + 1;
    lastRegScroll = at;
    if (foldPinned) {
      if (!down) return;
      foldPinned = false;
    }
    setFold(at / foldSpan(), false);
  }, { passive: true });
  window.addEventListener('resize', sizeRegisterScroller);

  el('wlQuickBtn').addEventListener('click', () => {
    quickEdit = undefined;
    quickTagSelection.wanted.clear();
    quickTagSelection.links.clear();
    renderQuickTags();
    paintQuickLeaseState();
    el('wlqTitle').textContent = 'Add an entry';
    el('wlqTitle').classList.remove('visually-hidden');
    el('wlqBank').innerHTML = '';
    el('wlqFull').hidden = true;
    // Nothing has happened to an entry that does not exist yet.
    el('wlqHistory').hidden = true;
    quickTimeline?.close(false);
    quickTimeline = undefined;
    setQuickReadOnly(false);
    quickFiles.dropHeld();
    void quickFiles.reload();   // nothing to attach to yet: files are held until Save
    el('wlqNotice').hidden = true;
    el<HTMLInputElement>('wlqAmount').value = '';
    el<HTMLInputElement>('wlqNote').value = '';
    el<HTMLInputElement>('wlqDate').value = todayIso();
    // A new entry owes nobody until it is told so.
    el<HTMLSelectElement>('wlqXferKind').value = '';
    el<HTMLSelectElement>('wlqXferFor').value = '';
    el<HTMLInputElement>('wlqXferAmount').value = '';
    el<HTMLSelectElement>('wlqXferCcy').value = '';
    forgetTargetAmounts();
    refreshQuickForm();
    openQuickForm('wlqDate');
  });
  el('wlqPrev').addEventListener('click', () => { void stepQuick(OLDER); });
  el('wlqNext').addEventListener('click', () => { void stepQuick(YOUNGER); });
  /*
   * The same two moves under a thumb, on the short form as on the full one.
   *
   * Gated on the buttons rather than on the list, because the buttons
   * already carry the answer: `paintQuickNav` hides the pair while adding a
   * new entry and disables whichever end of the log this one sits at. A
   * swipe that works where the button is greyed out would be the gesture
   * disagreeing with the thing it is a shortcut for.
   */
  wireSwipeNav(el('wlQuickDlg'), {
    onRight: () => {
      if (!el('wlqNav').hidden && !el<HTMLButtonElement>('wlqNext').disabled) void stepQuick(YOUNGER);
    },
    onLeft: () => {
      if (!el('wlqNav').hidden && !el<HTMLButtonElement>('wlqPrev').disabled) void stepQuick(OLDER);
    },
  });

  /*
   * Take over, take back, keep mine, discard mine — delegated, because the
   * notice and the banner are rebuilt from scratch as the hold changes.
   */
  el('wlQuickDlg').addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('#wlqTakeOverBtn') || target.closest('#wlqTakeBackBtn')) { void quickTakeOver(); return; }
    const keep = target.closest<HTMLElement>('[data-qkeep]');
    if (keep) { void applyQuickHeld(keep.dataset['qkeep']!); return; }
    if (target.closest('#wlqKeepAllMineBtn')) { void applyQuickHeld('all'); return; }
    if (target.closest('#wlqDiscardMineBtn')) {
      void releaseQuickHeld().then(() => client.notify('Your kept changes were discarded.'));
    }
  });

  /*
   * What is being typed, for whoever is watching this entry — the date and
   * the note, under the same field names the full editor sends and shows, so
   * either form can watch the other. Never persisted.
   */
  const previewQuick = (field: 'txDate' | 'description', id: string) => () => {
    if (!quickEdit || !quickLeaseId()) return;
    client.session.preview('transaction', quickEdit.txId, field, { t: field === 'txDate' ? 'date' : 'string', v: el<HTMLInputElement>(id).value });
  };
  el('wlqDate').addEventListener('input', previewQuick('txDate', 'wlqDate'));
  el('wlqNote').addEventListener('input', previewQuick('description', 'wlqNote'));

  el('wlqCancel').addEventListener('click', () => {
    void releaseQuickLease();
    closeQuickForm();
  });
  // Escape or a backdrop click must free the hold too, or the row stays
  // marked as "being edited" until the TTL lapses. `hidden.bs.modal` is the
  // modal's equivalent of the native dialog's `close`.
  el('wlQuickDlg').addEventListener('hidden.bs.modal', () => {
    void releaseQuickLease();
    // Held files belong to the entry being typed; closing it lets them go.
    quickFiles.dropHeld();
    el('wlqLease').innerHTML = '';
    el<HTMLButtonElement>('wlqSave').disabled = false;
    // Back to the middle for the next entry. Where a panel sits is a property
    // of the moment, not a preference — the same rule `makeDraggable` states
    // for not persisting it — and a form that opens off to one side because
    // of something you did ten minutes ago reads as a glitch.
    quickDrag?.reset();
  });
  /**
   * Hand this entry over to the full editor, optionally with its history open.
   *
   * The lease goes first and is awaited: the editor claims the same record
   * lease the moment it opens, and if the release is still in flight it reads
   * the record as held — by this very tab — and opens read-only saying so.
   */
  const handOver = (openHistory: boolean) => () => {
    const txId = el('wlqFull').dataset['tx'];
    void (async () => {
      clearPageNotice(root);
      await releaseQuickLease();
      closeQuickForm();
      if (!txId) return;
      const res = await apiFetch(`/apps/webledger/transactions/${txId}`, { token: client.token })
        .catch(() => undefined);
      if (!res?.ok) { fail(await failureMessage(res, 'Could not open that transaction.')); return; }
      const body = await res.json() as { transaction: Parameters<typeof openTransactionEditor>[2] };
      await openTransactionEditor(root, client, body.transaction, allAccounts, currencies,
        (savedId) => { currentTxId = savedId; flashNext = true; void afterWrite(); },
        {
          openHistory,
          ...simpleEditorFor(body.transaction as never),
          // Handed over from the short form, so it keeps the list it was
          // stepping through — the arrows must not vanish on the way.
          neighbours: registerNeighbours,
        });
    })();
  };
  el('wlqFull').addEventListener('click', handOver(false));
  // Not a hand-over any more: the history opens over this form. Being thrown
  // into the full editor to read three lines loses the form you were in.
  el('wlqHistory').addEventListener('click', () => { void quickTimeline?.toggle(); });
  /*
   * The short form can be pushed aside.
   *
   * It is a modal over the very log it is about — checking the date or the
   * amount of the row below against what you are typing meant closing it and
   * opening it again. Same helper, same grip and same keyboard handling as
   * the full editor and the history panel (`makeDraggable`): arrows move it,
   * Home puts it back.
   */
  // The date field is ours rather than the browser's — see dateField.ts. It
  // is mounted once: the control lives in the page from the start and the
  // form only fills it.
  mountDateField(
    el<HTMLInputElement>('wlqDate'),
    root.querySelector<HTMLElement>('[data-date-open="wlqDate"]')!,
    () => { renderQuickPreview(); },
  );

  const quickPanel = root.querySelector<HTMLElement>('#wlQuickDlg [data-dlg-panel]');
  const quickHandle = root.querySelector<HTMLElement>('#wlQuickDlg [data-dlg-handle]');
  const quickDrag = quickPanel && quickHandle
    ? makeDraggable({ panel: quickPanel, handle: quickHandle })
    : undefined;

  el('wlqSave').addEventListener('click', () => { void saveQuickEntry(); });
  root.querySelectorAll<HTMLInputElement>('input[name="wlqKind"]')
    .forEach((r) => { r.addEventListener('change', refreshQuickForm); });
  // The account lists are built from the currency, so changing it rebuilds
  // them — and a chosen account that cannot hold the new currency drops out
  // rather than being carried into a save that would misstate it.
  el('wlqCcy').addEventListener('change', refreshQuickForm);
  for (const id of ['wlqWallet', 'wlqWallet2', 'wlqAccount', 'wlqAccount2', 'wlqCategory']) {
    el(id).addEventListener('change', paintMoveExtras);
  }
  // Picking the arriving currency repaints the field under it: the amount is
  // cleared when the currency changes, and mirrors the sent amount while the
  // two are the same.
  el('wlqTargetCcy').addEventListener('change', () => {
    // Picking it here is the user choosing; an account forcing it is not.
    chosenTargetCcy = el<HTMLSelectElement>('wlqTargetCcy').value;
    paintTargetCurrency();
    renderQuickPreview();
  });
  // ...and so does typing the amount, while the arriving side is mirroring it.
  el('wlqAmount').addEventListener('input', () => {
    if (el<HTMLInputElement>('wlqTargetAmount').readOnly) {
      el<HTMLInputElement>('wlqTargetAmount').value = el<HTMLInputElement>('wlqAmount').value;
    }
  });
  el('wlqXferKind').addEventListener('change', paintMoveExtras);
  /*
   * The assistants open and close as one, per user: somebody who reads them
   * keeps them open on every device, and somebody who does not is not handed
   * two tables on every entry. Collapsed until opened the first time.
   */
  const paintAssist = (): void => {
    const open = client.prefs.get(PREF.quickAssist) === '1';
    el('wlqAssist').hidden = !open;
    el('wlqAssistBtn').setAttribute('aria-expanded', String(open));
  };
  paintAssist();
  el('wlqAssistBtn').addEventListener('click', () => {
    client.prefs.set(PREF.quickAssist, client.prefs.get(PREF.quickAssist) === '1' ? '' : '1');
    paintAssist();
  });
  el('wlqWarn').addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-borrow-short]');
    if (!target || quickReadOnly) return;
    el<HTMLSelectElement>('wlqXferKind').value = 'borrow';
    // Counted in the payment's own currency: that is what is short.
    el<HTMLSelectElement>('wlqXferCcy').value = 'sent';
    el<HTMLInputElement>('wlqXferAmount').value = toFixedFor(Number(target.dataset['borrowShort']), quickCurrency());
    paintMoveExtras();
    renderQuickPreview();
    el<HTMLSelectElement>('wlqXferFor').focus();
  });
  // The helper's two offers: settle exactly, or count it in the currency
  // that is owed. Delegated, because the helper is redrawn on every change.
  el('wlqXferHelp').addEventListener('click', (event) => {
    if (quickReadOnly) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-debt-settle],[data-debt-ccy]');
    if (!target) return;
    const settle = target.dataset['debtSettle'];
    const code = target.dataset['debtCcy'];
    if (settle !== undefined) {
      const select = el<HTMLSelectElement>('wlqXferCcy');
      const ccy = select.value === 'sent' || select.hidden ? quickCurrency() : targetCurrency();
      el<HTMLInputElement>('wlqXferAmount').value = toFixedFor(Number(settle), ccy);
    } else if (code !== undefined) {
      el<HTMLSelectElement>('wlqXferCcy').value = code === quickCurrency() ? 'sent' : 'arrived';
      // The part typed was in the other currency, and means nothing in this one.
      el<HTMLInputElement>('wlqXferAmount').value = '';
    }
    renderQuickPreview();
  });
  ['wlqAmount', 'wlqTargetAmount', 'wlqTargetCcy', 'wlqAccount', 'wlqAccount2', 'wlqWallet', 'wlqCategory', 'wlqNote', 'wlqDate',
    'wlqWallet2', 'wlqXferKind', 'wlqXferFor', 'wlqXferAmount', 'wlqXferCcy']
    .forEach((id) => {
      el(id).addEventListener('input', renderQuickPreview);
      el(id).addEventListener('change', renderQuickPreview);
    });

  /*
   * The account fields open the tree, the same control the full editor uses.
   *
   * A flat `<select>` of the whole chart is close to unusable in this book —
   * `Cash` appears under three different parents, and the options give no way
   * to tell which is which (webledgerAccountPicker.ts). The button writes back
   * through the hidden select and dispatches `change`, so the preview, the
   * shortfall warning and the save path all carry on reading one value and
   * needed no rewrite to gain a picker.
   */
  root.querySelectorAll<HTMLButtonElement>('[data-acct-btn]').forEach((btn) => {
    btn.addEventListener('click', () => {
      // A form showing a past version is not one to pick a new account on.
      // Checked here rather than suppressed on the button, because an
      // `onclick` property cannot reliably pre-empt this listener.
      if (quickReadOnly) return;
      const selectId = btn.dataset['acctBtn']!;
      const select = el<HTMLSelectElement>(selectId);
      openAccountPicker(btn, {
        accounts: pickableByField.get(selectId) ?? [],
        prefs: client.prefs,
        selectedId: select.value,
        onPick: (id) => {
          // The tree is built from this field's own accounts, so this should
          // always hold; refusing rather than assigning keeps a mismatch from
          // becoming a select with no selection and an entry that will not
          // save, with nothing on screen to say why.
          if (![...select.options].some((o) => o.value === id)) {
            refuseQuickSave('That account cannot go in this field.');
            return;
          }
          select.value = id;
          paintAccountButton(selectId);
          followAccountCurrency(selectId, id);
          select.dispatchEvent(new Event('change'));
        },
      });
    });
  });

  el('wlNewBtn').addEventListener('click', () => {
    el<HTMLInputElement>('wlNewName').value = '';
    el<HTMLInputElement>('wlNewShort').value = '';
    // The first palette colour no wallet is drawn in yet — what it would get
    // anyway, now written down so it does not change when others are added.
    el<HTMLInputElement>('wlNewColour').value = walletColours([
      ...data.wallets.map((w) => ({ id: w.id, name: w.name, sortOrder: w.sortOrder, color: w.color })),
      { id: 'new', name: '', sortOrder: Number.MAX_SAFE_INTEGER, color: null },
    ]).get('new')!;
    el<HTMLSelectElement>('wlNewParent').innerHTML = '<option value="">— top level —</option>'
      + orderWallets(data.wallets).map(({ wallet }) =>
        `<option value="${escapeHtml(wallet.id)}">${escapeHtml(walletPath(data.wallets, wallet.id))}</option>`).join('');
    clearDialogNotice('wlNew');
    el<HTMLDialogElement>('wlNewDlg').showModal();
  });
  el('wlEditDone').addEventListener('click', () => { el<HTMLDialogElement>('wlEditDlg').close(); });
  el('wlEditDelete').addEventListener('click', () => {
    if (editingWalletId) void deleteWallet(editingWalletId);
  });
  // Escape and the backdrop close it too, and both have to tidy up — the
  // mounted field holds a subscription and the record holds our presence.
  el<HTMLDialogElement>('wlEditDlg').addEventListener('close', closeWalletEditor);
  el('wlEditParent').addEventListener('change', () => {
    if (editingWalletId) void moveWallet(editingWalletId, el<HTMLSelectElement>('wlEditParent').value);
  });
  el('wlEditColour').addEventListener('change', () => {
    if (editingWalletId) void recolourWallet(editingWalletId, el<HTMLInputElement>('wlEditColour').value);
  });
  el('wlNewCancel').addEventListener('click', () => { el<HTMLDialogElement>('wlNewDlg').close(); });
  el('wlNewConfirm').addEventListener('click', () => { void createWallet(); });
  el('wlTransferCancel').addEventListener('click', () => { el<HTMLDialogElement>('wlTransferDlg').close(); });
  el('wlTransferConfirm').addEventListener('click', () => { void submitTransfer(); });
  el('wlTransferAmount').addEventListener('input', updateWarning);
  el('wlTransferFrom').addEventListener('change', updateWarning);
  wireTagSelection(el('wlqTagChips'), el<HTMLSelectElement>('wlqTagPick'),
    quickTagSelection, () => { renderQuickTags(); });

  /*
   * Hovering a `∑∑` running total: what it is made of, wallet by wallet and
   * currency by currency, at that point in the book — the same table the
   * accounts chart gives for a day, asked of the moment after an entry.
   * Wired once; `bookAt` is whatever was drawn last, and is unset outside
   * the whole-book view, where there is nothing to break down.
   */
  el('wlRegister').addEventListener('mousemove', (event) => {
    // An A or W mark: what it is about, and from what to what — at once.
    const mark = (event.target as HTMLElement).closest<HTMLElement>('.wl-reg__over[data-tip-says]');
    if (mark) {
      const moves = (mark.dataset['tipMoves'] ?? '').split('\n').filter(Boolean);
      showTableTip(`<span class="wl-spark__tipdate">${escapeHtml(mark.dataset['tipSays'] ?? '')}</span>`
        + moves.map((m) => `<span class="wl-mark-tip__move">${escapeHtml(m)}</span>`).join(''),
      mark.getBoundingClientRect(), event.clientX);
      return;
    }
    const cell = (event.target as HTMLElement).closest<HTMLElement>('.wl-reg__run');
    const row = cell?.closest<HTMLElement>('[data-tx]');
    const positions = row && bookAt?.get(row.dataset['tx'] ?? '');
    if (!cell || !row || !positions) { hideTableTip(); return; }
    const money = (code: string): MoneyCurrency | undefined => moneyByCode.get(code);
    showTableTip(
      constellationHtml(`${escapeHtml(row.dataset['date'] ?? '')} · after this entry`, positions,
        walletKeys(datasetLedger(client.dataset)), money),
      cell.getBoundingClientRect(), event.clientX);
  });
  el('wlRegister').addEventListener('mouseleave', hideTableTip);

  for (const id of ['wlSettledList', 'wlSettledTotals']) {
    el(id).addEventListener('change', () => {
      settled = el<HTMLInputElement>(id).checked;
      client.prefs.set(PREF.walletsSettled, settled ? '1' : '');
      renderList();
      paintCashTotals();
      // The running column follows it too.
      if (section === 'log') void renderRegister();
    });
  }

  el('wlLogCcy').addEventListener('change', () => {
    client.prefs.set(PREF.walletsLogCurrency, logCurrency());
    void renderRegister();
  });

  /*
   * The same tree every account field on this page opens, so "pick an
   * account" is one gesture wherever it is asked for — including the
   * collapsed/expanded state and the recently-used list, which are the
   * picker's own and shared by everything that opens it.
   */
  el('wlLogAccBtn').addEventListener('click', (event) => {
    openAccountPicker(event.currentTarget as HTMLElement, {
      accounts: allAccounts,
      prefs: client.prefs,
      selectedId: logAccountId,
      // A folder is a fair question here — "everything in Expenses" — and
      // `accountSubtree` already answers it.
      foldersPickable: true,
      onPick: (id) => {
        logAccountId = id;
        client.prefs.set(PREF.walletsLogAccount, id);
        void renderRegister();
      },
    });
  });
  el('wlLogAccClear').addEventListener('click', () => {
    logAccountId = '';
    client.prefs.set(PREF.walletsLogAccount, '');
    void renderRegister();
  });

  /*
   * The two selects clear by going back to their first option, which is a
   * real choice — "All wallets together", "Any" — and not only the absence
   * of a filter. The cross is a second way to reach it, in the same place as
   * every other filter's.
   */
  el('wlLogSelectClear').addEventListener('click', () => {
    const select = el<HTMLSelectElement>('wlLogSelect');
    select.value = 'all';
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });
  el('wlLogCcyClear').addEventListener('click', () => {
    const select = el<HTMLSelectElement>('wlLogCcy');
    select.value = '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /*
   * Narrowing as you type, a beat behind the keys: a fast typist's five
   * letters are one redraw, not five, and the pause is too short to read as
   * waiting. Escape empties it, as it does a browser's own search field.
   * Not kept in the prefs, unlike the four filters before it — a search is
   * a question of the moment, and one left over from yesterday would be a
   * filter you forgot you set.
   */
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  const searchNow = (): void => {
    if (searchTimer !== undefined) clearTimeout(searchTimer);
    searchTimer = undefined;
    void renderRegister({ reuseLocks: true });
  };
  el('wlLogSearch').addEventListener('input', () => {
    if (searchTimer !== undefined) clearTimeout(searchTimer);
    searchTimer = setTimeout(searchNow, 120);
  });
  el('wlLogSearch').addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || (event.target as HTMLInputElement).value === '') return;
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLInputElement).value = '';
    searchNow();
  });
  el('wlLogSearchClear').addEventListener('click', () => {
    const input = el<HTMLInputElement>('wlLogSearch');
    input.value = '';
    input.focus();
    searchNow();
  });

  el('wlLogTagBtn').addEventListener('click', () => { openTagPanel(); });
  el('wlLogTagClear').addEventListener('click', () => {
    selectedTags.clear();
    client.prefs.set(PREF.walletsTags, '');
    renderTagFilter();
    void renderRegister();
  });

  /*
   * Delegated on the document, because the panel is built on each opening
   * and torn down on each close — binding to it would mean rebinding, which
   * is how a listener gets forgotten.
   */
  document.addEventListener('change', (event) => {
    if (!tagPanel || !tagPanel.contains(event.target as Node)) return;
    const box = (event.target as HTMLElement).closest<HTMLInputElement>('[data-tagf-pick]');
    if (box) {
      const id = box.dataset['tagfPick']!;
      if (box.checked) selectedTags.add(id); else selectedTags.delete(id);
      client.prefs.set(PREF.walletsTags, [...selectedTags].join(','));
      renderTagFilter();
      void renderRegister();
      return;
    }
    if ((event.target as HTMLElement).id === 'wlLogTagMode') {
      tagModeValue = (event.target as HTMLSelectElement).value === 'all' ? 'all' : 'any';
      client.prefs.set(PREF.walletsTagMode, tagModeValue);
      renderTagFilter();
      void renderRegister();
    }
  });
  document.addEventListener('input', (event) => {
    if (!tagPanel || !tagPanel.contains(event.target as Node)) return;
    if ((event.target as HTMLElement).matches('[data-tagf-search]')) renderTagPanel();
  });
  document.addEventListener('mousedown', (event) => {
    if (!tagPanel) return;
    const target = event.target as Node;
    if (tagPanel.contains(target) || el('wlLogTagBtn').contains(target)) return;
    closeTagPanel();
  }, true);
  document.addEventListener('keydown', (event) => {
    if (!tagPanel || event.key !== 'Escape') return;
    event.stopPropagation();
    closeTagPanel();
    el('wlLogTagBtn').focus();
  }, true);
  window.addEventListener('resize', () => {
    if (tagPanel) placePopover(el('wlLogTagBtn'), tagPanel);
  });

  el('wlTotalMode').addEventListener('change', () => {
    client.prefs.set(PREF.walletsTotalMode, el<HTMLSelectElement>('wlTotalMode').value);
    paintCashTotals();
    // The running column is converted into the same currency, so the mode now
    // changes the list as well as the bar above it.
    if (section === 'log') void renderRegister();
  });
  el('wlLogSelect').addEventListener('change', () => {
    client.prefs.set(PREF.walletsLog, el<HTMLSelectElement>('wlLogSelect').value);
    // The bar follows the choice — see `cashInScope`.
    paintCashTotals();
    void renderRegister();
  });

  /*
   * The four sections are nav links to real routes, and they are also four
   * panes of a page that is already loaded. Both, deliberately.
   *
   * A plain navigation would rebuild the wallet list, the budgets and the
   * debts to show something the document already holds — the switch was
   * instant as a tab and would stop being. So the click is taken here and
   * the pane is swapped, with `pushState` putting the URL where the link
   * said it would go. Middle-click, Cmd-click and "open in new tab" are left
   * alone: those ask for a document, and the href is a real one.
   */
  for (const link of root.querySelectorAll<HTMLAnchorElement>('[data-nav] .nav-link')) {
    const target = (link.closest('[data-nav]') as HTMLElement).dataset['nav'];
    if (!SECTIONS.some((sec) => sec.id === target)) continue;
    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      showSection(target as Section, { push: true });
    });
  }
  // Back and forward move between sections rather than out of the page.
  window.addEventListener('popstate', () => {
    const sub = /\/webledger\/wallets(?:\/(log|budgets|between))?\/?$/.exec(window.location.pathname);
    if (!sub) return;
    showSection(sub[1] === 'between' ? 'debts' : readSection(sub[1]));
  });

  /*
   * The form's notice follows its hold — the connection going and coming
   * back, the hold resumed — so it says the fact as it changes rather than
   * what was true when the form opened.
   */
  client.leaseClient.subscribe(() => { paintQuickLeaseState(); });
  // The connection going or coming back changes what the form can promise.
  client.transport.onStateChange(() => { paintQuickLeaseState(); });

  client.transport.onMessage((msg) => { records.handleMessage(msg); });
  /*
   * Somebody else's entry, open here read-only, shows what they do: their
   * typing in the date and the note as it happens, and the whole entry again
   * each time they save. The amount and accounts follow on their save — the
   * full editor's per-leg typing has no one place on this form to land.
   */
  client.transport.onMessage((msg) => {
    if (!quickEdit || !quickTheirs() || quickTimeline?.viewing() !== undefined) return;
    if (msg.t === 'preview') {
      const { entity, recordId, field } = parseFieldKey(msg.key);
      if (entity !== 'transaction' || recordId !== quickEdit.txId) return;
      const text = msg.value.t === 'null' ? '' : String(msg.value.v);
      if (field === 'txDate') el<HTMLInputElement>('wlqDate').value = text;
      else if (field === 'description') el<HTMLInputElement>('wlqNote').value = text;
      else return;
      paintQuickHold();
      return;
    }
    if (msg.t === 'recordEvent' && msg.entity === 'transaction' && msg.recordId === quickEdit.txId) {
      void reloadQuick().then((r) => { if (r === 'unfit') handOver(false)(); else paintQuickHold(); });
    }
  });
  client.transport.onStateChange((state) => {
    records.resetSubscriptions();
    // Only when the log is what is on screen: a reconnect while reading the
    // wallet list would otherwise fetch a register nobody is looking at, and
    // `subscribe` would register interest in rows that are not rendered.
    if (state === 'open' && section === 'log') void renderRegister();
  });

  // Applied before the load, without rendering: the load fetches the log
  // itself when the log is the remembered section.
  showSection(section, { render: false });
  await primeCurrencies();
  await loadAll();

  /*
   * The short form's unsaved changes: asked about before the page is left,
   * and kept in the tab as they are typed so a tab the browser discards comes
   * back with them (ADR-0003, 2026-10-22). The full editor does the same for
   * itself.
   */
  const quickOpen = (): boolean => el('wlQuickDlg').classList.contains('show');
  const quickHasChanges = (): boolean => quickOpen() && !quickReadOnly && quickSnapshot() !== quickOpenedWith;
  guardUnsaved(quickHasChanges);
  let quickRememberTimer: ReturnType<typeof setTimeout> | undefined;
  const rememberQuickSoon = (): void => {
    if (quickRememberTimer !== undefined) clearTimeout(quickRememberTimer);
    quickRememberTimer = setTimeout(() => {
      if (!quickHasChanges()) { forgetForm(); return; }
      const built = buildQuickEntry();
      if (typeof built === 'string') return;
      const { leaseId: _token, ...payload } = built.body;
      rememberForm({ form: 'quick', txId: quickEdit?.txId ?? null, payload: payload as unknown as QueuedTransactionPayload });
    }, 400);
  };
  el('wlQuickDlg').addEventListener('input', rememberQuickSoon);
  el('wlQuickDlg').addEventListener('change', rememberQuickSoon);
  el('wlQuickDlg').addEventListener('hidden.bs.modal', () => {
    if (quickRememberTimer !== undefined) clearTimeout(quickRememberTimer);
    forgetForm();
  });

  await restoreDiscardedForm();

  /**
   * A form this tab had open when the browser discarded it, back where it
   * was. The short form reopens on its entry with what was typed; if
   * somebody has the entry by now, or it is a shape the short form cannot
   * say, the full editor opens instead and keeps what was typed beside theirs.
   */
  async function restoreDiscardedForm(): Promise<void> {
    const saved = takeSavedForm();
    if (!saved) return;
    const onSaved = (savedId: string): void => { currentTxId = savedId; flashNext = true; void afterWrite(); };
    const draftTx = queuedPayloadToDto(saved.payload);
    if (saved.form === 'quick') {
      const fit = fitsQuickForm(draftTx);
      if (!saved.txId && fit) {
        el('wlQuickBtn').click();
        paintQuickFields(draftTx, fit);
        refreshQuickForm();
        client.notify('Your unsaved entry is back, as you left it.');
        return;
      }
      if (saved.txId && fit) {
        const res = await apiFetch(`/apps/webledger/transactions/${saved.txId}`, { token: client.token })
          .catch(() => undefined);
        const current = res?.ok ? (await res.json() as { transaction: Parameters<typeof openQuickEdit>[0] }).transaction : undefined;
        if (current && await openQuickEdit(current)) {
          if (!quickTheirs()) {
            paintQuickFields(draftTx, fit);
            refreshQuickForm();
            client.notify('Your unsaved changes are back, as you left them.');
            return;
          }
          // Somebody has it by now: what was typed is kept beside their
          // version, as if they had taken it over while we looked.
          const view = quickHold?.view;
          const holder = view?.kind === 'theirs' ? view.holder : undefined;
          quickStolenBy = holder ? quickPerson(holder) : undefined;
          quickHeld = await client.outbox.enqueueDocument({
            entity: 'transaction', recordId: saved.txId, op: 'update', payload: saved.payload,
            clock: client.session.nextClock(),
            held: {
              takenBy: holder?.userId ?? client.userId, takenByName: quickStolenBy?.name ?? 'Someone',
              at: new Date().toISOString(),
            },
          });
          if (paintQuickHold() === 0) await releaseQuickHeld();
          else client.notify('Your unsaved changes are kept — somebody else has this entry now.');
          return;
        }
      }
    }
    const existing = saved.txId
      ? await apiFetch(`/apps/webledger/transactions/${saved.txId}`, { token: client.token })
        .then(async (r) => (r.ok ? (await r.json() as { transaction: Parameters<typeof openTransactionEditor>[2] }).transaction : null))
        .catch(() => null) ?? draftTx
      : null;
    await openTransactionEditor(root, client, existing, allAccounts, currencies, onSaved,
      { restore: saved.payload, ...(existing ? { neighbours: registerNeighbours } : {}) });
  }
}
